'use client';

// HomeV2 - duplicated helpers from app/page.js so the beta route is fully
// self-contained. Keep these in sync if the originals change. Everything here
// is a pure function; no imports from app/page.js (which exports default only).

import { matchesSearchText } from '@/lib/search/local-query.mjs';
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useContext, forwardRef } from 'react';
import { closeHairlineGrid, liveColumnCount, hairlineGapPx } from '@/lib/hairline-grid.mjs';
import { createPortal } from 'react-dom';
import { parseSalaryNL } from '@/lib/salary.mjs';
import { isActionChecked, processPhaseProgress, processChance } from '@/lib/process-chance.mjs';
import { ensurePhaseFloors } from '@/lib/deal-phase.mjs';
// Alleen de pure SVG-functies, NIET components/HomeV2/MarketIcon.js: dat bestand
// leest HV2 uit dit bestand en een import terug zou een cirkel geven.
// marketGlyph = kale glyph (RecordBadges tekent zijn eigen vakje),
// marketIcon = witte tegel + glyph (MarketOptionLabel, de dropdownregel).
import { marketGlyph, marketIcon, industryGlyph, GLYPHS, NH_NEW_PATH, NH_NEW_VIEWBOX, BRANCH_PATHS } from '@/lib/partnerbook-icons.mjs';
import { KPI_ICON_SHAPES, CALLS_PATH } from '@/lib/kpi-icons.mjs';

// KPI-vormen uit lib/kpi-icons.mjs als React-elementen (EEN bron met het kantoorscherm).
const kpiShapes = (key) => KPI_ICON_SHAPES[key].map(([tag, attrs], i) => React.createElement(tag, { key: i, ...attrs }));
// De testpersona herkennen we via de gedeelde constanten, nooit via een naam of
// een e-mailadres dat hier los wordt herhaald.
import { isResearchAgentUser } from '@/lib/research-agent.mjs';
// De drie klanttypen (klant, contracting, prospect) komen uit een bron; deze
// module kent de waarden, de labels en de regel dat contracting ook een klant is.
import { CLIENT_TYPES } from '@/lib/crm/client-type.mjs';
// De kleur achter de initialen: een regel voor app en kantoorscherm.
import { avatarFill } from '@/lib/user-colors.mjs';
// Het research-beeldmerk staat op zichzelf en leest niets uit dit bestand, dus
// deze import geeft geen cirkel (anders dan MarketIcon.js hierboven).
import MatchIcon from './MatchIcon.js';
import { gotoMarkten } from './marktenNav';
import { recordHref, normalizeHash, functieHash, functieHref } from '@/lib/nav-routes.mjs';
import { functieWhitespace } from '@/lib/functie.mjs';
import { fmtEuro, fmtFee, fmtSalaris, fmtSalarisRange, feeHint, salarisHint, salarisRangeOf } from '@/lib/fee';
import { JOB_STATUS_LABEL, jobStatusDisplay } from '@/lib/job-status.mjs';
import { statusShort } from '@/lib/cv-send-labels.mjs';
import { pmarkWidth } from '@/lib/brand-marks.mjs';
import { DROPDOWN_LAYOUT, dropdownWidth, dropdownLeft, clipBounds } from '@/lib/dropdown-layout.mjs';
import { registerKeyboardLayer } from '@/lib/keyboard-layers.mjs';
// De rekenkern van het slepen (grenzen + opslagvorm) staat als pure module in
// lib/resize-core.mjs, zodat die zonder browser te testen is.
import {
  clampSplit, nudgeRatio, autofitFromWidths, parseStored, serializeStored,
  SPLIT_GUTTER, SPLIT_MIN_RATIO, SPLIT_MAX_RATIO, SPLIT_MIN_PANE,
} from '@/lib/resize-core.mjs';

export const BD_STATUSES = ['Lead','Voorstel NCNP','Voorstel Retained','Voorstel Partnership','Vragen over Raamovereenkomst'];
export const PLACEMENT_STATUSES = ['IV1','IV2','IV3','IV4','IV5','IV6','Aanbieding arbeidscontract'];
// Sourcing track - the pre-interview funnel. Statuses drive the board columns;
// they deliberately avoid the word "Unsourced" so they don't clash with the
// derived candidate lifecycle stage (see deriveStage / CAND_STAGES).
export const SOURCING_STATUSES = ['Benaderen','In gesprek','Gekwalificeerd','CV Send','Uitgenodigd'];

// CV Send client-response lifecycle. 'open' = no client response yet ("No Follow-up");
// a CV Send should not stay open - the client either rejects (afgewezen) or invites
// the candidate (uitgenodigd, the last sourcing stage before an interview process is
// planned via upgrade-to-interview).
export const CV_SEND_STATUSES = ['open', 'afgewezen', 'uitgenodigd'];
export const CV_SEND_STATUS_META = {
  open: { label: 'Open', tone: 'neutral' },
  afgewezen: { label: 'Afgewezen', tone: 'red' },
  uitgenodigd: { label: 'Uitgenodigd', tone: 'blue' },
};
export function cvSendIsOpen(s) { return !s || s === 'open'; }

// De statuswoorden van een CV Send en van een interviewronde staan sinds 20
// september 2026 in lib/cv-send-labels.mjs: een pure module, zodat het raster
// (via lib/processen-rows.mjs), de rijactie en de test uit dezelfde bron lezen.
// Zelfde patroon als lib/job-status.mjs. Weergave alleen; de opgeslagen status
// van het externe systeem blijft ongemoeid.
export { RCRM_STATUS_LABELS, rcrmStatusLabel, rcrmStatusShort, rcrmStatusTone, intDealRoundLabel } from '@/lib/cv-send-labels.mjs';

// Een job-status (owner besluit 13 sep 2026: open/gepauzeerd/gesloten,
// substatussen Nieuw/Geen activiteit alleen op open) leeft in
// lib/job-status.mjs; jobStatusLabel/jobPickerHint blijven hier als dunne
// wrappers zodat bestaande imports blijven compileren.
export function jobStatusLabel(status) {
  return JOB_STATUS_LABEL[status] ?? String(status ?? '');
}

// ── Job-picker disambiguation (U41) ─────────────────────────────────────────
// Two same-title jobs at one client are indistinguishable in a bare picker (a
// Smartworkz 'Data Engineer' duplicate cost an hour). These helpers give every
// job option a completeness hint and flag duplicate titles. Shared by the
// NieuwProcesModal job picker and the manual CV-send job selector.
//
// jobPickerHint(job) -> "Open (terms ✓, salaris ✓, 9 juli 2026)". Status first,
// then the cheap has_terms/has_salary flags + the created/updated date (voluit,
// DESIGN-SYSTEM rule 16). The ✓ is a plain typographic glyph (like '✓ bekeken'
// elsewhere), not an emoji.
export function jobPickerHint(job) {
  if (!job) return '';
  const parts = [];
  if (job.has_terms) parts.push('terms ✓');
  if (job.has_salary) parts.push('salaris ✓');
  const dt = job.updated_on || job.created_on || job.created_at || job.created_in_app;
  if (dt) { try { parts.push(new Date(dt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })); } catch { /* bad date */ } }
  const status = jobStatusDisplay(job.status, job.status_sub);
  const detail = parts.length ? `(${parts.join(', ')})` : '';
  return [status, detail].filter(Boolean).join(' ');
}
// A duplicate is the same title at the same client (company_slug). Composite key
// so a cross-company list never falsely flags jobs at different clients.
export const jobDupKey = (job) => `${job?.company_slug || ''}||${String(job?.name || '').trim().toLowerCase()}`;
export function jobPickerDupKeys(jobs) {
  const counts = new Map();
  for (const j of (jobs || [])) { const k = jobDupKey(j); if (String(j?.name || '').trim()) counts.set(k, (counts.get(k) || 0) + 1); }
  const dups = new Set();
  for (const [k, n] of counts) if (n > 1) dups.add(k);
  return dups;
}

// Interview-process rejection. A placement that ends without a signed contract is
// "Afgewezen" - either the client rejected the candidate, or the candidate ended
// the process themselves. Captured when the consultant marks it rejected, or chosen
// when archiving an unconcluded interview process. Stored on the deal as
// { rejected, rejectedReason, rejectedAt } so it survives into the archive.
export const REJECT_REASONS = { client: 'Afgewezen door klant', candidate: 'Afgewezen door kandidaat', anders: 'Anders' };
export function rejectLabel(reason) { return REJECT_REASONS[reason] || 'Afgewezen'; }

export const PHASE_CHECKLIST = {
  bd: [
    { phase: 'Voorbereiding',  actions: ['Lead gevonden en opgeslagen', 'Bedrijf en DMU geresearcht', 'Direct nummer P&L verantwoordelijke gevonden', 'LinkedIn profiel bekeken'] },
    { phase: 'Eerste Contact', actions: ['Intro en probe doorlopen via IPMERC', 'FABs gebruikt en match gepresenteerd', 'Rules of engagement bevestigd'] },
    { phase: 'Propositie',     actions: ['Raamovereenkomst pdf email verstuurd naar prospect', 'Voorstel Retained verstuurd naar prospect', 'Voorstel nagebeld', 'Bezwaren behandeld via WRIOC'] },
    { phase: 'Deal',           actions: ['Mondeling akkoord ontvangen', 'Contract verstuurd', 'Getekend contract ontvangen'] },
  ],
  // fromStatus = de statusondergrens van de fase (lib/deal-phase.mjs): vanaf die
  // status staat een proces MINSTENS in deze fase, ook zonder klik op 'Fase
  // afronden'. Leeg = geen ondergrens.
  placement: [
    { phase: 'Intake',          fromStatus: '', actions: ['Hot job criteria vastgesteld', 'Job kwalificatie ingevuld via IPMERC', 'Zoekopdracht aangemaakt in CRM'] },
    { phase: 'Search',          fromStatus: '', actions: ['Longlist aangemaakt', 'Kandidaten gebeld via IPMERC', 'Kandidaat kwalificatie ingevuld', 'CV ontvangen en geaccordeerd'] },
    { phase: 'Interviewronden', fromStatus: 'IV1', actions: ['CV naar klant verstuurd en nagebeld', 'Interview gepland en bevestigd', 'Pre- en post-interview calls gedaan en feedback opgehaald'] },
    { phase: 'Closing',         fromStatus: 'Aanbieding arbeidscontract', actions: ['Pre-close gedaan: theoretisch aanbod gecheckt', 'Bezwaren behandeld', 'Aanbieding ontvangen van klant', 'Aanbieding uitgebracht en startdatum bevestigd'] },
  ],
  sourcing: [
    { phase: 'Contact',        actions: ['Kandidaat gebeld via IPMERC', 'Gesproken en interesse gepeild', 'Samenwerking afgesproken'] },
    { phase: 'Kwalificatie',   actions: ['Kandidaat kwalificatie ingevuld', 'CV ontvangen en geaccordeerd', 'Contactgegevens compleet (tel + e-mail)'] },
    { phase: 'Beschikbaarheid', actions: ['Beschikbaarheid / interviewslots vastgelegd', 'CV verstuurd naar klant'] },
  ],
};

// Maps a process fase → the IPMERC crm_phase tokens whose Kennis/Training is
// most relevant there, so ProcessDetailV3 can surface "bij deze fase" content.
// Keyed by normalized (lowercase) fase name; falls back per track when a fase
// has been renamed at runtime via /api/phases.
export const PHASE_CRM_TOKENS = {
  // BD
  'voorbereiding':   ['intro', 'terminology', 'discipline', 'mindset'],
  'eerste contact':  ['intro', 'probe', 'match', 'objection'],
  'propositie':      ['recommend', 'match', 'objection', 'close'],
  'deal':            ['close', 'offer', 'counter_offer', 'objection'],
  // Placement / INT
  'intake':          ['probe', 'match', 'engineer'],
  'search':          ['probe', 'match', 'engineer', 'recommend'],
  'interviewronden': ['interview', 'recommend', 'objection'],
  'closing':         ['close', 'offer', 'counter_offer', 'objection'],
  // Sourcing
  'contact':         ['intro', 'probe', 'match'],
  'kwalificatie':    ['probe', 'match', 'engineer'],
  'beschikbaarheid': ['recommend', 'match'],
};
export const PHASE_CRM_FALLBACK = {
  bd:        ['intro', 'probe', 'match', 'recommend', 'objection', 'close'],
  placement: ['probe', 'match', 'engineer', 'interview', 'recommend', 'close'],
  sourcing:  ['intro', 'probe', 'match', 'engineer', 'recommend'],
};
export function phaseCrmTokens(phaseName, track) {
  const key = String(phaseName || '').trim().toLowerCase();
  return PHASE_CRM_TOKENS[key] || PHASE_CRM_FALLBACK[track] || PHASE_CRM_FALLBACK.bd;
}

// (Dode duplicaat-KPI_LIST verwijderd 2026-07-12: miste 'Calls uitgaand' en werd
// door niemand geimporteerd - de canonieke lijst leeft in lib/kpi.js. KPI-audit.)

// HV2 - de design-tokens van de app: hardware-grijs met één lichtmodel, van
// laag naar hoog: chassis (bgPage, donkerst) -> rail (railBg, gegoten deel van
// het chassis) -> paneel (bg, lichtst = focus) ->
// knoppen (reliëf in globals.css). Verzonken vlakken (bgSub, inputs) liggen
// één stap ONDER hun paneel, nooit onder het chassis. De hele ladder is op
// 27 juli 2026 lichter en minder verzadigd gezet (eigenaarsverzoek): de grijzen
// waren KOEL-neutraal (blauwkanaal >= rood/groen), nu vrijwel echt neutraal
// (r=g=b). De volgorde van de ladder is daarbij bewust intact gebleven - die
// draagt het hardware-effect, niet de tint. bgHead had als enige een WARME cast
// en is meegegaan naar neutraal. Teksttinten zijn NIET aangeraakt: die stonden
// al donkerder dan op wit omdat grijs-op-grijs meer contrast vraagt, en op een
// lichter vlak wint dat alleen maar. We vermijden CSS var() zodat
// inline-SVG strokes (die var() in presentation-attributes niet resolven)
// blijven werken. Op 12 augustus 2026 zijn alleen de drie vlakken BOVEN het
// chassis lichter gezet (eigenaarsverzoek): bg f7f7f7 naar fdfdfd, bgHead
// f2f2f2 naar f8f8f8, bgSub ededee naar f2f2f3. Panelen beslaan de meeste
// pixels, dus dit is wat als "lichter" leest. Op 16 augustus 2026 is, na een
// UI-review, de HELE ladder lichter gezet en zijn de randen haarlijnen
// geworden (eigenaarsbesluit): bgPage d7d7d8 naar ebebec, railBg e9e9ea naar
// f0f0f1, bgSub f2f2f3 naar f4f4f5, bgHead f8f8f8 naar f9f9f9, border b3b3b6
// naar cbcbcf, divider dadadc naar e1e1e3, borderIn aaaaad naar c0c0c4,
// borderStrong a5a5a8 naar b6b6ba, neutralBd acacaf naar c6c6ca, subtle
// e7e7e8 naar ededee - bg blijft fdfdfd. De volgorde van de ladder blijft
// STRIKT bgPage < railBg < bgSub < bgHead < bg; de afstand chassis naar
// paneel (bgPage naar bg) is nu 18 punten. Op 19 augustus 2026 (eigenaarsbesluit
// na mockup-vergelijking, contrastronde): tekst naar bijna-zwart en vlakken naar
// bijna-wit. ink 141416 naar 000000, ink2 1f1f23 naar 0f0f11, ink3 3a3a3e en
// muted 4a4a50 naar 161618, mute2 6f6f73 naar 2a2a2e; bg fdfdfd naar ffffff,
// bgHead f9f9f9 naar fdfdfd, bgSub f4f4f5 naar fcfcfc, railBg f0f0f1 naar fafafa,
// bgPage ebebec naar f8f8f9. Hiërarchie komt nu vrijwel alleen uit gewicht en
// randen; de ladder blijft strikt geordend.
export const HV2 = {
  ink: '#000000', ink2: '#0f0f11', ink3: '#161618', muted: '#161618', mute2: '#2a2a2e',
  divider: '#e1e1e3', border: '#cbcbcf', borderIn: '#c0c0c4',
  bg: '#ffffff', bgSub: '#fcfcfc', bgPage: '#f8f8f9', bgHead: '#fdfdfd',
  accentInt: '#2e5aa8', accentBd: '#1f7a72', accentLead: '#6f6f6d', accentSrc: '#a8385d', srcTint: '#eeebe1',
  // Roze (eigenaarsbesluit 4 september 2026): de kleur van een contracting klant, nergens anders.
  pink: '#d63384',
  blue: '#2d4f9e', blueBg: '#e6e9f0', green: '#2e7d4f', greenBg: '#e6ece8', greenBd: '#b9cfbf',
  red: '#b03434', redBg: '#f1e7e6',
  orange: '#b25a20', orangeBg: '#f1e8e0',
  // Darker TEXT variants of the semantic colors (fills use red/green).
  // One token each - before this, six ad-hoc ambers / three reds / four greens
  // had drifted across the app (UX audit 2026-07-06).
  redText: '#8f2b2b', greenText: '#25603e',
  // Systeemupdate-stip (eigenaarsregel 28 aug 2026, DESIGN-SYSTEM regel 30). De
  // ENIGE geel-oranje statuskleur in de app: hij scheidt een productmelding van
  // de app ('nieuwe versie van de extensie', 'nieuwe functie') van een gewoon
  // ongelezen bericht, dat rood blijft. Uitzondering op regel 17, expliciet
  // toegestaan door de eigenaar en nergens anders in te zetten: geen pill, geen
  // chip, geen knop, geen waarschuwing. Zelfde hex als PERSON_COLORS.prospect,
  // bewust hergebruikt in plaats van een nieuwe oranje te munten.
  updateDot: '#d9890a',
  // Ongelezen-vulling in het postvak (eigenaarsregel 28 aug 2026, designregel 30).
  // Dezelfde geel-oranje als updateDot, op 8% over wit, dus een ongelezen rij leest
  // als een lichte tint van de stip ernaast in plaats van als een nieuwe kleur.
  // Tweede en laatste uitzondering op regel 17: alleen deze vulling, alleen in het
  // postvak, nooit in een pill, chip, knop of waarschuwing.
  unreadBg: '#fcf6ec',
  // De open Gespreksvoorbereiding van Match (eigenaarsbesluit 28 sep 2026): de rij
  // of kaart met een openstaande briefing staat op dezelfde vulling als een ongelezen
  // postvakrij, met een iets donkerdere rand van dezelfde tint. Derde en uitdrukkelijk
  // toegestane uitzondering op regel 17: alleen de open briefing en zijn knop
  // (components/HomeV2/MatchBriefing.js en de rijen die hem dragen), nooit een pill,
  // chip, knop elders of waarschuwing. lib/design-inbox.test.mjs bewaakt de plekken.
  briefBg: '#fcf6ec',
  briefBd: '#ecdcbc',
  // DISC-geel, uitsluitend voor de vier gedragsstijlen uit de gespreksvoering-
  // training (paragraaf 9 van het bronartikel). Het model wordt in zijn populaire
  // vorm ALTIJD als rood/geel/groen/blauw weergegeven; rood, groen en blauw staan
  // hierboven al, geel ontbrak. Gekozen op dezelfde verzadiging en helderheid als
  // die drie zodat de vier als een set lezen.
  //
  // LET OP - dit is GEEN amber in de zin van regel 17 (DESIGN-SYSTEM.md). Die regel
  // verbiedt amber voor waarschuwingen, pills, chips, badges en knoppen: statuskleur
  // dus. Dit is inhoud, de naam van een gedragsstijl, net zoals rood hier geen
  // urgentie betekent. Niet gebruiken voor status, nergens anders inzetten.
  discYellow: '#9a7b12',
  neutralBd: '#c6c6ca', intTint: '#e8e9f0', borderStrong: '#b6b6ba', subtle: '#ededee', onAccent: '#f5f5f6',
  // Op 16 augustus 2026 verder lichter gezet (eigenaarsbesluit na de UI-review);
  // blijft tussen bgPage (#f8f8f9) en bgSub (#fcfcfc) zodat de licht-ladder
  // intact is.
  railBg: '#fafafa',
  // Typografie: DRIE maten voor de hele app (eigenaarsbesluit 15 juli, van 26
  // maten naar 3). fsText = alle leestekst (body, tabelrijen én -koppen,
  // veldlabels, hints, timestamps, knoppen, dropdowns), hiërarchie komt uit
  // gewicht/kleur, niet uit maat. fsBadge = alleen acroniem-badges/pills
  // (INT/KWL/Off-Limits), telbadges en micro-glyphs. fsTitle = paginatitels.
  // Display-cijfers en marketing-/videocontent vallen buiten dit systeem.
  // Op 13 september 2026 een stap groter gezet (eigenaarsbesluit na de
  // UI-review): 13 -> 14 en 10 -> 11. fsBadge is nu de ONDERGRENS van de app,
  // niets dat tekst draagt staat er nog onder. Bewaakt door
  // lib/design-tekstgrootte.test.mjs.
  fsText: 14, fsBadge: 11, fsTitle: 20,
};
// Universele knop-diepte, zelfde waarden als .pb-btn in globals.css.
export const BTN_RELIEF = {
  borderRadius: 5,
  border: '1px solid rgba(0,0,0,0.24)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.22)',
};
export const BTN_RELIEF_PRESSED = {
  boxShadow: 'inset 0 1.5px 3px rgba(0,0,0,0.26), inset 0 -1px 0 rgba(255,255,255,0.24)',
};
// Inline symbol pill: [INT] (Interview) / [BD] (Business Development) / [KWL]
// (Kwalificatie). The app's universal shorthand - use instead of the full words.
// Spelled-out tooltips for the inline acronym pills, so users aren't left guessing.
export const SYM_TITLES = { RC: 'Recruitmentcampagne (retained search)', KWL: 'Kwalificatie', KAND: 'Kandidaat', INT: 'Interviewproces', BD: 'Business Development', SRC: 'CV Send', CVS: 'CV Send', JOB: 'Job', DMU: 'Decision-making unit (beslisser)', CLIENT: 'Klant', PROSP: 'Prospect' };

// The sidebar 'branch' pictogram (Processen nav). Single source of truth - the
// LeftRail nav and the detail-window EntityChip render the same glyph, so a
// process window visually matches the nav item that opened it.
// Belrichting-pijlen, EEN bron: belvenster (CallPlusWindow), Call Logs en de
// activiteitenfeed tekenen dezelfde glyphs. Uitgaand ↗ (accentInt), inkomend ↙ (green).
export const CALL_ARROW_OUT = ['M7 17L17 7', 'M8 7h9v9'];
export const CALL_ARROW_IN = ['M17 7L7 17', 'M16 17H7V8'];
// Bron: lib/partnerbook-icons.mjs (React-vrij, zodat de banner dezelfde paden leest).
export { BRANCH_PATHS };

// EntityChip - DE typechip van de app (eigenaarsbesluit 26 augustus 2026).
// Was een omrande haarlijn-chip; leest nu als de typeband van het recordvenster:
// gevulde grond per entiteitstype + pictogram + het volle woord. De vulling komt
// uit RECORD_TYPE_META, dezelfde bron als die band, zodat de chip in een rij en
// de band in de kop nooit meer uit elkaar lopen - dat was de hele splitsing.
// Lichte vullingen (goud, zalm, zeegroen) dragen inkt, donkere wit; dat staat
// per type in bandText en wordt hier niet opnieuw geraden.
// Badge blijft de acroniem-shorthand voor krappe cellen. Procesvensters gebruiken
// BRANCH_PATHS voor elk proces, zowel in navigatie als op een recordtype.
const CHIP_META = {
  candidate: { label: 'Kandidaat', icon: 'candidate', kobalt: true },
  // Sentence case (no ALLCAPS/SMALLCAPS per DESIGN-SYSTEM rule 9); BD stays an acronym.
  client:    { label: 'Klant', icon: 'client' },
  prospect:  { label: 'Prospect', icon: 'prospect' },
  // Contracting: ook een getekende klant, ander soort werk (uurtarief). Zelfde
  // bedrijfsglyph als Klant, eigen woord en eigen vulling.
  contracting: { label: 'Klant contracting', icon: 'client' },
  // Prospect contracting (eigenaarsregel 11 september 2026): huurt freelancers in,
  // maar heeft niets getekend. Dus het prospectglyph, niet het bedrijfsglyph van
  // een klant.
  prospect_contracting: { label: 'Prospect contracting', icon: 'prospect' },
  // recordLabel: het volle woord dat de recordvenster-typeband draagt. De chip in
  // een rij blijft kort ('Contact'); de band noemt de entiteit voluit.
  contact:   { label: 'Contact', recordLabel: 'Contactpersoon', icon: 'contact' },
  job:       { label: 'Job', icon: 'job' },
  // Elk proces draagt het takglyph (eigenaarsbesluit 4 oktober 2026).
  // Trackkleur en label blijven de drie soorten onderscheiden.
  placement: { label: 'Interviewproces', icon: 'process' },
  sourcing:  { label: 'CV Send', icon: 'process' },
  // 'BD' over the full 'Business development' - the long form crowded the bar
  // next to 6+ action buttons (approved deviation from the handoff).
  bd:        { label: 'BD proces', icon: 'process' },
  callList:  { label: 'Lijst', icon: 'callList' },
  callLog:   { label: 'Call log', icon: 'callLog' },
  applicant: { label: 'Sollicitant', icon: 'applicant' },
};
// EEN maat (eigenaarsbesluit 17 sep 2026): de chip staat overal op fsText met
// 18px regel en 2px 10px, ook in een lijstrij. De badgemaat-variant (mini) is
// afgeschaft; een chip die kleiner is dan de tekst ernaast leest als een fout.
// fit (opt-in, zoeklijst 17 sep 2026): de chip mag krimpen tot zijn cel en knipt
// het woord af met een ellips ('Prospect contra...'), de volle tekst staat in de
// title. Zonder fit houdt de chip zijn natuurlijke maat (flexShrink 0), zodat
// geen andere aanroeper verandert.
export function EntityChip({ kind, title, label, fit = false }) {
  const m = CHIP_META[kind] || CHIP_META.candidate;
  const rt = RECORD_TYPE_META[kind] || RECORD_TYPE_META.candidate;
  const text = label || t(m.label);
  const fitStyle = fit ? { minWidth: 0, maxWidth: '100%' } : { flexShrink: 0 };
  return (
    <span title={title || (fit ? text : undefined)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: rt.color, color: rt.bandText, borderRadius: 4, padding: '2px 10px', fontSize: HV2.fsText, fontWeight: 650, lineHeight: '18px', whiteSpace: 'nowrap', ...fitStyle }}>
      <EntityIcon entity={m.icon} size={13} color="currentColor" />
      {fit ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{text}</span> : text}
    </span>
  );
}

// De vulkleur van een recordtype als losse waarde. Bestaat zodat een surface die
// GEEN chip tekent (de statuspill van RecordGrid op een interviewproces) dezelfde
// kleur leest als de chip en de typeband, in plaats van de hex over te schrijven.
// Een feit, een bron: RECORD_TYPE_META hieronder is de enige vindplaats.
export function recordTypeColor(kind) {
  return (RECORD_TYPE_META[kind] || RECORD_TYPE_META.candidate).color;
}

// ── RecordTypeBand: de typeband van het recordvenster, op rijmaat ─────────────
// Het recordvenster opent met een gekleurde typeband (glyph + vulling + woord);
// een lijstrij die naar datzelfde record wijst draagt hem op dezelfde maat
// (een maat app-wijd, 17 sep 2026), anders zegt de rij niet waar hij over gaat.
// EEN bron: `rt` komt uit
// resolveRecordType, dus kleur, glyph, label en de gesplitste contactband
// (klant/prospect + contact) lopen nooit uit de pas met de venstertitelbalk.
// rt === null (type nog onbekend) tekent niets, precies zoals de chrome doet.
export function RecordTypeBand({ rt, title }) {
  if (!rt) return null;
  const seg = (c, txt, ico, radius) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: c.color, color: c.bandText, borderRadius: radius, padding: '2px 10px', fontSize: HV2.fsText, fontWeight: 650, lineHeight: '18px', whiteSpace: 'nowrap' }}>
      <EntityIcon entity={ico} size={13} color={c.bandText} />
      {txt}
    </span>
  );
  // Het woord van de chrome, overal: 'Contactpersoon', geen verkorte rijvariant.
  const woord = (m) => m.label;
  return (
    <span title={title || rt.label} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      {rt.lead ? seg(rt.lead, woord(rt.lead), rt.lead.icon, '4px 0 0 4px') : null}
      {seg(rt, woord(rt), rt.icon, rt.lead ? '0 4px 4px 0' : 4)}
    </span>
  );
}

// ── recordType: EEN bron voor de identiteit van een recordvenster ────────────
// Het universele recordvenster (UnifiedDetailWindow) kleurt zijn typeband en
// typetegel hierop. Bewust naast CHIP_META: de labels komen daar vandaan, zodat
// de chip in een rij en de band in de header nooit uit elkaar lopen. Een bedrijf
// splitst op client_type (klant/prospect) en een proces op track - exact dezelfde
// splitsing als canonicalWorkspaceHash/workspaceKey in lib/nav-routes.mjs maakt.
// color = bandvulling; mark = tegelrand + tegelglyph (donkerder waar de vulling
// te licht is voor een haarlijn); bandText = ink op de lichte vullingen
// (wit haalt daar geen 3:1), wit op de donkere. De vier niet-HV2-hexen (zalm,
// geel-oranje, paars, zeegroen) wonen ALLEEN hier - geen HV2-tokens, geen
// tweede vindplaats.
// De track-accenten (accentInt/accentBd/accentSrc) behouden hun eigen rol
// (spinefill, lane-dots, TrackTag-randen) en zijn hier bewust NIET hergebruikt.
// De sollicitant (13 okt 2026): eigen recordtype met een eigen venster
// (ApplicantRecord.js). APPLICANT_BG is de lichte tint van dezelfde kleur, voor de
// volle-breedte balk 'Nog geen kandidaat'. Staat hier, boven RECORD_TYPE_META,
// omdat die tabel hem bij het laden van de module al leest.
export const APPLICANT_COLOR = '#c2410c';
export const APPLICANT_BG = '#fbeee6';
// Mailing list aanmelding (vacaturemelding van de site, 14 okt 2026): zelfde rij als een
// sollicitant, eigen kleur zodat hij in Aanmeldingen niet voor een sollicitatie doorgaat.
export const MAILING_COLOR = '#0e7490';
export const MAILING_BG = '#e6f3f6';
const RECORD_TYPE_META = {
  candidate: { key: 'candidate', chip: 'candidate', color: HV2.blue,       mark: HV2.blue,       bandText: '#ffffff', icon: 'candidate' },
  client:    { key: 'client',    chip: 'client',    color: HV2.green,      mark: HV2.green,      bandText: '#ffffff', icon: 'client' },
  prospect:  { key: 'prospect',  chip: 'prospect',  color: HV2.accentLead, mark: HV2.accentLead, bandText: '#ffffff', icon: 'prospect' },
  // Contracting (eigenaarsregel 4 september 2026): de tweede getekende klant.
  // Roze (HV2.pink), een eigen token op eigenaarsbesluit: niet het groen van
  // Klant, niet het grijs van Prospect, en niet het framboos van een contactpersoon.
  contracting: { key: 'contracting', chip: 'contracting', color: HV2.pink, mark: HV2.pink, bandText: '#ffffff', icon: 'client' },
  // Prospect contracting (eigenaarsregel 11 september 2026): niet getekend, dus de
  // vorm van een prospect, met het roze van contracting als merkteken. Zo leest de
  // band waar het over gaat zonder een getekende klant te claimen.
  prospect_contracting: { key: 'prospect_contracting', chip: 'prospect_contracting', color: HV2.pink, mark: HV2.pink, bandText: '#ffffff', icon: 'prospect' },
  contact:   { key: 'contact',   chip: 'contact',   color: HV2.accentSrc,  mark: HV2.accentSrc,  bandText: '#ffffff', icon: 'contact' },
  job:       { key: 'job',       chip: 'job',       color: '#ee9d8d',      mark: '#c06552',      bandText: HV2.ink,   icon: 'job' },
  placement: { key: 'process',   chip: 'placement', color: '#e8b055',      mark: '#a67016',      bandText: HV2.ink,   icon: 'process' },
  bd:        { key: 'process',   chip: 'bd',        color: '#5b21b6',      mark: '#5b21b6',      bandText: '#ffffff', icon: 'process' },
  sourcing:  { key: 'process',   chip: 'sourcing',  color: '#9dc3b4',      mark: '#33705c',      bandText: HV2.ink,   icon: 'process' },
  // Bellijst: petrol (eigenaarskeuze 28 aug 2026). Stond hier niet, waardoor een
  // bellijst-chip terugviel op het kobalt van een kandidaat en dus als kandidaat
  // las. Petrol is de enige blauwgroene vulling in de tabel.
  callList:  { key: 'callList',  chip: 'callList',  color: '#1f7a72',      mark: '#1f7a72',      bandText: '#ffffff', icon: 'callList' },
  // Call log: leisteen. Geen recordtype maar een activiteit; de band bestaat
  // zodat het venster Gesprek loggen dezelfde kop draagt als de andere
  // Nieuw-vensters (eigenaarsverzoek 30 aug 2026).
  callLog:   { key: 'callLog',   chip: 'callLog',   color: '#475569',      mark: '#475569',      bandText: '#ffffff', icon: 'callLog' },
  applicant: { key: 'applicant', chip: 'applicant', color: APPLICANT_COLOR, mark: APPLICANT_COLOR, bandText: '#ffffff', icon: 'applicant' },
};
// entity: candidate | company (of client/prospect) | contact | job | process.
// clientType telt bij een bedrijf EN bij een contactpersoon, track alleen bij een
// proces. Onbekend type (bv. een record dat nog laadt) geeft null terug - de band
// tekent dan geen kleur in plaats van er een te raden.
// Bij een contactpersoon draagt clientType het type van ZIJN bedrijf: dat levert
// een tweede bandsegment (`lead`) voor de klant/prospect-helft, zodat de kop
// 'Klant contactpersoon' of 'Prospect contactpersoon' leest (eigenaarsbesluit
// 28 aug 2026). Zonder bekend bedrijfstype blijft `lead` null en staat de band
// er precies zoals voorheen.
export function resolveRecordType({ entity, clientType, client_type: clientTypeSnake, track } = {}) {
  const ct = clientType != null ? clientType : clientTypeSnake;
  const e = String(entity || '');
  let metaKey = null;
  if (e === 'candidate' || e === 'kandidaat') metaKey = 'candidate';
  else if (e === 'company' || e === 'bedrijf' || e === 'client' || e === 'contracting' || e === 'prospect_contracting' || e === 'prospect') {
    metaKey = (ct === 'contracting' || e === 'contracting') ? 'contracting'
      : (ct === 'client' || e === 'client') ? 'client'
        : (ct === 'prospect_contracting' || e === 'prospect_contracting') ? 'prospect_contracting'
          : 'prospect';
  }
  else if (e === 'contact') metaKey = 'contact';
  else if (e === 'job') metaKey = 'job';
  else if (e === 'process' || e === 'proces' || e === 'deal') metaKey = track === 'sourcing' ? 'sourcing' : track === 'bd' ? 'bd' : 'placement';
  else if (e === 'callList' || e === 'calllist' || e === 'bellijst') metaKey = 'callList';
  else if (e === 'callLog' || e === 'calllog') metaKey = 'callLog';
  else if (e === 'applicant' || e === 'sollicitant') metaKey = 'applicant';
  if (!metaKey) return null;
  const m = RECORD_TYPE_META[metaKey];
  const chip = CHIP_META[m.chip] || CHIP_META.candidate;
  let lead = null;
  if (metaKey === 'contact' && CLIENT_TYPES.includes(ct)) {
    const lm = RECORD_TYPE_META[ct];
    const lchip = CHIP_META[lm.chip] || CHIP_META.prospect;
    lead = { label: t(lchip.label), shortLabel: t(lchip.label), color: lm.color, bandText: lm.bandText, icon: lm.icon };
  }
  // label = het volle woord voor de venstertitelbalk ('Contactpersoon'),
  // shortLabel = het korte woord voor een lijstrij ('Contact'). Voor elk ander
  // type zijn ze gelijk; alleen contact heeft een recordLabel.
  return { key: m.key, label: t(chip.recordLabel || chip.label), shortLabel: t(chip.label), color: m.color, mark: m.mark, bandText: m.bandText, icon: m.icon, lead };
}

// RequestChip - kleine geanimeerde indicator voor een binnengekomen interview-
// verzoek dat nog op afhandeling wacht (cv_interview_requests.status='open').
// Kobalt TEKST (HV2.blue) met een langzame opacity-breathe: geen fill, geen glow,
// geen gradient (design-regel - een pill met kobalt-fill is verboden, EntityChip
// gebruikt kobalt legitiem als tekst/rand). De @keyframes leeft één keer in de head
// (id-guarded); de animatie hangt via inline style aan de span (geen className -
// design-guard). prefers-reduced-motion -> geen breathe. Klikbaar via onClick (bv. de
// Requests-detail-popover, geplaatst door de SURFACES-unit); dense genoeg voor
// tabel-/kaartcellen naast EntityChip. count>1 -> telbadge achter 'Request'.
let _reqBreatheInjected = false;
function ensureReqBreathe() {
  if (_reqBreatheInjected || typeof document === 'undefined') return;
  _reqBreatheInjected = true;
  if (document.getElementById('pa-reqbreathe-kf')) return;
  const el = document.createElement('style');
  el.id = 'pa-reqbreathe-kf';
  el.textContent = '@keyframes pa-reqbreathe{0%{opacity:1}50%{opacity:0.55}100%{opacity:1}}';
  document.head.appendChild(el);
}
export function RequestChip({ onClick, title, label = 'Request', count }) {
  const [anim, setAnim] = useState(false);
  useEffect(() => {
    ensureReqBreathe();
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) setAnim(true); // na mount aanzetten - geen SSR/hydration-mismatch
  }, []);
  const clickable = typeof onClick === 'function';
  const n = Number(count) || 0;
  const activate = clickable ? (e) => { e.stopPropagation(); onClick(e); } : undefined;
  return (
    <span
      title={title || t('Openstaand interviewverzoek')}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={activate}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(e); } } : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: HV2.blue, fontSize: HV2.fsText, fontWeight: 650, lineHeight: '18px', whiteSpace: 'nowrap', flexShrink: 0, cursor: clickable ? 'pointer' : 'default', animation: anim ? 'pa-reqbreathe 1.6s ease-in-out infinite' : undefined }}
    >
      {label}{n > 1 ? ` ${n}` : ''}
    </span>
  );
}

// ── Open-interviewverzoek: gedeelde open-set-fetch + klik-popover ─────────────
// De "Request"-chip verschijnt op elk sourcing/CV-Send-oppervlak waar de entiteit
// een OPEN interviewverzoek heeft. Om N+1 te vermijden halen we de open-set (de
// slugs met een open verzoek, gegroepeerd per entiteit) via /api/requests?open=1
// EEN keer op en cachen 'm module-breed; elk oppervlak checkt daarna puur in-memory
// (Set.has) per rij/kaart. De fetch is director-gated: een non-director krijgt 403
// -> lege set -> geen chips (fail-safe, geen console-noise).
const _OPENREQ_TTL = 60000;
const _openReq = { data: null, at: 0, promise: null, subs: new Set() };
function _emitOpenReq() { for (const fn of _openReq.subs) { try { fn(); } catch { /* subscriber weg */ } } }
function _loadOpenReq() {
  return fetch('/api/requests?open=1')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => ({
      candidate: new Set((j && j.candidate) || []),
      company: new Set((j && j.company) || []),
      job: new Set((j && j.job) || []),
    }))
    .catch(() => ({ candidate: new Set(), company: new Set(), job: new Set() }));
}
function ensureOpenReq(force) {
  if (typeof window === 'undefined') return;
  const fresh = _openReq.data && (Date.now() - _openReq.at) < _OPENREQ_TTL;
  if (!force && fresh) return;
  if (_openReq.promise) return; // een lopende fetch dekt alle wachtende subscribers
  _openReq.promise = _loadOpenReq().then((d) => {
    _openReq.data = d; _openReq.at = Date.now(); _openReq.promise = null; _emitOpenReq();
  });
}
// useOpenRequestSet: subscribe op de module-cache. Triggert precies EEN fetch (de
// eerste mount vult 'm; verdere mounts binnen de TTL delen de cache). Geeft
// has(entity, slug) terug - een pure in-memory Set-lookup, dus veilig per rij aan te
// roepen (geen per-rij-request). Werkt op elk oppervlak zonder prop-drilling.
export function useOpenRequestSet() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    _openReq.subs.add(fn);
    ensureOpenReq(false);
    return () => { _openReq.subs.delete(fn); };
  }, []);
  const data = _openReq.data;
  const has = useCallback((entity, slug) => {
    if (!data || !slug) return false;
    const set = data[entity];
    return !!(set && set.has(String(slug)));
  }, [data]);
  return { ready: !!data, has };
}

// Detail-cache voor de klik-popover: de volledige OPEN verzoeken van EEN entiteit
// (/api/requests?entity=..&slug=..). Lui opgehaald bij de eerste klik, gecachet per
// entity:slug (zelfde TTL), zodat her-openen niet opnieuw fetcht.
const _reqDetail = new Map(); // key -> { at, data, promise }
function fetchReqDetail(entity, slug) {
  const key = `${entity}:${slug}`;
  const hit = _reqDetail.get(key);
  if (hit && hit.data && (Date.now() - hit.at) < _OPENREQ_TTL) return Promise.resolve(hit.data);
  if (hit && hit.promise) return hit.promise;
  const promise = fetch(`/api/requests?entity=${encodeURIComponent(entity)}&slug=${encodeURIComponent(slug)}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const data = (j && Array.isArray(j.requests)) ? j.requests : [];
      _reqDetail.set(key, { at: Date.now(), data, promise: null });
      return data;
    })
    .catch(() => { _reqDetail.set(key, { at: Date.now(), data: [], promise: null }); return []; });
  _reqDetail.set(key, { at: (hit && hit.at) || 0, data: (hit && hit.data) || null, promise });
  return promise;
}

// Plaatsing van de popover: body-portal met position:ABSOLUTE in pagina-coordinaten
// (rect + scroll). Escapet zo elke overflow:hidden-voorouder (net als InfoHint's
// portal) MAAR zonder position:fixed - de page.js-root heeft overflow:hidden dat
// fixed clipt (bekende watchpoint). De popover sluit bij scroll/resize, dus 'ie kan
// nooit losgekoppeld van z'n anker blijven staan.
function _placeReqPopover(rect, width) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const sx = typeof window !== 'undefined' ? (window.scrollX || window.pageXOffset || 0) : 0;
  const sy = typeof window !== 'undefined' ? (window.scrollY || window.pageYOffset || 0) : 0;
  let left = rect.left;
  if (left + width > vw - 8) left = vw - 8 - width;
  if (left < 8) left = 8;
  return { top: rect.bottom + 6 + sy, left: left + sx };
}

// Compacte formattering voor de popover (de Requests-pagina heeft z'n eigen rijkere
// weergave). Datum voluit + tijd; slot = datum voluit (+ tijd); voorkeur -> label.
function _reqWhen(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 16);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatDateVoluit(d)} ${hh}:${mm}`;
}
function _reqSlot(s) {
  if (!s || !s.date) return '';
  const base = formatDateVoluit(`${s.date}T00:00:00`) || String(s.date);
  return s.time ? `${base} ${s.time}` : base;
}
function _reqMode(mode) {
  if (mode === 'video') return t('Videogesprek');
  if (mode === 'office') return t('In-Office');
  return null;
}
// Een label/waarde-regel in de popover (label gedempt links, waarde rechts).
function ReqLine({ label, value, pre }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
      <span style={{ width: 116, flexShrink: 0, color: HV2.muted, fontWeight: 650 }}>{label}</span>
      <span style={{ minWidth: 0, color: HV2.ink2, whiteSpace: pre ? 'pre-wrap' : 'normal', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

// RequestChipFor: DE plaatsingscomponent voor elk oppervlak. Checkt via de gedeelde
// open-set of {entity, slug} een open verzoek heeft; zo ja rendert 'ie de RequestChip
// met een klik-popover die de detail lui ophaalt (wie/contact, kandidaat/job, wanneer,
// voorgestelde momenten, voorkeur, notitie). Rendert null zonder open verzoek, dus
// veilig overal neer te zetten. GEEN eigen open-set-fetch per instantie (module-cache).
export function RequestChipFor({ entity, slug }) {
  const { has } = useOpenRequestSet();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [detail, setDetail] = useState(null); // null = laden; [] = geen details
  const anchorRef = useRef(null);
  const cardRef = useRef(null);
  const width = 300;
  const openPopover = useCallback(() => {
    const el = anchorRef.current;
    if (el) setPos(_placeReqPopover(el.getBoundingClientRect(), width));
    setDetail(null);
    setOpen(true);
    fetchReqDetail(entity, slug).then((d) => setDetail(d));
  }, [entity, slug]);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = (e) => {
      if (cardRef.current && cardRef.current.contains(e.target)) return;
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!has(entity, slug)) return null;
  return (
    <span ref={anchorRef} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      <RequestChip onClick={() => (open ? setOpen(false) : openPopover())} />
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div ref={cardRef} role="dialog" onClick={(e) => e.stopPropagation()}
          style={{ position: 'absolute', top: pos.top, left: pos.left, zIndex: 9700, width, maxWidth: 'calc(100vw - 16px)', ...SURFACE.tooltip, padding: '10px 12px', fontSize: HV2.fsText, color: HV2.ink2, display: 'flex', flexDirection: 'column', gap: 8, cursor: 'default' }}>
          <div style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.blue }}>{t('Openstaand interviewverzoek')}</div>
          {detail == null && <div style={{ color: HV2.mute2 }}>{t('Laden…')}</div>}
          {detail != null && detail.length === 0 && <div style={{ color: HV2.mute2 }}>{t('Geen details gevonden')}</div>}
          {detail != null && detail.map((r, i) => (
            <div key={r.requestId || i} style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingTop: i ? 8 : 0, borderTop: i ? `1px solid ${HV2.divider}` : 'none' }}>
              <div style={{ fontWeight: 650, color: HV2.ink }}>{[r.contactName || r.requesterName, r.companyName || r.requesterCompany].filter(Boolean).join(' - ') || '-'}</div>
              {(r.candidateName || r.jobName) && (
                <div style={{ fontSize: HV2.fsBadge, color: HV2.muted }}>{[r.candidateName, r.jobName].filter(Boolean).join(' - ')}</div>
              )}
              <ReqLine label={t('Wanneer')} value={_reqWhen(r.at)} />
              {_reqMode(r.preferredMode) && <ReqLine label={t('Voorkeur')} value={_reqMode(r.preferredMode)} />}
              {Array.isArray(r.slots) && r.slots.length > 0 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ width: 116, flexShrink: 0, color: HV2.muted, fontWeight: 650 }}>{t('Voorgestelde momenten')}</span>
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {r.slots.map((s, si) => <span key={si} style={{ fontWeight: 560, color: HV2.ink }}>{_reqSlot(s)}</span>)}
                  </span>
                </div>
              )}
              {r.note && <ReqLine label={t('Notitie')} value={r.note} pre />}
            </div>
          ))}
        </div>, document.body)}
    </span>
  );
}
// ── Language (NL default / EN) ────────────────────────────────────────────────
// The choice lives in localStorage and switching it
// triggers a full window.location.reload(), so every component re-reads the active
// language on next paint - no context/provider threading. t(nl, vars) returns the
// English string when the EN dictionary (./i18n.js) has the Dutch source as a key,
// otherwise the Dutch source itself, so the UI keeps working untranslated and the
// dictionary can grow incrementally. Placeholders: t('{label} begint nu', {label}).
// Safe at first paint: app/page.js renders null until mounted, so no SSR text to
// mismatch - _lang is already read from localStorage by the time anything renders.
// Dutch is the default for everyone. The English toggle is disabled for now (the team
// works in Dutch and the EN dictionary is incomplete, so it leaked mixed copy). Locking
// to 'nl' forces Dutch even for anyone who previously switched, no-ops setLang, and hides
// the Settings toggle (LANG_LOCKED). Set back to null to re-introduce the English option.
// The EN dictionary (./i18n.js, ~2100 lines) is fetched via dynamic import() only when
// the active language is actually 'en', so it costs nothing in the shared bundle while
// LANG_LOCK keeps everyone on 'nl' (the only live path today). If unlocked, there can be
// one brief paint of untranslated (Dutch) copy right after the reload while the chunk
// loads - acceptable since that path isn't live.
export const LANG_LOCK = 'nl';
export const LANG_LOCKED = LANG_LOCK != null;
let _lang = LANG_LOCK || 'nl';
let _EN = null;
function loadEN() {
  if (_EN || typeof window === 'undefined') return;
  import('./i18n').then((m) => { _EN = m.EN; });
}
if (typeof window !== 'undefined') {
  if (!LANG_LOCK) {
    try { _lang = window.localStorage.getItem('pb-lang') === 'en' ? 'en' : 'nl'; } catch {}
    if (_lang === 'en') loadEN();
  }
  try { if (typeof document !== 'undefined') document.documentElement.lang = _lang; } catch {}
}
export function getLang() { return _lang; }
export function setLang(name) {
  if (LANG_LOCK) return; // language is hard-locked (currently 'nl') - toggle disabled
  _lang = name === 'en' ? 'en' : 'nl';
  if (_lang === 'en') loadEN();
  try { if (typeof window !== 'undefined') window.localStorage.setItem('pb-lang', _lang); } catch {}
}
function interpolate(str, vars) {
  return String(str).replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] != null ? String(vars[k]) : ''));
}
export function t(nl, vars) {
  const en = _lang === 'en' && _EN ? _EN[nl] : undefined;
  const s = en != null ? en : nl;
  return vars ? interpolate(s, vars) : s;
}

// Whole days from local midnight today until an ISO date-string (accepts a full
// timestamp too - only the YYYY-MM-DD head is used). Null when unset/invalid.
export function daysUntilDate(iso) {
  if (!iso) return null;
  const tdy = new Date(); tdy.setHours(0, 0, 0, 0);
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  return Math.round((d - tdy) / 86400000);
}
// 'nog X dagen tot startdatum' countdown for a signed placement's first working
// day. Empty string once the date is unset or already reached/past (no countdown
// after the candidate has started).
export function startdateCountdownLabel(iso) {
  const n = daysUntilDate(iso);
  if (n == null || n < 0) return '';
  if (n === 0) return t('vandaag de startdatum');
  if (n === 1) return t('nog 1 dag tot startdatum');
  return t('nog {n} dagen tot startdatum', { n });
}

// ── Button + label style helpers (Claude Design "quiet chrome" recipe) ────────
// Action buttons: white bg, black text, 1px black border + 3px black bottom that
// reads like a hard shadow. No solid-black-background action buttons. Selected
// toggle/segment states render as SegControl's raised white pill on a subtle
// track, or a kobalt tint for other toggles (handled inline at each call site).
// Compose: style={{ ...btnBase, ...btnSize.md, ...btnKind.primary }}.
export const btnBase = { borderRadius: 5, fontFamily: 'inherit', fontWeight: 560, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap', boxSizing: 'border-box', border: BTN_RELIEF.border, boxShadow: BTN_RELIEF.boxShadow };
export const btnSize = { sm: { padding: '5px 10px', fontSize: HV2.fsText }, md: { padding: '8px 14px', fontSize: HV2.fsText }, lg: { padding: '9px 16px', fontSize: HV2.fsText } };
// Primary is the kobalt fill (no more white-face/3px-bottom). No colored-text
// kinds (green/blue retired) - rule 4: buttons carry white or ink text only.
// Border/boxShadow come from btnBase (relief) unless a kind overrides - secondary
// keeps its own grey border, ghost stays fully flat (transparent + no shadow).
export const btnKind = {
  primary:    { background: HV2.blue, color: HV2.onAccent },
  secondary:  { background: HV2.bg, color: HV2.ink, border: `1px solid ${HV2.borderStrong}` },
  subtle:     { background: HV2.subtle, color: HV2.ink },
  ghost:      { background: 'transparent', color: HV2.ink, border: '1px solid transparent', boxShadow: 'none' },
  danger:     { background: HV2.red, color: '#fff' },
  greenSolid: { background: HV2.green, color: '#fff' },
};
// Quiet micro-label: sentence case, no tracking, weight 600 (replaces the old
// uppercase + letterSpacing section/field labels we are migrating away from).
export const microLabel = { fontSize: HV2.fsText, fontWeight: 560, color: HV2.muted, letterSpacing: '0', textTransform: 'none' };
export const sectionTitle = { fontSize: HV2.fsText, fontWeight: 560, color: HV2.ink };

// Shared recipes for the app's ordinary surfaces. Components add layout and
// interaction; colour, edge and elevation changes start here. Floating chrome
// is the only surface family with a shadow.
export const modalScrim = 'rgba(0,0,0,0.4)';
export const modalShadow = '0 20px 60px rgba(0,0,0,0.25)';
export const modalRadius = 3; // universal window/popup edge radius (owner rule 3 Sep 2026): modals, windows, popovers, menus, dropdowns, tooltips, toasts
// Record pages opt in through their shell; shared sections elsewhere stay 1px.
export const recordSurfaceStyle = Object.freeze({
  '--pb-record-container-border': `3px solid ${HV2.borderStrong}`,
});
export const recordContainerBorder = (color = HV2.border) => `var(--pb-record-container-border, 1px solid ${color})`;
export const SURFACE = Object.freeze({
  canvas: Object.freeze({ get background() { return HV2.bg; } }),
  pageHeader: Object.freeze({ get background() { return HV2.bg; }, get borderBottom() { return `1px solid ${HV2.border}`; } }),
  section: Object.freeze({ background: HV2.bg, border: `1px solid ${HV2.border}`, borderBottom: recordContainerBorder(), borderRadius: 8 }),
  card: Object.freeze({ background: HV2.bg, border: `1px solid ${HV2.border}`, borderBottom: recordContainerBorder(), borderRadius: 8, padding: '12px 14px' }),
  field: Object.freeze({ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: 6, fontFamily: 'inherit', fontSize: HV2.fsText, color: HV2.ink, outline: 'none' }),
  select: Object.freeze({ boxSizing: 'border-box', padding: '7px 30px 7px 10px', background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: 6, fontFamily: 'inherit', fontSize: HV2.fsText, color: HV2.ink, outline: 'none', cursor: 'pointer' }),
  menu: Object.freeze({ background: HV2.bg, border: `1px solid ${HV2.borderStrong}`, borderRadius: modalRadius, boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }),
  tooltip: Object.freeze({ background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: modalRadius, boxShadow: '0 8px 24px rgba(0,0,0,0.16)' }),
  modal: Object.freeze({ background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: modalRadius, boxShadow: modalShadow }),
});
export const cardStyle = SURFACE.card;
export const inputStyle = SURFACE.field;

// Dropdown box colors. Prefer the <Select> component (below) over a native
// <select> - it themes the OPEN option list too, which CSS can't. selectStyle is
// still handy as the `style={}` you pass to <Select> (or a legacy <select>).
export const selectStyle = SURFACE.select;

export const REQ_BG = '#fffde7';
export function RequiredWrap({ children, show = true }) {
  if (!show) return children;
  return (
    <span style={{ position: 'relative', display: 'block' }}>
      {children}
      <span style={{ position: 'absolute', right: 8, top: 0, bottom: 0, display: 'flex', alignItems: 'center', pointerEvents: 'none' }} aria-hidden="true">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="#b8860b" stroke="none"><path d="M12 2l2.9 6.3L22 9.2l-5 5.2L18.2 22 12 18.5 5.8 22 7 14.4l-5-5.2 7.1-.9Z" /></svg>
      </span>
    </span>
  );
}

// ── P& System Alert: app-styled confirm()/alert() ────────────────────────────
// window.confirm/alert render the browser's own dialog ("pearsonapp.com
// meldt het volgende") which clashes with the UI. appConfirm/appAlert emit to a
// mounted host (AppDialogHost, in layout.js) and resolve a Promise; if no host
// is mounted they fall back to the native dialog so nothing ever hangs.
let _dialogSub = null;
export function _subscribeDialog(fn) { _dialogSub = fn; return () => { if (_dialogSub === fn) _dialogSub = null; }; }
export function appConfirm(message, opts = {}) {
  return new Promise((resolve) => {
    if (!_dialogSub) { resolve(typeof window !== 'undefined' ? window.confirm(message) : false); return; }
    _dialogSub({ kind: 'confirm', message, ...opts, resolve });
  });
}
export function appAlert(message, opts = {}) {
  return new Promise((resolve) => {
    if (!_dialogSub) { if (typeof window !== 'undefined') window.alert(message); resolve(); return; }
    _dialogSub({ kind: 'alert', message, ...opts, resolve });
  });
}
// appPrompt: the app-styled window.prompt() - a text field in the P& System
// dialog. Resolves to the entered string on confirm, or null on cancel (same
// contract as window.prompt). opts: defaultValue, placeholder, multiline,
// confirmLabel, cancelLabel, title, danger.
export function appPrompt(message, opts = {}) {
  return new Promise((resolve) => {
    if (!_dialogSub) { resolve(typeof window !== 'undefined' ? window.prompt(message, opts.defaultValue || '') : null); return; }
    _dialogSub({ kind: 'prompt', message, ...opts, resolve });
  });
}

// ── Bugmelding via de Research Agent ─────────────────────────────────────────
// Zelfde ontkoppeling als appConfirm hierboven, en om dezelfde reden: het venster
// hoort bij AgentReportModal.js, dat op zijn beurt Modal.js gebruikt en dus dit
// bestand leest. Een directe import terug zou een cirkel geven. De melder roept
// openAgentReport() aan, het in app/layout.js gemonteerde venster luistert. Zonder
// gemonteerd venster gebeurt er niets, in plaats van een fout.
let _agentReportSub = null;
export function _subscribeAgentReport(fn) { _agentReportSub = fn; return () => { if (_agentReportSub === fn) _agentReportSub = null; }; }
export function openAgentReport() { if (_agentReportSub) _agentReportSub(); }

// The ONE definition of what a salary field means, shown as an InfoHint next to
// every user-fillable salary input (kwalificaties, CRM detail, fee calculator,
// pre-close, nieuwe kandidaat) so amounts are always entered on the same basis.
export const SALARY_HINT = 'Bruto maandsalaris op basis van 40 uur, zonder emolumenten zoals vakantiegeld.';

// placeTooltip: the ONE viewport-aware placement math shared by every hover
// popover (InfoHint, HoverCard). Given the trigger's rect plus the popover's
// width/height, returns { top, left } in FIXED (viewport) coordinates: hang
// below the trigger, aligned to the requested edge, then clamp so the box stays
// 8px inside the viewport - shift left if it would overrun the right edge, flip
// above the trigger if it would overrun the bottom. Pass height 0 before the box
// is measured (places below); a follow-up call with the measured height applies
// the flip. This is why a tooltip is NEVER clipped by an ancestor overflow or
// the screen edge - it lives in a document.body portal, positioned here.
// `side` 'left' | 'right' puts the box BESIDE the trigger, top edges level, and
// swaps sides when the preferred side has no room: for a row inside a click menu,
// where a box below would cover the rows under it (eigenaarsbesluit 13 sep 2026).
export function placeTooltip(rect, width, height, align = 'left', side = 'bottom') {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  if (side === 'left' || side === 'right') {
    let left = side === 'left' ? rect.left - 8 - width : rect.right + 8;
    if (side === 'left' && left < 8) left = rect.right + 8;
    if (side === 'right' && left + width > vw - 8) left = rect.left - 8 - width;
    left = Math.max(8, Math.min(left, vw - 8 - width));
    let top = rect.top;
    if (height && top + height > vh - 8) top = Math.max(8, vh - 8 - height);
    return { top, left };
  }
  let left = align === 'right' ? rect.right - width : rect.left;
  if (left + width > vw - 8) left = vw - 8 - width; // overflow right -> shift left
  if (left < 8) left = 8;
  let top = rect.bottom + 6; // default: below the trigger
  if (height && top + height > vh - 8) { // would overflow bottom -> flip above
    const above = rect.top - 6 - height;
    top = above >= 8 ? above : Math.max(8, vh - 8 - height);
  }
  return { top, left };
}

// InfoHint: an (i) icon (or any custom trigger) with a REAL hover/focus tooltip.
// Native title= tooltips are flaky and often never render, so we draw our own
// popover (white face, neutral shadow). Pass `text` for the content; pass
// `children` to use a custom trigger (e.g. a warning triangle) instead of the
// default info circle. `align` picks which edge the popover hangs from.
//
// The popover renders through a document.body portal at position:fixed, anchored
// to the trigger's live getBoundingClientRect() (re-placed on scroll/resize while
// open). That's the fix for the recurring clip bug: an ancestor overflow:hidden
// (RailCard/Section) or the viewport edge can no longer cut the tooltip off -
// placeTooltip clamps it to stay on-screen. pointerEvents:none so it never
// intercepts a click. API (text/width/children/align) is unchanged.
export function InfoHint({ text, width = 260, children, align = 'left', color }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const [pos, setPos] = useState(null);
  const place = useCallback(() => {
    const el = anchorRef.current; if (!el) return;
    const h = tipRef.current ? tipRef.current.offsetHeight : 0;
    setPos(placeTooltip(el.getBoundingClientRect(), width, h, align));
  }, [width, align]);
  const show = () => { place(); setOpen(true); };
  // While open, re-measure (now that the box is mounted, so the bottom-flip can
  // apply) and follow the trigger on scroll/resize.
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);
  return (
    <span ref={anchorRef} style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
      <span tabIndex={0} onFocus={show} onBlur={() => setOpen(false)}
        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help', color: color || HV2.muted, outline: 'none' }}>
        {children || (
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
        )}
      </span>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <span ref={tipRef} role="tooltip" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9700, pointerEvents: 'none', width, ...SURFACE.tooltip, padding: '8px 10px', fontSize: HV2.fsText, fontWeight: 400, lineHeight: 1.45, color: HV2.ink2, whiteSpace: 'normal' }}>
          {text}
        </span>, document.body)}
    </span>
  );
}

// ── HintBody: de body van een InfoHint (eigenaarsbesluit 3 sep 2026) ────────
// Geen proza in een hover. Een hint is: een miniband als hij over EEN recordtype
// gaat (band = resolveRecordType(...)), een vette titel, en per feit een regel
// met een punt, een glyph of een kleurstaal. rows tekent een uitsplitsing als
// label/waarde-raster (rating, kans, statushistorie) met tabulaire cijfers
// rechts. Regels dragen geen punt aan het eind en hoogstens negen woorden.
// icon: een naam uit ENT_PATHS, MENU_ICON_PATHS of GLYPHS (in die volgorde),
// of een eigen node (bv. <NieuwHotIcon kind="hot" />). Nooit een emoji.
const HINT_GLYPH_STYLE = { flexShrink: 0, position: 'relative', top: 3 };
function hintGlyph(icon) {
  if (!icon) return null;
  if (typeof icon !== 'string') return <span style={{ display: 'inline-flex', ...HINT_GLYPH_STYLE }}>{icon}</span>;
  if (ENT_PATHS[ENT_ALIAS[icon] || icon]) return <EntityIcon entity={icon} size={12} style={HINT_GLYPH_STYLE} />;
  if (MENU_ICON_PATHS[icon]) return <MenuIcon name={icon} size={12} style={HINT_GLYPH_STYLE} />;
  if (GLYPHS[icon]) return <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={HINT_GLYPH_STYLE}><path d={GLYPHS[icon]} /></svg>;
  return null;
}
export function HintBody({ band = null, title, lines = [], rows = null, children }) {
  const dot = <span style={{ width: 4, height: 4, borderRadius: 2, background: 'currentColor', flexShrink: 0, display: 'inline-block', marginTop: 7 }} />;
  return (
    <span style={{ display: 'block' }}>
      {band ? <span style={{ display: 'block', marginBottom: 6 }}><RecordTypeBand rt={band} /></span> : null}
      {title ? <span style={{ display: 'block', fontWeight: 560, marginBottom: 3 }}>{title}</span> : null}
      {lines.length > 0 ? (
        <span style={{ display: 'block' }}>
          {lines.map((l, i) => {
            const it = typeof l === 'string' ? { text: l } : l;
            let lead;
            if (it.swatch) lead = <span style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, display: 'inline-block', marginTop: 4, boxSizing: 'border-box', background: it.outline ? 'transparent' : it.swatch, border: it.outline ? `2px solid ${it.swatch}` : 'none' }} />;
            else lead = hintGlyph(it.icon) || dot;
            return (
              <span key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: i ? 3 : 0 }}>
                {lead}
                <span style={{ minWidth: 0 }}>{it.label ? <span style={{ fontWeight: 560 }}>{it.label}{it.text ? ': ' : ''}</span> : null}{it.text}</span>
              </span>
            );
          })}
        </span>
      ) : null}
      {rows ? (
        <span style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '2px 12px', fontVariantNumeric: 'tabular-nums', marginTop: (title || lines.length) ? 4 : 0 }}>
          {rows.map(([k, v, c], i) => (
            <React.Fragment key={i}>
              <span style={{ color: c || undefined }}>{k}</span>
              <span style={{ textAlign: 'right', color: c || undefined }}>{v}</span>
            </React.Fragment>
          ))}
        </span>
      ) : null}
      {children}
    </span>
  );
}

// ── Bedragen: fee en salaris dragen de munt (eigenaarsregel 13 sep 2026) ─────
// DESIGN-SYSTEM regel 33. Een fee of een salaris op een kaart is nooit los
// samengestelde tekst met een euroteken erin: het is deze primitive, met de
// muntglyph als teken en een InfoHint met de onderbouwing eronder. Zo staat de
// salarisregel (40 uur, zonder emolumenten) en de fee-formule uit de getekende
// voorwaarden op EEN plek, en niet in elke kaart opnieuw.
//
// De munt: een cirkel met een dikke onderboog, met het euroteken erin. Stroke,
// currentColor, nooit een emoji (regel 7).
export function CoinGlyph({ size = 13, color = 'currentColor', style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeLinecap="round"
      aria-hidden="true" style={{ flexShrink: 0, verticalAlign: -2, ...style }}>
      <circle cx="12" cy="12" r="9.5" strokeWidth="1.5" />
      <path d="M4.2 15.5 A9.5 9.5 0 0 0 19.8 15.5" strokeWidth="3.2" />
      <path d="M15.2 8.6 A4.2 4.2 0 0 0 8.4 12 A4.2 4.2 0 0 0 15.2 15.4 M6.2 10.7 H12.6 M6.2 13.3 H12.6" strokeWidth="1.7" />
    </svg>
  );
}

// Een bedrag met de munt ervoor, in de vorm die de hele app deelt.
//   label: true -> het woord ervoor ('Fee: '), een string -> dat woord
//          ('Upfront fee'), false -> niets (alleen in een kolom of rij die het
//          woord al draagt).
//   source/formula: de onderbouwing voor de hover (zie feeHint in lib/fee.js).
const AMOUNT_HINT_WIDTH = 300;
function amountRow({ word, glyphSize, prefix, text, color, weight, style }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color, fontWeight: weight, ...style }}>
      {word ? `${word}: ` : null}
      <CoinGlyph size={glyphSize} />
      {prefix}
      {text}
    </span>
  );
}

export function FeeAmount({ value, estimated = false, source = null, formula = null, label = true, size = 13, color, weight, style }) {
  const n = Number(value) || 0;
  if (!n) return null;
  const tone = color || (estimated ? HV2.muted : HV2.ink);
  const word = label === true ? t('Fee') : (label || null);
  return (
    <InfoHint width={AMOUNT_HINT_WIDTH} color={tone || HV2.ink2} text={<HintBody {...feeHint(source, n, formula)} />}>
      {amountRow({ word, glyphSize: size, prefix: estimated ? '≈' : null, text: fmtFee(n), color: tone, weight, style })}
    </InfoHint>
  );
}

// Hetzelfde, maar voor een salaris: bruto per maand, geen centen, met 'bpm'
// erachter. Een band ([lo, hi], {lo, hi} of {min, max}) wint van de losse waarde.
export function SalarisAmount({ value, range = null, label = true, size = 13, color, weight, style }) {
  const n = Number(value) || 0;
  const band = salarisRangeOf(range);
  const text = band ? fmtSalarisRange(band.lo, band.hi) : (n ? fmtSalaris(n) : null);
  if (!text) return null;
  const word = label === true ? t('Salaris') : (label || null);
  return (
    <InfoHint width={AMOUNT_HINT_WIDTH} color={color || HV2.ink2} text={<HintBody {...salarisHint(n, band)} />}>
      {amountRow({ word, glyphSize: size, prefix: null, text, color, weight, style })}
    </InfoHint>
  );
}

// Verwacht deze maand: EEN tekst voor de Vandaag-rail en Beheer > Verwachte
// omzet (de formule staat ook in lib/dealForecast.js en de API-route).
export const VERWACHT_OMZET_HINT = {
  title: 'Verwacht deze maand',
  lines: [
    { label: 'Gerealiseerd', text: 'getekende omzet, bron Team target' },
    { label: 'Plus', text: 'gewogen pipeline die deze maand landt' },
    { label: 'Fee', text: 'Fee calculator, anders honorarium × jaarsalaris' },
    { label: 'Gewogen', text: 'fee × actuele proceskans, gelijk aan de proceskaart' },
    { label: 'Landt', text: 'startdatum, anders IV1 +6 wk, Aanbieding +2 wk, Closing +10 dg' },
  ],
};

// ── De sleepkern ──────────────────────────────────────────────────────────────
// EEN sleepmotor onder alle handvatten (kolommen, splitsbalken, panelen). Waarom
// pointer events en geen mousemove op window: met setPointerCapture blijft de
// muisaanwijzer aan het handvat geplakt, ook boven een iframe, een select-popup
// of buiten het venster. Dat was de belangrijkste jank van de oude sleep.
//
// Verder afgedwongen op één plek, zodat geen enkel handvat het kan vergeten:
// - alleen de primaire knop (een rechtsklik levert op macOS nooit een mouseup
//   af, dan bleef de sleep hangen met een col-resize cursor op body);
// - hoogstens één onMove per beeldframe, met de LAATST bekende positie, zodat
//   een tabel niet per muisbericht opnieuw layout doet;
// - pointercancel en lostpointercapture eindigen de sleep precies als pointerup,
//   en onEnd valt dan exact één keer;
// - verdwijnt het component midden in een sleep, dan worden de cursor en de
//   tekstselectieblokkade op body altijd teruggezet.
//
// Mouse-events blijven werken: bestaande aanroepers geven nog een onMouseDown
// door (ColResizeHandle in zijn oude vorm). Zonder pointerId valt de motor terug
// op mousemove/mouseup op window, precies het oude gedrag.
const rafSchedule = (fn) => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 16));
const rafCancel = (id) => { try { if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id); else clearTimeout(id); } catch { /* geen venster */ } };
const bodyDragStyle = (cursor) => { try { document.body.style.cursor = cursor; document.body.style.userSelect = cursor ? 'none' : ''; } catch { /* geen document */ } };

export function usePointerDrag() {
  const [dragging, setDragging] = React.useState(false);
  const live = React.useRef(null);

  // fireEnd=false is de opruimweg bij unmount: geen setState, geen onEnd, alleen
  // de listeners en de body-stijlen terug.
  const stop = React.useCallback((fireEnd) => {
    const d = live.current;
    if (!d) return;
    // Eerst het nog wachtende sleepframe TOEPASSEN, dan pas eindigen: bij een
    // snelle sleep vallen alle pointermoves binnen één beeldframe, en zonder
    // deze flush zou de sleep dan letterlijk niets doen (en elk sleepeinde zou
    // de laatste ~16ms verliezen).
    if (d.raf) {
      rafCancel(d.raf);
      d.raf = 0;
      const p = d.last;
      if (fireEnd && p && typeof d.onMove === 'function') d.onMove(p.x - d.startX, p.y - d.startY, p.ev);
    }
    live.current = null;
    try { d.detach(); } catch { /* al losgekoppeld */ }
    bodyDragStyle('');
    if (fireEnd) {
      setDragging(false);
      if (typeof d.onEnd === 'function') d.onEnd();
    }
  }, []);

  React.useEffect(() => () => stop(false), [stop]);

  const start = React.useCallback((e, opts = {}) => {
    if (!e) return;
    if (e.button != null && e.button !== 0) return;
    stop(false); // een blijven hangen sleep nooit dubbel laten lopen
    if (typeof e.preventDefault === 'function') e.preventDefault();
    if (typeof e.stopPropagation === 'function') e.stopPropagation();

    const pid = e.pointerId;
    const el = e.currentTarget;
    // Capture is een extraatje (sleep boven een iframe blijft plakken), maar de
    // listeners staan ALTIJD op window: pointer events bubbelen ook met capture
    // gewoon door, en als React het handvat midden in de sleep hervangt (de
    // bellijst hertekent bij de eerste sleep al zijn grepen) sterft een element-
    // listener met het element mee terwijl window alles blijft ontvangen.
    if (pid !== undefined && el && typeof el.setPointerCapture === 'function') {
      try { el.setPointerCapture(pid); } catch { /* onbekende pointer (synthetisch), geen capture */ }
    }
    const target = (typeof window !== 'undefined' ? window : null);
    if (!target) return;
    const names = pid !== undefined
      ? ['pointermove', 'pointerup', 'pointercancel']
      : ['mousemove', 'mouseup'];

    const d = {
      startX: e.clientX, startY: e.clientY,
      onMove: opts.onMove, onEnd: opts.onEnd,
      raf: 0, last: null, detach: () => {},
    };
    const flush = () => {
      d.raf = 0;
      const p = d.last;
      if (!p || live.current !== d) return;
      if (typeof d.onMove === 'function') d.onMove(p.x - d.startX, p.y - d.startY, p.ev);
    };
    const onMove = (ev) => {
      if (live.current !== d) return;
      d.last = { x: ev.clientX, y: ev.clientY, ev };
      if (!d.raf) d.raf = rafSchedule(flush);
    };
    const onEnd = () => stop(true);
    d.detach = () => {
      target.removeEventListener(names[0], onMove);
      for (let i = 1; i < names.length; i += 1) target.removeEventListener(names[i], onEnd);
    };
    target.addEventListener(names[0], onMove);
    for (let i = 1; i < names.length; i += 1) target.addEventListener(names[i], onEnd);

    live.current = d;
    setDragging(true);
    bodyDragStyle(opts.cursor || 'col-resize');
  }, [stop]);

  return { start, dragging };
}

// ── Resizable table columns ───────────────────────────────────────────────────
// Dependency-free column resizing. Drag a header's right edge to set that
// column's width; widths persist per-table in localStorage so a user's layout
// sticks across sessions. Usage: feed `widths` into a <colgroup> of <col> width
// styles (the table must use tableLayout:'fixed'), and drop a <ColResizeHandle>
// into each resizable, position:relative <th>. `storageKey` namespaces the saved
// widths; `defaults` is a { columnKey: px } map used until the user drags.
//
// Opslag gaat door de envelop van lib/resize-core.mjs; het oude kale
// { kolom: px }-object blijft leesbaar, dus niemand raakt zijn indeling kwijt.
// De blinde samenvoeging met de standaardwaarden blijft staan (hernoemde
// kolomsleutels opruimen is een aparte ronde).
export const COL_MIN_PX = 56;
export const COL_MAX_PX = 560;   // nieuw: zonder bovengrens sleepte één kolom de tabel uit beeld
export function useColumnResize(storageKey, defaults) {
  const [widths, setWidths] = React.useState(() => {
    if (typeof window === 'undefined') return { ...defaults };
    try {
      const saved = parseStored(window.localStorage.getItem(storageKey), 'colw');
      return saved ? { ...defaults, ...saved.w } : { ...defaults };
    } catch { return { ...defaults }; }
  });
  const core = usePointerDrag();
  const widthsRef = React.useRef(widths);
  widthsRef.current = widths;
  // Wisselt de sleutel zonder remount (het CRM-grid wisselt van tabblad, het
  // recordvenster van soort), dan de opslag voor de nieuwe sleutel herlezen;
  // de initializer hierboven draait immers maar één keer per mount.
  const defaultsRef = React.useRef(defaults);
  defaultsRef.current = defaults;
  const keyRef = React.useRef(storageKey);
  React.useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    try {
      const saved = parseStored(window.localStorage.getItem(storageKey), 'colw');
      setWidths(saved ? { ...defaultsRef.current, ...saved.w } : { ...defaultsRef.current });
    } catch { setWidths({ ...defaultsRef.current }); }
  }, [storageKey]);
  // Aanvullend (niets aan het bestaande gedrag verandert): een grid dat zelf een
  // ander sleepgedrag nodig heeft - de bellijst verschuift breedte tussen twee
  // buurkolommen omdat het totaal daar vastligt - zet de breedtes rechtstreeks
  // met setWidths en schrijft ze aan het eind van de sleep weg met persist().
  // Vandaar het schrijven IN de updater: alleen die ziet de breedtes die in
  // dezelfde afhandelaar net zijn gezet. Een dubbele schrijfbeurt (React roept
  // een updater in dev twee keer aan) levert exact dezelfde string op.
  const persist = React.useCallback(() => setWidths((prev) => {
    try { window.localStorage.setItem(storageKey, serializeStored('colw', { w: prev })); } catch { /* geen opslag */ }
    return prev;
  }), [storageKey]);

  // Start vanaf de getekende breedte: een grid dat een ondergrens boven de
  // opgeslagen breedte legt (CRM-kop die nooit afknipt) tekent max(opgeslagen,
  // min), dus slepen vanaf de opgeslagen waarde gaf een dode zone.
  const startResize = (key, e, min = COL_MIN_PX, max = COL_MAX_PX) => {
    const startW = Math.max(min, widthsRef.current[key] || defaults[key] || 120);
    core.start(e, {
      cursor: 'col-resize',
      onMove: (dx) => {
        const w = Math.min(max, Math.max(min, Math.round(startW + dx)));
        setWidths((prev) => (prev[key] === w ? prev : { ...prev, [key]: w }));
      },
      onEnd: persist,
    });
  };
  const widthOf = (key) => widths[key] || defaults[key] || 120;
  const reset = () => { setWidths({ ...defaults }); try { window.localStorage.removeItem(storageKey); } catch { /* geen opslag */ } };
  // Eén kolom terug naar zijn standaardbreedte (dubbelklik zonder autofit).
  const resetCol = (key) => {
    setWidths((prev) => { const next = { ...prev }; delete next[key]; return { ...defaults, ...next }; });
    persist();
  };
  const nudge = (key, dir, min = COL_MIN_PX, max = COL_MAX_PX) => {
    const cur = Math.max(min, widthsRef.current[key] || defaults[key] || 120);
    const w = Math.min(max, Math.max(min, cur + 16 * (Number(dir) || 0)));
    setWidths((prev) => (prev[key] === w ? prev : { ...prev, [key]: w }));
    persist();
  };
  return { widths, widthOf, startResize, reset, resetCol, nudge, setWidths, persist };
}

// Meet een kolom en geef de breedte die zijn inhoud precies laat passen. Alleen
// de kop plus de eerste `sample` lichaamsrijen: bij duizenden rijen is elke cel
// meten een forced layout van een halve seconde, en de brede uitschieter zit in
// de praktijk in het zicht. Geen tabel of geen cel -> null, dan blijft de
// breedte staan.
export function autofitColumn(tableEl, colIndex, opts = {}) {
  if (!tableEl || typeof tableEl.querySelectorAll !== 'function') return null;
  const sample = Number.isFinite(opts.sample) ? opts.sample : 200;
  const rows = [];
  try {
    if (tableEl.tHead) rows.push(...Array.from(tableEl.tHead.rows));
    const body = tableEl.tBodies && tableEl.tBodies[0];
    if (body) rows.push(...Array.from(body.rows).slice(0, sample));
  } catch { return null; }
  const measured = [];
  for (let i = 0; i < rows.length; i += 1) {
    const cell = rows[i].cells && rows[i].cells[colIndex];
    if (cell && Number.isFinite(cell.scrollWidth)) measured.push(cell.scrollWidth);
  }
  return autofitFromWidths(measured, { min: opts.min, max: opts.max });
}

// The draggable grip on a header cell's right edge. Place inside a position:relative
// <th>; it swallows click so resizing never triggers the column's sort handler.
//
// Twee vormen. ZONDER `resize` is dit letterlijk het oude handvat: een span die
// een meegegeven onMouseDown doorgeeft, voor de aanroepers die nog niet
// omgebouwd zijn. MET `resize` (de terugkeer van useColumnResize) doet het
// handvat het zelf: slepen op pointer events, dubbelklik past de kolom
// automatisch of zet hem terug, en de pijltoetsen verschuiven hem met 16px.
export function ColResizeHandle({ resize, colKey, min = COL_MIN_PX, max = COL_MAX_PX, onAutofit, title, onMouseDown }) {
  const grip = { position: 'absolute', top: 0, right: -3, width: 7, height: '100%', cursor: 'col-resize', zIndex: 3, touchAction: 'none' };
  if (!resize) {
    return (
      <span onMouseDown={onMouseDown} onClick={(e) => e.stopPropagation()} title={title} style={grip} />
    );
  }
  const applyAutofit = () => {
    const px = typeof onAutofit === 'function' ? onAutofit(colKey) : null;
    if (typeof px === 'number' && Number.isFinite(px)) {
      resize.setWidths((prev) => ({ ...prev, [colKey]: px }));
      resize.persist();
      return;
    }
    if (typeof onAutofit !== 'function') resize.resetCol(colKey);
  };
  return (
    <span role="separator" aria-orientation="vertical" tabIndex={0}
      aria-label={title || 'Kolombreedte aanpassen'} title={title}
      onPointerDown={(e) => resize.startResize(colKey, e, min, max)}
      onDoubleClick={applyAutofit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); resize.nudge(colKey, -1, min, max); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); resize.nudge(colKey, 1, min, max); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); }
        else if (e.key === 'Escape') { try { e.currentTarget.blur(); } catch { /* geen focus */ } }
      }}
      style={{ ...grip, outline: 'none' }} />
  );
}

// ── Versleepbare tweekolomsverdeling ──────────────────────────────────────────
// Twee panelen die samen de volle breedte vullen: wat links erbij krijgt, geeft
// rechts af. Daarom bewaart deze hook een VERHOUDING en geen pixels: de
// container is de viewport min de railbreedte en die verandert live als de rail
// versleept wordt (railChrome), een ratio overleeft dat, een px-breedte niet.
// Sleepgedrag is gemodelleerd op de bellijst (buurkolommen, CallLists.js).
//
// opts.collapsible ('left' | 'right' | false) zet inklappen aan: sleep je het
// betreffende paneel meer dan COLLAPSE_SLACK px ONDER zijn minimum, dan meldt de
// hook collapsed=true, live tijdens de sleep, en terugslepen haalt het weer op.
// De hook MELDT dat alleen; het paneel op nul zetten of verbergen doet de
// aanroeper zelf, want elk scherm heeft daar zijn eigen layout voor.
const COLLAPSE_SLACK = 120;
export function useSplitRatio(storageKey, defaultRatio = 0.5, opts = {}) {
  const collapsible = opts.collapsible || false;
  const [state, setState] = React.useState(() => {
    const fallback = { ratio: defaultRatio, collapsed: false };
    if (typeof window === 'undefined') return fallback;
    try {
      const saved = parseStored(window.localStorage.getItem(storageKey), 'split');
      if (!saved) return fallback;
      const ok = saved.ratio >= SPLIT_MIN_RATIO && saved.ratio <= SPLIT_MAX_RATIO;
      return { ratio: ok ? saved.ratio : defaultRatio, collapsed: collapsible ? !!saved.collapsed : false };
    } catch { return fallback; }
  });
  const core = usePointerDrag();
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const pending = React.useRef(null);   // de waarde van het laatste sleepframe
  const lastAvail = React.useRef(0);    // voor de pijltoetsen, die niets meten
  // Wisselt de sleutel zonder remount (het recordvenster wisselt van soort),
  // dan de opslag voor de nieuwe sleutel herlezen; de initializer draait maar
  // één keer per mount.
  const keyRef = React.useRef(storageKey);
  React.useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    const fallback = { ratio: defaultRatio, collapsed: false };
    try {
      const saved = parseStored(window.localStorage.getItem(storageKey), 'split');
      if (!saved) { setState(fallback); return; }
      const ok = saved.ratio >= SPLIT_MIN_RATIO && saved.ratio <= SPLIT_MAX_RATIO;
      setState({ ratio: ok ? saved.ratio : defaultRatio, collapsed: collapsible ? !!saved.collapsed : false });
    } catch { setState(fallback); }
  }, [storageKey, defaultRatio, collapsible]);

  const persist = React.useCallback((s) => {
    try { window.localStorage.setItem(storageKey, serializeStored('split', s)); } catch { /* geen opslag */ }
  }, [storageKey]);

  // Alleen muis/pen/aanraking: de splitsbalk bestaat niet op mobiel (daar staan
  // de kolommen onder elkaar met een tabstrook), net als de andere handvatten.
  const startDrag = (e, containerEl) => {
    if (!containerEl || typeof containerEl.getBoundingClientRect !== 'function') return;
    let pad = 0;
    try {
      const cs = window.getComputedStyle(containerEl);
      pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    } catch { /* geen computed style */ }
    const avail = containerEl.getBoundingClientRect().width - pad - SPLIT_GUTTER;
    if (!(avail > 0)) return;
    lastAvail.current = avail;
    const startRatio = stateRef.current.ratio;
    pending.current = null;
    core.start(e, {
      cursor: 'col-resize',
      onMove: (dx) => {
        const raw = startRatio + dx / avail;
        const ratio = Math.round(clampSplit(raw, avail) * 1000) / 1000;
        // De ONgeklemde breedte bepaalt het inklappen: geklemd blijft het paneel
        // op zijn minimum staan en zou de drempel nooit gehaald worden.
        let collapsed = false;
        if (collapsible) {
          const px = collapsible === 'left' ? raw * avail : (1 - raw) * avail;
          collapsed = px < SPLIT_MIN_PANE - COLLAPSE_SLACK;
        }
        const next = { ratio, collapsed };
        pending.current = next;
        setState((prev) => (prev.ratio === ratio && prev.collapsed === collapsed ? prev : next));
      },
      onEnd: () => {
        persist(pending.current || stateRef.current);
        pending.current = null;
      },
    });
  };
  const reset = () => {
    const next = { ratio: defaultRatio, collapsed: false };
    setState(next);
    try { window.localStorage.removeItem(storageKey); } catch { /* geen opslag */ }
  };
  const setCollapsed = (v) => {
    const next = { ratio: stateRef.current.ratio, collapsed: !!v };
    setState(next);
    persist(next);
  };
  // Zonder ooit gesleept te hebben is de container niet gemeten; dan gebeurt er
  // niets, liever dan een sprong op een geraden breedte.
  const nudge = (dir) => {
    const avail = lastAvail.current;
    if (!(avail > 0)) return;
    const next = { ratio: nudgeRatio(stateRef.current.ratio, dir, avail), collapsed: stateRef.current.collapsed };
    setState(next);
    persist(next);
  };
  return {
    ratio: state.ratio, dragging: core.dragging, collapsed: state.collapsed,
    startDrag, reset, setCollapsed, nudge, gutter: SPLIT_GUTTER,
  };
}

// ── Versleepbaar paneel met vaste breedte ─────────────────────────────────────
// De railvariant: een paneel van zoveel PIXELS met daarnaast de vloeiende rest
// (inbox-bronnenlijst, Training-boom, Markten-rail). Hier is een pixelbreedte
// wél de juiste opslag: het paneel hoort even breed te blijven als het venster
// verandert, de rest rekt mee. `edge` zegt aan welke kant van het paneel het
// handvat zit: 'right' (paneel links, naar rechts slepen is breder) of 'left'.
export function usePaneWidth(storageKey, defaultPx, opts = {}) {
  const min = Number.isFinite(opts.min) ? opts.min : 160;
  const max = Number.isFinite(opts.max) ? opts.max : 560;
  const edge = opts.edge === 'left' ? 'left' : 'right';
  const collapsible = !!opts.collapsible;
  const clampPx = React.useCallback((v) => Math.min(max, Math.max(min, Math.round(Number(v) || 0))), [min, max]);
  const [state, setState] = React.useState(() => {
    const fallback = { width: clampPx(defaultPx), collapsed: false };
    if (typeof window === 'undefined') return fallback;
    try {
      const saved = parseStored(window.localStorage.getItem(storageKey), 'pane');
      if (!saved) return fallback;
      return { width: clampPx(saved.width), collapsed: collapsible ? !!saved.collapsed : false };
    } catch { return fallback; }
  });
  const core = usePointerDrag();
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const pending = React.useRef(null);
  // Zelfde afspraak als useColumnResize/useSplitRatio: wisselt de sleutel
  // zonder remount, dan de opslag voor de nieuwe sleutel herlezen.
  const keyRef = React.useRef(storageKey);
  React.useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    const fallback = { width: clampPx(defaultPx), collapsed: false };
    try {
      const saved = parseStored(window.localStorage.getItem(storageKey), 'pane');
      setState(saved ? { width: clampPx(saved.width), collapsed: collapsible ? !!saved.collapsed : false } : fallback);
    } catch { setState(fallback); }
  }, [storageKey, defaultPx, collapsible, clampPx]);

  const persist = React.useCallback((s) => {
    try { window.localStorage.setItem(storageKey, serializeStored('pane', s)); } catch { /* geen opslag */ }
  }, [storageKey]);

  const startDrag = (e) => {
    const startW = stateRef.current.width;
    pending.current = null;
    core.start(e, {
      cursor: 'col-resize',
      onMove: (dx) => {
        const raw = startW + (edge === 'left' ? -dx : dx);
        const width = clampPx(raw);
        const collapsed = collapsible ? raw < min - COLLAPSE_SLACK : false;
        const next = { width, collapsed };
        pending.current = next;
        setState((prev) => (prev.width === width && prev.collapsed === collapsed ? prev : next));
      },
      onEnd: () => {
        persist(pending.current || stateRef.current);
        pending.current = null;
      },
    });
  };
  const reset = () => {
    setState({ width: clampPx(defaultPx), collapsed: false });
    try { window.localStorage.removeItem(storageKey); } catch { /* geen opslag */ }
  };
  const setCollapsed = (v) => {
    const next = { width: stateRef.current.width, collapsed: !!v };
    setState(next);
    persist(next);
  };
  const nudge = (dir) => {
    const next = { width: clampPx(stateRef.current.width + 16 * (Number(dir) || 0)), collapsed: stateRef.current.collapsed };
    setState(next);
    persist(next);
  };
  return { width: state.width, dragging: core.dragging, collapsed: state.collapsed, startDrag, reset, setCollapsed, nudge };
}

// ── Het handvat op een naad ───────────────────────────────────────────────────
// Werkt op zowel useSplitRatio als usePaneWidth: geef de terugkeer van de hook
// mee als `ctl`. Chroomloos: een haarlijn (HV2.divider) die bij hover (of focus)
// donkerder wordt, geen knop, geen vlak. De ouder bepaalt de
// geometrie van de naad; het handvat vult die en is 16px breed, de breedte van
// de gutter tussen twee panelen. Dubbelklik zet de verdeling terug.
export function SplitHandle({ ctl, containerRef, label, onDoubleClick }) {
  const [hover, setHover] = React.useState(false);
  const active = ctl && ctl.dragging;
  // In rust een haarlijn (2 okt 2026), zodat de naad te vinden is; hover en slepen sterker.
  const line = active ? HV2.ink2 : (hover ? HV2.border : HV2.divider);
  return (
    <div role="separator" aria-orientation="vertical" tabIndex={0} aria-label={label}
      onPointerDown={(e) => ctl.startDrag(e, containerRef && containerRef.current)}
      onDoubleClick={onDoubleClick || ctl.reset}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)} onBlur={() => setHover(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); ctl.nudge(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); ctl.nudge(1); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); }
        else if (e.key === 'Escape') { try { e.currentTarget.blur(); } catch { /* geen focus */ } }
      }}
      style={{ alignSelf: 'stretch', flex: '0 0 auto', width: SPLIT_GUTTER, display: 'flex', justifyContent: 'center', cursor: 'col-resize', touchAction: 'none', outline: 'none' }}>
      <div style={{ width: 1, background: line }} />
    </div>
  );
}

// Count badge: the outline pill that shows a number beside a label.
// Badge (eigenaarsregel 17 sep 2026): EEN badge-primitief voor acroniemen,
// tags en tellingen. Vervangt Sym, Pill en CountBadge. Ontwerp B: 1.5px
// inkrand, witte vulling, radius 3, fsBadge/700, pad 1px 6px. active=true
// (voor tellingen) trekt de rand naar ink, anders border. color overschrijft
// de randkleur (voor gekleurde IV-badges). filled zet bgSub als vulling.
export function Badge({ children, active = false, color, filled = false, title, style }) {
  const bc = color || (active ? HV2.ink : HV2.border);
  return <span title={title} style={{ display: 'inline-block', border: `1.5px solid ${bc}`, color: HV2.ink, background: filled ? HV2.bgSub : HV2.bg, borderRadius: 3, padding: '1px 6px', fontSize: HV2.fsBadge, fontWeight: 650, lineHeight: '14px', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 18, textAlign: 'center', ...style }}>{children}</span>;
}
// DE neutrale tekst-chip (eigenaarsbesluit 17 juli 2026): ELKE statische
// informatieve chip/label in de app is dit component of TonePill (semantische
// status). Nooit meer inline spans met eigen border/radius/padding - de
// design-guard-hook en lib/design-chips.test.mjs bewaken dat. Contract:
// radius 4, hairline HV2.border, bgSub-vulling, inkt-tekst, fsText/600.
export function Chip({ children, title, style }) {
  return <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${HV2.border}`, color: HV2.ink2, background: HV2.bgSub, borderRadius: 4, padding: '1px 8px', fontSize: HV2.fsText, fontWeight: 560, lineHeight: '18px', whiteSpace: 'nowrap', flexShrink: 0, ...style }}>{children}</span>;
}

// HET haarlijnraster (DESIGN-SYSTEM.md Regel 39, eigenaarsregel 17 september
// 2026): Kerngegevens op elk recordvenster, het formulier Nieuwe kandidaat en de
// kwalificatie zijn dit component. Cellen dragen HAIRLINE_CELL (wit, 9/14
// padding), een cel over de volle breedte HAIRLINE_CELL_FULL. Niemand telt
// cellen: het raster sluit zichzelf via lib/hairline-grid.mjs op de echte DOM,
// na elke render en bij elke wijziging in de kinderen (een cel die pas na een
// fetch verschijnt, een cel die een component niet rendert). Een ongepaarde
// halve cel spant de rest van zijn rij. Vulcellen, een % 2 formule of een
// handgeschreven raster met gap 1 op HV2.divider zijn verboden; de ratchet in
// lib/design-kerngegevens.test.mjs bewaakt dat.
export const HAIRLINE_CELL = Object.freeze({ background: HV2.bg, padding: '9px 14px', minWidth: 0 });
export const HAIRLINE_CELL_FULL = Object.freeze({ ...HAIRLINE_CELL, gridColumn: '1 / -1' });
export const HAIRLINE_LABEL = Object.freeze({ fontSize: HV2.fsText, fontWeight: 560, color: HV2.muted, marginBottom: 3 });
export const HAIRLINE_VALUE = Object.freeze({ fontSize: HV2.fsText, color: HV2.ink, wordBreak: 'break-word' });

// De gap volgt de pixelverhouding (browserzoom, extern scherm): zie
// hairlineGapPx. Een matchMedia op de huidige resolutie meldt elke zoomstap.
function useHairlineGap() {
  const [gap, setGap] = useState(1);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mq = null;
    const sync = () => {
      setGap(hairlineGapPx(window.devicePixelRatio));
      if (mq) mq.removeEventListener('change', sync);
      mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mq.addEventListener('change', sync);
    };
    sync();
    return () => { if (mq) mq.removeEventListener('change', sync); };
  }, []);
  return gap;
}

export const HairlineGrid = forwardRef(function HairlineGrid({ cols = 2, style, children, ...rest }, fwd) {
  const gap = useHairlineGap();
  const inner = useRef(null);
  const setRef = useCallback((el) => {
    inner.current = el;
    if (typeof fwd === 'function') fwd(el);
    else if (fwd) fwd.current = el;
  }, [fwd]);
  // Geen deps: elke render van de aanroeper kan cellen wisselen. De observer
  // vangt de rest (een kind dat zelf rendert). Alleen inline gridColumn wordt
  // gelezen, dus dit dwingt geen layout af.
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return undefined;
    const run = () => closeHairlineGrid(el, cols);
    run();
    if (typeof MutationObserver !== 'function') return undefined;
    const mo = new MutationObserver(run);
    mo.observe(el, { childList: true });
    // cols 'auto' (auto-fit, template uit style): de kolomtelling verandert
    // met de breedte, dus de sluiting loopt ook na elke resize.
    const ro = cols === 'auto' && typeof ResizeObserver === 'function' ? new ResizeObserver(run) : null;
    if (ro) ro.observe(el);
    return () => { mo.disconnect(); if (ro) ro.disconnect(); };
  });
  const auto = cols === 'auto';
  const n = auto ? 'auto' : Math.max(1, Number(cols) || 1);
  return (
    <div ref={setRef} data-hairline-grid={n} style={{ display: 'grid', ...(auto ? {} : { gridTemplateColumns: `repeat(${n}, 1fr)` }), gap, background: HV2.divider, ...style }} {...rest}>
      {children}
    </div>
  );
});

// DE industriepil (13 sep 2026): een industrie draagt altijd zijn eigen icoon,
// en daarom is dit een eigen primitief naast Chip in plaats van een Chip met een
// plaatje erin. Witte vulling en een donkere rand, zodat een rij industrieën
// zichtbaar een andere laag is dan de grijze tekstchips ernaast. Zelfde weg als
// MarketGlyph: de SVG gaat als data-URI in een <img> en komt dus nooit als
// pagina-HTML binnen.
export function IndustryPill({ name, size = 14, onRemove, title }) {
  const svg = industryGlyph(name, { size });
  return (
    <span title={title || name} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: HV2.bg, border: `1px solid ${HV2.mute2}`, color: HV2.ink, borderRadius: 4, padding: '1px 8px 1px 5px', fontSize: HV2.fsText, fontWeight: 560, lineHeight: '18px', whiteSpace: 'nowrap', flexShrink: 0 }}>
      <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`} alt="" width={size} height={size} style={{ display: 'block', width: size, height: size, flexShrink: 0 }} />
      {name}
      {onRemove && (
        <button type="button" onClick={onRemove} title={t('Verwijder')}
          style={{ width: 16, height: 16, border: 'none', borderRadius: 5, background: 'transparent', cursor: 'pointer', color: HV2.mute2, fontSize: HV2.fsText, lineHeight: 1, padding: 0 }}>×</button>
      )}
    </span>
  );
}

// ── Track containers (eigenaarsbesluit 13 augustus 2026) ───────────────────
// EEN universeel systeem voor de drie pipeline-tracks (Interviews, Plaatsingen,
// CV Send) die tot nu toe als losse gekleurde dots over twee tokenfamilies
// (HV2 en TONE_COLORS) uiteen waren gedreven. TRACK_META is de ene bron voor
// kleur, tint en label per track; TrackIcon rendert het bijpassende Material
// Symbols-icoon (public/icons) getint via CSS-mask, want een <img> kan niet
// van kleur wisselen; TrackLane is de laan-shell eromheen. Sourcing is een
// APARTE track (eigen accentSrc-dot in ProcessCvLanes.js) en hoort hier niet bij.
export const TRACK_META = {
  placement: { key: 'placement', label: 'Plaatsingen', color: HV2.green, tint: '#e6ece8', icon: 'plaatsing' },
  interview: { key: 'interview', label: 'Interviews', color: HV2.blue, tint: '#e6e9f0', icon: 'interview' },
  // CV Send tekent het entiteitsglyph (document met pijl), geen Material-bestand.
  cvsend: { key: 'cvsend', label: 'CV Send', color: HV2.accentSrc, tint: HV2.srcTint, ent: 'cvSend' },
};

// Getint Material Symbols-icoon via CSS-mask (nooit <img>: dat kan niet van
// kleur wisselen). `color` overschrijft de tracktint, bijvoorbeeld HV2.muted
// voor een gedempte archiefvariant van dezelfde track.
export function TrackIcon({ track, size, color }) {
  const meta = TRACK_META[track];
  const px = size || 16;
  if (meta.ent) return <EntityIcon entity={meta.ent} size={px} color={color || meta.color} />;
  return (
    <span aria-hidden="true" style={{
      display: 'inline-block', width: px, height: px, background: color || meta.color,
      WebkitMaskImage: `url(/icons/${meta.icon}.svg)`, maskImage: `url(/icons/${meta.icon}.svg)`,
      WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center', maskPosition: 'center',
      WebkitMaskSize: 'contain', maskSize: 'contain',
    }} />
  );
}

// Laan-shell voor een track: getinte 22px iconvierkant + label + Badge in
// de kop, optionele actie rechts uitgelijnd, content eronder. Geometrie volgt
// de bestaande laan in ProcessCvLanes.js.
export function TrackLane({ track, count, action, children }) {
  const meta = TRACK_META[track];
  return (
    <div style={{ ...SURFACE.section, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderBottom: `1px solid ${HV2.divider}` }}>
        <span style={{ width: 22, height: 22, borderRadius: 4, background: meta.tint, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <TrackIcon track={track} size={15} />
        </span>
        <span style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.ink }}>{meta.label}</span>
        <Badge>{count}</Badge>
        {action && <span style={{ marginLeft: 'auto' }}>{action}</span>}
      </div>
      <div style={{ padding: '2px 10px 6px' }}>{children}</div>
    </div>
  );
}

// ActionRow: DE rijvorm van de CV-kaart op de kandidaatpagina (eigenaarsbesluit A1,
// 13 augustus 2026). Markt, Beoordeling en Kandidaat kwalificatie stonden er in drie
// verschillende vormen: een omrande knop met een 34px-icoontegel, een kale sectiekop
// met twee meterblokken, en een dunne 20px-uitklapregel. Niets lijnde uit. Deze
// primitief legt de grammatica vast, zodat de drie niet opnieuw uit elkaar kunnen
// groeien en een vierde rij vanzelf meedoet:
//
//   [34px icoonvak] [vette label / gedempte samenvatting van EEN regel] [rechterslot]
//
// Het rechterslot houdt bewust de INTERACTIE vast (de beoordelingsbolletjes, een
// statuswoord, een chevron): een samenvattingsrij mag niets wegnemen wat er nu
// zonder klik kan. `onClick` maakt er een knop van; zonder onClick is het een
// gewone rij, zodat een rij die alleen zijn rechterslot bedient geen dode klikzone
// krijgt.
export function ActionRow({ icon, label, summary, right, onClick, disabled = false, title, style }) {
  const inner = (<>
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 6, background: HV2.bgPage, color: HV2.ink2, flexShrink: 0, overflow: 'hidden' }}>{icon}</span>
    <span style={{ minWidth: 0, flex: 1, display: 'block', textAlign: 'left' }}>
      <span style={{ display: 'block', fontSize: HV2.fsText, fontWeight: 650, color: HV2.ink }}>{label}</span>
      {summary ? <span style={{ display: 'block', fontSize: HV2.fsText, color: HV2.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary}</span> : null}
    </span>
    {right ? <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{right}</span> : null}
  </>);
  const base = { width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: 6, fontFamily: 'inherit', boxSizing: 'border-box', ...style };
  if (!onClick) return <div title={title} style={base}>{inner}</div>;
  return <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} title={title} style={{ ...base, cursor: disabled ? 'default' : 'pointer' }}>{inner}</button>;
}

// De chevron die een ActionRow afsluit: 'hier zit meer achter'. Als los teken zodat
// elke rij hem identiek tekent.
export function RowChevron() {
  return <span aria-hidden="true" style={{ fontSize: 16, color: HV2.mute2, flexShrink: 0, lineHeight: 1 }}>{'›'}</span>;
}

// 24h time picker. A native <input type=time> renders AM/PM on en-US machines;
// this select forces the 24h clock everywhere. 15-min steps, 06:00–22:45. Any
// pre-existing odd-minute value is injected so editing an old record never drops it.
// onChange receives the value string directly (not an event).
export const TIME_OPTS_24 = (() => { const o = []; for (let h = 6; h <= 22; h++) for (const m of [0, 15, 30, 45]) o.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`); return o; })();
// Select - the app-styled dropdown that REPLACES native <select>. A native
// <select> can only style its closed box (the pb-select chevron); the OPEN option
// list is drawn by the OS/browser and cannot be themed - that grey MacOS/Chrome
// popup is exactly the "vibecoded tell" we avoid. This renders a button trigger +
// our own portal popover, so the open menu matches the app (HV2 tokens, hairline
// border, hover rows, selected check). It is a DROP-IN for <select>: keep the same
// value / onChange / style / <option> (and <optgroup>) children. onChange still
// receives a synthetic { target: { value } }, so both `onChange={(e)=>set(e.target.value)}`
// and `onChange={onChange}` keep working. The menu portals to <body> at a high
// z-index so it escapes modal/overflow clipping (detail windows, dialogs).
function optionText(node) {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(optionText).join('');
  if (React.isValidElement(node)) return optionText(node.props.children);
  return '';
}

function flattenOptions(children) {
  const flat = []; // { value, label, disabled, group, searchLabel, market }
  const push = (el, group) => {
    if (!el || el.type !== 'option') return;
    const val = el.props.value !== undefined ? el.props.value : el.props.children;
    // Een label bestaat vaak uit meerdere React-kinderen, bijvoorbeeld
    // naam + functie of job + statushint. Doorzoek de volledige zichtbare tekst,
    // niet de interne slug/id waarop de optie toevallig is opgeslagen.
    const textLabel = optionText(el.props.children).trim();
    const searchLabel = el.props['data-search-label'] ?? (textLabel || (typeof val === 'string' || typeof val === 'number' ? String(val) : ''));
    flat.push({ value: val, label: el.props.children, disabled: !!el.props.disabled, group: group || null,
      searchLabel, market: el.props['data-market-option'] !== undefined });
  };
  React.Children.forEach(children, (child) => {
    if (!child || typeof child !== 'object') return;
    if (child.type === 'optgroup') React.Children.forEach(child.props.children, (o) => push(o, child.props.label));
    else push(child, null);
  });
  return flat;
}

// Shared appearance for open choice menus. Changing the surface or label rule
// here updates Select and the custom CRM pickers that use these same styles.
export const DROPDOWN_STYLE = Object.freeze({
  surface: SURFACE.menu,
  optionLabel: Object.freeze({ flex: 1, minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere' }),
});

export function Select({ value, onChange, children, style, title, disabled, className, placeholder, endIcon,
  searchable = false, searchPlaceholder, menuMinWidth, ...rest }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1); // keyboard-highlighted index
  const [pos, setPos] = useState(null); // { left, top, width, drop, maxH }
  const [query, setQuery] = useState('');
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const opts = flattenOptions(children);
  // marketOptions() marks its options. Every market dropdown thereby gets the
  // same searchable, wide menu without each callsite having to remember props.
  const isMarketSelect = opts.some((o) => o.market);
  const effectiveSearchable = searchable || isMarketSelect;
  const menuFloor = menuMinWidth || (isMarketSelect ? 260 : DROPDOWN_LAYOUT.minWidth);
  const queryNorm = query.trim().toLocaleLowerCase('nl-NL');
  const visibleOpts = opts.map((o, index) => ({ ...o, index })).filter((o) => (
    !queryNorm || matchesSearchText(query, o.searchLabel)
  ));
  const curIdx = opts.findIndex((o) => String(o.value) === String(value ?? ''));
  const cur = curIdx >= 0 ? opts[curIdx] : null;
  const isPlaceholder = !cur || cur.value === '' || cur.value == null;
  const display = cur ? cur.label : (placeholder || '');

  const place = useCallback(() => {
    const el = btnRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const drop = spaceBelow < 240 && spaceAbove > spaceBelow ? 'up' : 'down';
    const maxH = Math.max(140, Math.min(320, (drop === 'up' ? spaceAbove : spaceBelow) - 12));
    // The menu is an overlay, so its width follows the option labels rather
    // than the compact trigger. Extra room covers icons/checks and scrollbars.
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.font = `${HV2.fsText}px ${getComputedStyle(el).fontFamily || 'sans-serif'}`;
    const contentWidth = ctx ? opts.reduce((widest, o) => Math.max(widest, ctx.measureText(String(o.searchLabel || '')).width), 0) + DROPDOWN_LAYOUT.optionChrome : 0;
    const width = dropdownWidth({ triggerWidth: r.width, contentWidth, minWidth: menuFloor, viewportWidth: window.innerWidth });
    const left = dropdownLeft({ triggerLeft: r.left, width, viewportWidth: window.innerWidth });
    setPos({ left, top: drop === 'down' ? r.bottom + 4 : r.top - 4, width, drop, maxH });
  }, [menuFloor, opts]);

  const closeMenu = useCallback(() => { setOpen(false); setHi(-1); setQuery(''); }, []);
  useEffect(() => {
    if (!open) return undefined;
    const closeAndFocus = () => { closeMenu(); btnRef.current?.focus(); };
    return registerKeyboardLayer({
      element: () => menuRef.current, zIndex: 9750,
      onEscape: closeAndFocus,
      onTab: () => { closeAndFocus(); return btnRef.current; },
    });
  }, [open, closeMenu]);
  const openMenu = useCallback(() => {
    if (disabled) return;
    place();
    setHi(curIdx >= 0 ? curIdx : opts.findIndex((o) => !o.disabled));
    setOpen(true);
  }, [disabled, place, curIdx, opts]);

  const pick = useCallback((o) => {
    if (!o || o.disabled) return;
    closeMenu();
    btnRef.current?.focus();
    if (String(o.value) !== String(value ?? '')) onChange && onChange({ target: { value: o.value }, preventDefault() {}, stopPropagation() {} });
  }, [onChange, value, closeMenu]);

  useEffect(() => {
    if (open && effectiveSearchable && searchRef.current) searchRef.current.focus();
  }, [open, effectiveSearchable]);

  // Reposition on scroll/resize; close on outside pointer-down.
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => place();
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      closeMenu();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open, place, closeMenu]);

  // Keep the highlighted row in view during keyboard nav.
  useEffect(() => {
    if (!open || hi < 0 || !menuRef.current) return;
    const row = menuRef.current.querySelector(`[data-oi="${hi}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [open, hi]);

  const moveHighlight = (dir) => {
    const enabled = visibleOpts.filter((o) => !o.disabled);
    if (!enabled.length) { setHi(-1); return; }
    const at = enabled.findIndex((o) => o.index === hi);
    const next = at < 0 ? (dir > 0 ? 0 : enabled.length - 1) : (at + dir + enabled.length) % enabled.length;
    setHi(enabled[next].index);
  };

  const updateQuery = (next) => {
    setQuery(next);
    const norm = next.trim().toLocaleLowerCase('nl-NL');
    const first = opts.findIndex((o) => !o.disabled && (!norm || matchesSearchText(next, o.searchLabel)));
    setHi(first);
  };

  const onSearchKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeMenu(); btnRef.current?.focus(); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hi >= 0) pick(opts[hi]); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(e.key === 'ArrowDown' ? 1 : -1); }
    else if (e.key === 'Home') { e.preventDefault(); const first = visibleOpts.find((o) => !o.disabled); setHi(first ? first.index : -1); }
    else if (e.key === 'End') { e.preventDefault(); const enabled = visibleOpts.filter((o) => !o.disabled); setHi(enabled.length ? enabled[enabled.length - 1].index : -1); }
  };

  const onKey = (e) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); openMenu(); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); closeMenu(); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (hi >= 0) pick(opts[hi]); }
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Home') { e.preventDefault(); const first = visibleOpts.find((o) => !o.disabled); setHi(first ? first.index : -1); }
    else if (e.key === 'End') { e.preventDefault(); const enabled = visibleOpts.filter((o) => !o.disabled); setHi(enabled.length ? enabled[enabled.length - 1].index : -1); }
  };

  // Apply the caller's style verbatim (width/height/border/font all match the old
  // select), then force the interactive box model and reserve chevron room.
  const triggerStyle = {
    boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', gap: 6, textAlign: 'left',
    whiteSpace: 'nowrap', overflow: 'hidden', appearance: 'none', WebkitAppearance: 'none',
    fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, ...style,
  };

  return (
    <>
      <button type="button" ref={btnRef} title={title} disabled={disabled} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => (open ? closeMenu() : openMenu())} onKeyDown={onKey} style={triggerStyle} {...rest}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isPlaceholder ? HV2.muted : 'inherit' }}>{display}</span>
        {endIcon || <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto', opacity: 0.5 }}><polyline points="6 9 12 15 18 9" /></svg>}
      </button>
      {open && pos && typeof document !== 'undefined' && createPortal(
        <div ref={menuRef} role="listbox" onMouseDown={(e) => e.stopPropagation()} style={{
          position: 'fixed', left: pos.left, width: pos.width,
          ...(pos.drop === 'down' ? { top: pos.top } : { bottom: window.innerHeight - pos.top }),
          // 9750: above the hover cards (9700) and the call windows (9500, 9600). A
          // click-opened list is the newest intent on screen and wins over a hover.
          maxHeight: pos.maxH, overflowY: 'auto', zIndex: 9750, ...DROPDOWN_STYLE.surface,
          padding: 4, fontFamily: 'var(--font-geist), -apple-system, system-ui, sans-serif',
        }}>
          {effectiveSearchable && (
            <div style={{ position: 'sticky', top: -4, zIndex: 1, padding: '4px 4px 6px', background: HV2.bg, borderBottom: `1px solid ${HV2.divider}` }}>
              <input ref={searchRef} type="search" value={query} onChange={(e) => updateQuery(e.target.value)} onKeyDown={onSearchKey}
                placeholder={searchPlaceholder || t('Zoek markt…')} aria-label={searchPlaceholder || t('Zoek markt…')}
                style={{ width: '100%', height: 30, boxSizing: 'border-box', padding: '0 9px', border: `1px solid ${HV2.borderIn}`, borderRadius: 5, background: HV2.bg, color: HV2.ink, fontFamily: 'inherit', fontSize: HV2.fsText, outline: 'none' }} />
            </div>
          )}
          {visibleOpts.map((o, visibleIndex) => {
            const i = o.index;
            const sel = String(o.value) === String(value ?? '');
            const showGroup = o.group && (visibleIndex === 0 || visibleOpts[visibleIndex - 1].group !== o.group);
            return (
              <React.Fragment key={i}>
                {showGroup && <div style={{ padding: '6px 10px 3px', fontSize: HV2.fsText, fontWeight: 650, color: HV2.muted }}>{o.group}</div>}
                <div data-oi={i} role="option" aria-selected={sel}
                  onMouseEnter={() => !o.disabled && setHi(i)} onClick={() => pick(o)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 6,
                    cursor: o.disabled ? 'not-allowed' : 'pointer', opacity: o.disabled ? 0.4 : 1,
                    background: i === hi && !o.disabled ? HV2.subtle : 'transparent',
                    fontSize: HV2.fsText, color: HV2.ink, fontWeight: sel ? 560 : 400,
                  }}>
                  <span style={{ flex: '0 0 auto', width: 14, display: 'inline-flex', justifyContent: 'center', color: HV2.blue }}>
                    {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  </span>
                  <span style={DROPDOWN_STYLE.optionLabel}>{React.isValidElement(o.label) && o.label.type === MarketOptionLabel
                    ? React.cloneElement(o.label, { wrap: true }) : o.label}</span>
                </div>
              </React.Fragment>
            );
          })}
          {!visibleOpts.length && <div style={{ padding: '8px 10px', fontSize: HV2.fsText, color: HV2.muted }}>{t('Niets gevonden.')}</div>}
        </div>, document.body)}
    </>
  );
}

export function TimeSelect24({ value, onChange, style, endIcon }) {
  const opts = value && !TIME_OPTS_24.includes(value) ? [value, ...TIME_OPTS_24] : TIME_OPTS_24;
  return (
    <Select value={value || ''} onChange={(e) => onChange(e.target.value)} style={style} endIcon={endIcon}>
      <option value="">--:--</option>
      {opts.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
    </Select>
  );
}
// Segmented control - the ONE toggle pattern (Fase/Bord, Mij/Team, category
// filters). opts: [{ k, label, node? }]; value/onChange controlled.
// Active segment is a raised white chip on a subtle grey track; never the kobalt
// primary-button fill and never a full-black fill (those read as actions).
// `dense` maakt de totale hoogte 30 (22 + 2x3 rand + 2x1 border), gelijk aan de
// md-knop en de filtertriggers, voor rijen waar de toggle NAAST knoppen staat en
// het hoogteverschil anders scheef oogt (bellijstkop, eigenaarsverzoek 11 aug).
// `colors` (optioneel) hertint het spoor voor een donker vlak (Targets-terminal):
// { track, border, chip, ink, muted }; ontbrekende sleutels vallen terug op HV2.
// `fill` rekt het spoor over de hele breedte en verdeelt de segmenten gelijk
// (formuliervelden zoals Ja/Nee). Per optie mag `disabled`, `title` en `ariaLabel`
// mee (een segment met alleen een glyph als node).
// Opnieuw klikken op het actieve segment roept onChange gewoon aan; wie een
// leeg-toestand wil, vertaalt dat zelf (value === k ? '' : k).
export function SegControl({ value, onChange, opts, dense = false, colors = null, fill = false, style: outer = null }) {
  const c = { track: HV2.subtle, border: HV2.border, chip: HV2.bg, ink: HV2.ink, muted: HV2.muted, ...(colors || {}) };
  return (
    <div style={{ display: fill ? 'flex' : 'inline-flex', gap: 2, background: c.track, border: `1px solid ${c.border}`, borderRadius: 7, padding: 3, ...(outer || {}) }}>
      {opts.map((o) => {
        const on = value === o.k;
        const style = { display: 'inline-flex', alignItems: 'center', justifyContent: fill ? 'center' : undefined, flex: fill ? 1 : undefined, gap: 6, height: dense ? 22 : 26, padding: dense ? '0 10px' : '0 14px', borderRadius: 4, border: 'none', background: on ? c.chip : 'transparent', color: on ? c.ink : c.muted, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: on ? 560 : 480, cursor: o.disabled ? 'not-allowed' : 'pointer', opacity: o.disabled ? 0.5 : 1, whiteSpace: 'nowrap', boxShadow: on ? `0 1px 2px rgba(0,0,0,0.10), 0 0 0 1px ${c.border}` : 'none', textDecoration: 'none' };
        return o.href
          ? <a key={o.k} {...hashNav(o.href, () => onChange(o.k))} aria-current={on ? 'page' : undefined} title={o.title} style={style}>{o.node || o.label}</a>
          : <button key={o.k} type="button" disabled={!!o.disabled} onClick={() => onChange(o.k)} aria-pressed={on} aria-label={o.ariaLabel} title={o.title} style={style}>{o.node || o.label}</button>;
      })}
    </div>
  );
}

// activeRowStyle - DE geselecteerd-staat van een regel in een lijst, rail of
// menu (designregel 36, eigenaarsregel 17 september 2026). De staat is een
// vulling plus gewicht, nooit een balk aan de linkerrand en nooit een zwarte
// vulling (regel 37). Staat hier een keer zodat vijftien schermen niet vijftien
// keer hun eigen selectiekleur munten; wie een toggle bouwt neemt SegControl,
// wie een rij markeert neemt deze stijl.
export const activeRowStyle = { background: HV2.blueBg, color: HV2.ink, fontWeight: 560 };

// ── Universal label primitives (companion to the Button set) ─────────────────
// One source of truth for every small rounded label: tags, badges, dots, chips.
// All read HV2 at render so they recolour with the theme. Labels are static /
// read-only - interactivity (hover/active) belongs to the Button set, not here.

// TrackTag - THE canonical SRC / INT / BD marker (the "track tag" design from the
// Processen top toggle bar). A hairline-outlined, bold 3-letter code chip that
// inherits the surrounding text colour (currentColor): monochrome, subtle, and
// identical everywhere. Use this for ANY INT/BD/SRC label across the app - do not
// hand-roll coloured pills. Pass `track` ('sourcing'|'placement'|'bd') or an
// explicit `text`. See DESIGN-SYSTEM.md (Track Tag).
// CVS (CV Send) is de VIERDE code, geen uitzondering: zelfde recept, zelfde
// currentColor-erving. De code bestond al als bellijst-type (CLKind), dus het
// vocabulaire lag er; alleen de tag ontbrak nog op de CV-send-rijen.
export function TrackTag({ track, text, title }) {
  const code = text || (track === 'sourcing' ? 'CVS' : track === 'bd' ? 'BD' : (track === 'cvsend' || track === 'cv_send') ? 'CVS' : 'INT');
  return <span title={title !== undefined ? title : (SYM_TITLES[code] || undefined)} style={{ display: 'inline-block', border: '1px solid currentColor', borderRadius: 3, padding: '0 5px', fontSize: HV2.fsBadge, fontWeight: 650, lineHeight: '14px', whiteSpace: 'nowrap', flexShrink: 0 }}>{code}</span>;
}
// TrackBadge: kept for back-compat; renders the canonical TrackTag.
export function TrackBadge({ track }) { return <TrackTag track={track} />; }

// ClientTypeTag - marks whether a company is a signed Client, a signed
// Contracting client, a Contracting prospect or a plain Prospect. A signed client
// is the stronger signal (a lead/vacancy there = "call your own client about it"),
// so both signed kinds are a solid chip; both prospect kinds stay a quiet hairline
// outline, the contracting one with a roze merkteken rechts. Pass type
// ('client'|'contracting'|'prospect_contracting'|anything else).
export function ClientTypeTag({ type }) {
  const base = { display: 'inline-block', borderRadius: 3, padding: '0 5px', fontSize: HV2.fsBadge, fontWeight: 650, lineHeight: '14px', whiteSpace: 'nowrap', flexShrink: 0 };
  // Beide getekende soorten zijn massief: een getekende klant is het sterkere
  // signaal. Contracting draagt de petrolgrond uit RECORD_TYPE_META, zodat de
  // tag en de typeband van hetzelfde bedrijf dezelfde kleur spreken.
  if (type === 'contracting') return <span style={{ ...base, background: RECORD_TYPE_META.contracting.color, color: RECORD_TYPE_META.contracting.bandText }}>{t('Klant contracting')}</span>;
  // Klant draagt het groen van zijn typeband (20 sep 2026): geen zwarte vulling (Regel 37).
  if (type === 'client') return <span style={{ ...base, background: RECORD_TYPE_META.client.color, color: RECORD_TYPE_META.client.bandText }}>{t('Klant')}</span>;
  // Prospect contracting is niet getekend, dus dezelfde haarlijn als een prospect;
  // alleen het merkteken rechts draagt het roze van contracting.
  if (type === 'prospect_contracting') return <span style={{ ...base, border: `1px solid ${HV2.border}`, borderRight: `3px solid ${HV2.pink}`, color: HV2.muted }}>{t('Prospect contracting')}</span>;
  return <span style={{ ...base, border: `1px solid ${HV2.border}`, borderRight: '3px solid #5b21b6', color: HV2.muted }}>{t('Prospect')}</span>;
}

// activeUsers - de collega's die nog te kiezen zijn: niet gearchiveerd (alumni,
// uit dienst) en niet geblokkeerd. Bestaat omdat elke picker zijn eigen filter
// schreef en er zes helemaal geen filter hadden, zodat een vertrokken collega daar
// gewoon toewijsbaar bleef (opgeruimd 7 augustus 2026). Verdraagt beide vormen
// waarin een gebruiker binnenkomt: /api/bootstrap levert `suspended` als boolean,
// /api/users geeft de kale kolom `suspended_at` door.
//
// LET OP: dit is de TOEWIJZING-pool, niet de historie. Weergaven die het verleden
// tonen (Monitor, het Logboek, All-Time) moeten oud-collega's blijven laten zien en
// gaan hier bewust NIET doorheen.
export function activeUsers(users) {
  return (users || []).filter((u) => u && !u.alumni && !u.suspended && !u.suspended_at);
}

// UserScopeSelect - director-only dropdown to narrow a view (Processen, Vandaag) to
// one specific user's work; '' = the whole team.
export function UserScopeSelect({ users = [], value, onChange }) {
  const list = activeUsers(users).filter((u) => u.name).slice().sort((a, b) => String(a.name).localeCompare(String(b.name)));
  return (
    <DirectorHoverHint>
      <Select value={value} onChange={(e) => onChange(e.target.value)} title={t('Toon processen van een specifieke gebruiker')}
        style={{ height: 34, border: `1px solid ${value ? HV2.ink : HV2.border}`, borderRadius: 7, background: HV2.bg, color: HV2.ink, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 560, padding: '0 8px', outline: 'none' }}>
        <option value="">{t('Hele team')}</option>
        {list.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
      </Select>
    </DirectorHoverHint>
  );
}

// MarketOptionLabel - DE regel voor een markt in een keuzelijst: tegel + naam.
// Elke markt-dropdown gebruikt deze, zodat de gesloten knop en de open lijst
// hetzelfde icoon tonen als de rest van de app (eigenaarsregel 13 aug 2026: het
// markticoon hoort in elke dropdown te staan, ook in 'Markt toevoegen').
//
// Waarom hier en niet in MarketIcon.js: dat bestand leest HV2 uit lib.js, dus een
// import terug zou een cirkel geven. Net als RecordBadges roept dit de pure
// SVG-functie (marketIcon) rechtstreeks aan. De SVG gaat als data-URI in een
// <img> en NOOIT als DOM-HTML: marktnamen komen uit de database.
//
// Werkt in <Select> omdat die geen echte <option> rendert: flattenOptions neemt
// de children van een <option> als label en tekent ze in zijn eigen portal-lijst,
// dus JSX (icoon + tekst) komt gewoon door. In een NATIVE <select> kan dat niet -
// een markt-dropdown moet daarom altijd <Select> zijn.
export function MarketOptionLabel({ market, size = 16, label, style, wrap = false }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0, ...style }}>
      <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(marketIcon(market, { size }))}`}
        alt="" width={size} height={size} style={{ display: 'block', flex: '0 0 auto' }} />
      <span style={wrap ? DROPDOWN_STYLE.optionLabel : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label ?? t(market)}</span>
    </span>
  );
}

// marketOptions - de <option>-regels voor een markt-dropdown, mét icoon. Eén
// aanroep zodat geen enkel oppervlak zijn eigen icoonloze map overhoudt.
// Altijd alfabetisch (eigenaarsbesluit 13 september 2026): de registerlijst komt
// binnen in seed-volgorde (sort_order), en op de recordpagina's van kandidaat,
// bedrijf en vacature moet de keuzelijst gewoon van A naar Z lopen.
export function marketOptions(list, { size = 16 } = {}) {
  return (list || []).filter(Boolean).slice().sort((a, b) => String(a).localeCompare(String(b), 'nl')).map((mk) => (
    <option key={mk} value={mk} data-market-option="" data-search-label={mk}><MarketOptionLabel market={mk} size={size} /></option>
  ));
}

// MarketScopeSelect - narrow a deal-derived view (KPI-pijplijn, Agenda) to one markt;
// '' = alle markten. Zelfde Select-vorm als UserScopeSelect, maar voor iedereen (geen
// director-gate): een consultant mag z'n eigen markt-slice zien, net als de Mij/Team-
// toggle. De markt wordt afgeleid via deal.market (kandidaat/bedrijf-skills).
export function MarketScopeSelect({ markets = [], value, onChange, title, style }) {
  const list = (markets || []).filter(Boolean).slice().sort((a, b) => String(a).localeCompare(String(b)));
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} title={title || t('Filter op markt')}
      style={{ height: 34, border: `1px solid ${value ? HV2.ink : HV2.border}`, borderRadius: 7, background: HV2.bg, color: HV2.ink, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 560, padding: '0 8px', outline: 'none', ...style }}>
      <option value="">{t('Alle markten')}</option>
      {marketOptions(list)}
    </Select>
  );
}

// AlertBadge: required-tasks / unread count. Red solid, 99+ overflow.
export function AlertBadge({ n, max = 99 }) {
  return <span style={{ fontSize: HV2.fsBadge, fontWeight: 650, color: '#fff', background: HV2.red, borderRadius: 4, padding: '1px 7px', minWidth: 18, textAlign: 'center', display: 'inline-block', flexShrink: 0 }}>{n > max ? max + '+' : n}</span>;
}

// Dot: bare coloured signal. 6px (situational) 7–8px (status/urgency/KPI).
export function Dot({ color = HV2.red, size = 7 }) {
  return <span style={{ width: size, height: size, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
}

// Missing-data indicators for placement processen: which forecast/sign inputs are
// still empty. Shared labels so Vandaag, Fase, Bord, de grid en het proces-detail
// spreken dezelfde taal (endpoint: /api/deals/missing-data).
export const MISS_LABEL = { terms: 'Klant-terms', salary: 'Huidig salaris', expected: 'Gewenst salaris' };
// Small NEUTRAL alert glyph (circle-with-!). No amber: inherits currentColor so it
// reads as a muted signal, never as a warning hue.
export function AlertDot({ size = 11 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
// Inline icon-only mark for list surfaces (Vandaag rows, Fase-lane, Bord-kaart):
// the neutral AlertDot with a title tooltip naming what's missing. `missing` is
// the ['terms','salary'] array (or {missing:[...], fee, feeSource} object) from
// missingByDeal; renders nothing when empty.
export function MissingDataMark({ missing: raw, size = 13, style }) {
  const missing = Array.isArray(raw) ? raw : (raw?.missing || []);
  const issues = !Array.isArray(raw) && raw?.workflowIssues;
  if (!missing.length && !issues?.length) return null;
  const names = issues?.length ? issues.map((i) => i.label).join(', ') : missing.map((k) => t(MISS_LABEL[k] || k)).join(' + ');
  return (
    <span tabIndex={0} role="img" aria-label={names} title={t('Ontbreekt: {x}', { x: names })} style={{ display: 'inline-flex', alignItems: 'center', color: HV2.muted, flexShrink: 0, ...style }}>
      <AlertDot size={size} />
    </span>
  );
}

// Sep: the inline metadata divider - a thin vertical hairline between two pieces
// of text (replaces the old floating middot "A | B"). One shared element so the
// divider is one edit, not a literal glyph scattered everywhere. Reads HV2 at
// render so it recolours with the theme; carries its own horizontal margin, so
// drop it straight between the two pieces (no surrounding spaces needed). Only
// works where the divider is rendered as JSX - string separators (joins, t()
// keys, tooltips) can't hold an element and stay a plain space.
export function Sep({ flush = false }) {
  // `flush` drops the horizontal margin for flex rows that already space their
  // items with `gap` (else the gap and the margin stack into too much air).
  return <span aria-hidden="true" style={{ display: 'inline-block', width: 1, height: 11, background: HV2.borderStrong, margin: flush ? 0 : '0 8px', verticalAlign: -1, flexShrink: 0 }} />;
}

// UrgencyDot/Label: opvolgdatum urgency. red=verlopen ink=vandaag hollow=deze week.
// Pairs with urgencyDot(deal.nextDate) -> { dot: 'red'|'ink'|'hollow', label }.
export function UrgencyDot({ kind }) {
  const st = kind === 'red' ? { background: HV2.red } : kind === 'ink' ? { background: HV2.ink } : { background: 'transparent', border: `1.5px solid ${HV2.ink3}` };
  return <span style={{ width: 7, height: 7, borderRadius: '50%', boxSizing: 'border-box', display: 'inline-block', flexShrink: 0, ...st }} />;
}
export function UrgencyLabel({ kind, label }) {
  // Text is ink; the urgency colour is carried by the dot, never the words.
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: HV2.fsText, fontWeight: kind === 'red' ? 650 : 560, color: HV2.ink }}>
      <UrgencyDot kind={kind} /><span style={{ whiteSpace: 'nowrap' }}>{label}</span>
    </span>
  );
}

// Reschedule: a deal pushed N× (struggling at >= 2). Orange tint Pill.
export function Reschedule({ n }) {
  return <TonePill tone="orange">{n}×</TonePill>;
}

// TonePill: het canonieke semantische status-label. Een vorm voor elke inline
// statuspill (Verlopen, Achterstand, Gemiste stap, reschedule N×, stage badges,
// …), met dezelfde geometrie als EntityChip. Leest HV2 bij render, zodat hij
// meekleurt met het thema.
// TONE_COLORS hieronder is de EXPORT voor niet-pill-gebruik: icoonstreken en
// laan-dots. De vullingen van de pill zelf staan in de map in TonePill; die twee
// liepen bewust uiteen toen de informatieve tonen naar de lichte helft zakten.
export const TONE_COLORS = { red: '#dc2626', amber: '#ea580c', orange: '#ea580c', green: '#16a34a', blue: '#7c3aed', accent: '#7c3aed', slate: '#475569', neutral: '#6b6b73' };
// Vulling + tekstkleur per toon (eigenaarsbesluit 26 augustus 2026). De drie
// tonen die urgentie dragen (rood, oranje, groen) houden hun gewicht; de twee
// informatieve tonen zakken naar de LICHTE helft van het goedgekeurde register,
// inkt op een gedempte vulling, precies zoals de gouden en zeegroene typeband:
//   - violet #7c3aed is geschrapt: een felle frameworkkleur, van geen token
//   - zwart HV2.muted is geschrapt: 'Gearchiveerd' was daarmee het ZWAARSTE
//     object in de rij, zwaarder dan rood, terwijl het juist moet wegvallen
// TONE_COLORS zelf blijft ongemoeid: die waarden voeden ook icoonstreken en
// laan-dots (CrmDatabase gebruikt TONE_COLORS.slate als stroke), en een lichte
// vulling zou daar onzichtbaar zijn.
const TONE_INFO = '#9db4d0';   // informatief (Afwezig, INT proces, Mailshot aan)
// Geëxporteerd sinds 17 september 2026: RecordGrid's StatusPill draagt dezelfde
// sluimertoon als TonePill neutral en mag de hex niet overschrijven.
export const TONE_QUIET = '#d9d8d4';  // sluimerend (Gearchiveerd, Niet geverifieerd)
export function TonePill({ tone = 'red', children }) {
  const map = {
    red: { bg: HV2.red, fg: '#ffffff' },
    amber: { bg: HV2.orange, fg: '#ffffff' },
    orange: { bg: HV2.orange, fg: '#ffffff' },
    green: { bg: HV2.green, fg: '#ffffff' },
    blue: { bg: TONE_INFO, fg: HV2.ink2 },
    accent: { bg: TONE_INFO, fg: HV2.ink2 },
    slate: { bg: TONE_INFO, fg: HV2.ink2 },
    neutral: { bg: TONE_QUIET, fg: HV2.ink2 },
  };
  const c = map[tone] || map.red;
  // Radius 4 en dezelfde geometrie als EntityChip (fsText/700, regelhoogte 18,
  // padding 2px 10px): een statuspill en een typechip staan naast elkaar in
  // dezelfde rij en mogen niet in maat verschillen. 'Nooit vol rond'.
  return <span style={{ fontSize: HV2.fsText, fontWeight: 650, color: c.fg, background: c.bg, borderRadius: 4, padding: '2px 10px', lineHeight: '18px', whiteSpace: 'nowrap', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{children}</span>;
}

// ── Partner (retained client) ────────────────────────────────────────────────
// A client who bought a retained search becomes a Partner. The set of partner
// company slugs is computed server-side (bootstrap) and set once here - read via
// isPartner(slug) anywhere a company renders, no prop-drilling (mirrors phases).
let _partnerSlugs = new Set();
export function setPartnerSlugs(slugs) { _partnerSlugs = new Set(slugs || []); }
export function isPartner(slug) { return !!slug && _partnerSlugs.has(slug); }

// ── Company website map + CompanyLogo ────────────────────────────────────────
// companySlug -> hostname, computed server-side (bootstrap + /api/deals) and set
// once here - read via companyWebsiteFor(slug) anywhere a company renders, no
// prop-drilling (mirrors partnerSlugs/phases). Merged on set so a partial poll
// payload never wipes earlier entries.
let _companyWebsites = {};
// Accepteert ook een updater (prev => next), zoals de deals-poll hem aanroept;
// voorheen was die aanroep stilletjes een no-op (typeof function !== 'object').
export function setCompanyWebsites(mapOrUpdater) {
  const map = typeof mapOrUpdater === 'function' ? mapOrUpdater(_companyWebsites) : mapOrUpdater;
  if (map && typeof map === 'object') _companyWebsites = { ..._companyWebsites, ...map };
}
export function companyWebsiteFor(slug) { return (slug && _companyWebsites[String(slug)]) || ''; }

const COMPANY_LOGO_UPDATED_EVENT = 'pb-company-logo-updated';
export function notifyCompanyLogoUpdated(slug, version = Date.now()) {
  if (typeof window === 'undefined' || !slug) return;
  // De verse upload moet meteen zichtbaar zijn, dus de onthouden eindstap van
  // deze slug vervalt: anders begint CompanyLogo bij de favicon of de tegel.
  _logoSettled.delete(logoKey(slug));
  persistLogoSettled();
  window.dispatchEvent(new CustomEvent(COMPANY_LOGO_UPDATED_EVENT, { detail: { slug: String(slug), version } }));
}

// Negatieve logocache, module-breed en alleen in het geheugen van deze pagina.
// De server beantwoordt een ontbrekend logo met 204 + 'private, max-age=300'
// (lib/company-logo.mjs), dus zonder deze cache vraagt elk bedrijf zonder logo
// het elke vijf minuten opnieuw, bij elke montage van elk grid opnieuw. We
// onthouden per slug (stap 0) en per domein (stap 1) de stap waarop het logo
// uiteindelijk bleef staan, zodat een volgende montage direct daar begint en de
// requests overslaat die al een 204 gaven. Alleen een uitgeputte poging landt
// hier, dus een tijdelijke 500 zet niets vast.
//
// Sinds 14 okt 2026 (kostenronde) ook in localStorage, zeven dagen: een ontbrekend
// logo kostte anders elke nieuwe pagina opnieuw een aanroep per bedrijf. Een
// upload wist de eigen sleutel (notifyCompanyLogoUpdated); bij een ander tabblad
// of een andere gebruiker verschijnt een nieuw logo pas na die zeven dagen of na
// het legen van de opslag. Een img-fout zegt niet of het een 204 of een 500 was;
// de ene herkansing hieronder vangt de tijdelijke 500.
const LOGO_SETTLED_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LOGO_SETTLED_MAX = 2000;
const LOGO_SETTLED_STORE = 'pb-logo-miss-v1';
const _logoSettled = new Map(); // 's:<slug>' | 'd:<domein>' -> { stage, at }
const logoKey = (slug) => `s:${slug}`;
const faviconKey = (domain) => `d:${domain}`;

let _logoStoreLoaded = false;
function loadLogoSettled() {
  if (_logoStoreLoaded || typeof window === 'undefined') return;
  _logoStoreLoaded = true;
  try {
    const raw = window.localStorage.getItem(LOGO_SETTLED_STORE);
    const saved = raw ? JSON.parse(raw) : null;
    if (!saved || typeof saved !== 'object') return;
    const now = Date.now();
    for (const [key, v] of Object.entries(saved)) {
      if (!v || typeof v.stage !== 'number' || typeof v.at !== 'number') continue;
      if (now - v.at > LOGO_SETTLED_TTL_MS) continue;
      if (!_logoSettled.has(key)) _logoSettled.set(key, { stage: v.stage, at: v.at });
    }
  } catch { /* geen opslag (privevenster, quotum): alleen het geheugen */ }
}

// Een schrijfronde per seconde: een grid vol ontbrekende logo's geeft anders een
// write per kaart.
let _logoStoreTimer = null;
function persistLogoSettled() {
  if (typeof window === 'undefined' || _logoStoreTimer) return;
  _logoStoreTimer = setTimeout(() => {
    _logoStoreTimer = null;
    try { window.localStorage.setItem(LOGO_SETTLED_STORE, JSON.stringify(Object.fromEntries(_logoSettled))); } catch { /* idem */ }
  }, 1000);
}

function logoSettledStage(key) {
  loadLogoSettled();
  const hit = _logoSettled.get(key);
  if (!hit) return null;
  if (Date.now() - hit.at > LOGO_SETTLED_TTL_MS) { _logoSettled.delete(key); return null; }
  return hit.stage;
}

function rememberLogoStage(key, stage) {
  loadLogoSettled();
  _logoSettled.set(key, { stage, at: Date.now() });
  if (_logoSettled.size > LOGO_SETTLED_MAX) {
    const oldest = _logoSettled.keys().next().value;
    _logoSettled.delete(oldest);
  }
  persistLogoSettled();
}

// Beginstap: de hoogste stap die voor deze slug én dit domein al vaststaat.
// Zonder treffer gewoon 0, precies het oude gedrag.
function initialLogoStage(slug, domain) {
  let stage = 0;
  if (slug) {
    const bySlug = logoSettledStage(logoKey(slug));
    if (bySlug != null) stage = bySlug;
  }
  if (stage <= 1 && domain) {
    const byDomain = logoSettledStage(faviconKey(domain));
    if (byDomain != null && byDomain > stage) stage = byDomain;
  }
  return stage;
}

// CompanyLogo - klein bedrijfslogo (16x16) voor de proces-cards. Bronvolgorde:
// 1. self-hosted logo (/api/company-logo: Brandfetch-cache + director-uploads,
//    dezelfde bron als CompanyAvatar in de detailvensters; 404 = geen logo),
// 2. favicon van de bedrijfswebsite via Google's favicon-service,
// 3. neutraal initiaal-tegeltje - zodat de gutter-uitlijning overal identiek
//    blijft (ook op pagina's zonder slug of website-map).
// Eén herkansing per slug per paginaleven: een tijdelijke 500/timeout op een koude
// instantie maakte het logo anders permanent een initiaal-tegel tot de refresh.
// Module-breed, zodat twintig kaarten van dezelfde klant niet twintig keer
// opnieuw vragen (en een terechte 204 hooguit één extra request kost).
const _logoRetried = new Set();
export function CompanyLogo({ slug, domain, name, size = 16 }) {
  const [stage, setStage] = useState(() => initialLogoStage(slug, domain)); // 0 self-hosted, 1 favicon, 2 initiaal
  const [revision, setRevision] = useState(2);
  // 'Geladen' is per URL, niet een losse boolean: een nieuwe src is vanzelf
  // 'nog niet geladen', en een prop-wissel zonder src-wissel (domain dat pas na
  // bootstrap binnenkomt) verbergt een al geladen plaatje niet meer. De oude
  // setLoaded(false) in het effect zette opacity 0 op een img die nooit meer een
  // load-event geeft: logo pas zichtbaar na refresh (eigenaarsbug 16 aug 2026).
  const [loadedSrc, setLoadedSrc] = useState(null);
  const imgRef = useRef(null);
  const retryTimer = useRef(null);
  useEffect(() => {
    setStage(initialLogoStage(slug, domain)); setRevision(2);
    const updated = (event) => {
      if (String(event.detail?.slug || '') !== String(slug || '')) return;
      setStage(0); setRevision(event.detail?.version || Date.now());
    };
    window.addEventListener(COMPANY_LOGO_UPDATED_EVENT, updated);
    return () => { window.removeEventListener(COMPANY_LOGO_UPDATED_EVENT, updated); if (retryTimer.current) clearTimeout(retryTimer.current); };
  }, [slug, domain]);
  // Stap 1 gaat via onze eigen favicon-proxy (/api/favicon): die detecteert
  // Google's '?'-placeholder (voor onbekende domeinen een 200!) en geeft dan
  // 404, zodat onError netjes doorvalt naar de initiaal-tegel i.p.v. een
  // kapot-ogende '?'-tegel te tonen (eigenaarsmelding 2026-07-10).
  // v=4 (9 okt 2026): bust van de 24u-browsercache vol Buffer-JSON uit de corrupte
  // favicon_cache (lib/favicon-bytes.mjs). v=3 was de bust van de 204's uit het
  // kalibratie-gat van 21 jul (Google s2 ging 404 geven voor onbekende domeinen,
  // waardoor de proxy fail-closed elke favicon 204'de). Eerdere busts: v=2 na het
  // '?'-placeholder-gat (20 jul). company-logo blijft v=2 (Brandfetch-datafix 21 jul).
  const src = (stage === 0 && slug) ? `/api/company-logo?slug=${encodeURIComponent(slug)}&v=${revision}`
    : ((stage <= 1 && domain) ? `/api/favicon?domain=${encodeURIComponent(domain)}&v=4` : null);
  const loaded = !!src && loadedSrc === src;
  // Cache-hit: het load-event kan vuren vóór React de handler koppelt (herbruikt
  // img-element, plaatje in geheugencache). Na de commit even nakijken.
  useEffect(() => {
    const el = imgRef.current;
    if (src && el && el.complete && el.naturalWidth > 0) setLoadedSrc(src);
  }, [src]);
  const onError = () => {
    // Stap 0 is alleen echt het self-hosted logo als er een slug is; zonder slug
    // toont stap 0 al de favicon en is dit dus een faviconfout.
    const fromSelfHosted = stage === 0 && !!slug;
    if (fromSelfHosted && !_logoRetried.has(slug)) {
      _logoRetried.add(slug);
      retryTimer.current = setTimeout(() => setRevision(Date.now()), 750);
      return;
    }
    const next = (stage === 0 && domain) ? 1 : 2;
    if (fromSelfHosted) rememberLogoStage(logoKey(slug), next);
    else if (domain) {
      // De favicon van dit domein bestaat niet: onthoud dat per domein, want
      // twintig bedrijven kunnen op hetzelfde domein zitten.
      rememberLogoStage(faviconKey(domain), 2);
      if (slug) rememberLogoStage(logoKey(slug), 2);
    }
    setStage(next);
  };
  return (
    <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size, borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
      {/* Basislaag alleen zolang de img niet geladen is: logo-PNG's zijn vaak
          transparant, een blijvende tegel+letter schijnt er doorheen. */}
      {!loaded && <span aria-hidden="true" style={{
        width: size, height: size, borderRadius: 3, background: HV2.bgSub,
        border: `1px solid ${HV2.border}`, boxSizing: 'border-box', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: HV2.fsBadge, fontWeight: 560, lineHeight: 1, color: HV2.muted, userSelect: 'none',
      }}>{(String(name || '').trim().charAt(0) || '?').toUpperCase()}</span>}
      {src && (
        <img ref={imgRef} src={src} alt="" width={size} height={size}
          onLoad={() => setLoadedSrc(src)}
          onError={onError}
          style={{ display: 'block', position: 'absolute', top: 0, left: 0, width: size, height: size, borderRadius: 3, flexShrink: 0, objectFit: 'contain', opacity: loaded ? 1 : 0 }} />
      )}
    </span>
  );
}

// PartnerPill: brand kobalt with the white P& mark + "Partner". The one deliberate
// kobalt-on-a-pill exception - it's literally the brand mark, on a logo'd badge.
// Supersedes the green CLIENT pill for partner clients.
export function PartnerPill() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 18, padding: '0 8px 0 6px', borderRadius: 999, background: '#0047ab', color: '#fff', fontSize: HV2.fsBadge, fontWeight: 650, whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1 }}>
      <span role="img" aria-label="Pearson & Partners" style={{ display: 'inline-block', width: pmarkWidth(10), height: 10, backgroundColor: '#fff', WebkitMask: 'url(/logo-pmark-white.png) center/contain no-repeat', mask: 'url(/logo-pmark-white.png) center/contain no-repeat', flexShrink: 0 }} />
      Partner
    </span>
  );
}

// DirectorPill: the kobalt [P& Director] pill VISUAL only (no tooltip wrapper).
// Used inside DirectorHoverHint's tooltip and by the legacy DirectorChip export.
// Same brand-mark treatment as PartnerPill (the one deliberate kobalt-on-a-pill
// exception).
function DirectorPill({ style } = {}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 18, padding: '0 8px 0 6px', borderRadius: 999, background: '#0047ab', color: '#fff', fontSize: HV2.fsBadge, fontWeight: 650, whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1, verticalAlign: 'middle', ...style }}>
      <span role="img" aria-label="Pearson & Partners" style={{ display: 'inline-block', width: pmarkWidth(10), height: 10, backgroundColor: '#fff', WebkitMask: 'url(/logo-pmark-white.png) center/contain no-repeat', mask: 'url(/logo-pmark-white.png) center/contain no-repeat', flexShrink: 0 }} />
      {t('Director')}
    </span>
  );
}

// DirectorChip: a small kobalt [P& Director] pill wrapped in an InfoHint that,
// on hover/focus, explains the feature is director-only. LEGACY — no longer
// mounted in the UI. Kept as an export for compatibility; new code should wrap
// the specific director-only CONTROL in DirectorHoverHint instead so the pill
// only surfaces on hover of the element non-directors don't get.
export function DirectorChip({ style } = {}) {
  return (
    <InfoHint text={t('Deze functie is alleen zichtbaar en actief voor directors.')} width={250}>
      <span style={{ cursor: 'help' }}><DirectorPill style={style} /></span>
    </InfoHint>
  );
}

// HoverCard: the shared hover-overlay skeleton. Wraps a trigger; on hover or
// keyboard focus it portals a fixed-position card anchored to the trigger's
// viewport rect (so an overflow:hidden card ancestor - RailCard etc. - never
// clips it). The card carries pointerEvents:'none' so it NEVER blocks a click.
// `card` is the card body content; this component owns the portal, positioning
// and card chrome. Both DirectorHoverHint and OwnerAvatar render through here so
// the overlay looks and behaves identically. Pass `align` ('left' default |
// 'right') for edge cases, `side` ('bottom' default | 'left' | 'right', see
// placeTooltip) for a trigger inside a click menu, and `style` to move
// layout-critical styles off the child onto the wrapper. `disabled` keeps the
// trigger wiring but renders no card: a row hands its hover over to a nested
// interactive overlay (AgendaRow's fasecel -> FaseButton), so the two never
// stack. `pinned` (celweergave van de Activiteit, U5) houdt de kaart open los
// van hover en focus en zet pointer-events aan, zodat links en knoppen erin
// klikbaar zijn; de aanroeper beheert het vastzetten en losmaken (Escape,
// klik erbuiten). Zonder pinned verandert er niets.
//
// Contract (eigenaarsbesluit 13 sep 2026): the card is up for as long as the
// cursor is on the trigger, EXCEPT while a click-opened menu of the wrapped
// control is open. A wrapped <Select> portals its listbox into document.body,
// but React routes the listbox's mouse events through this wrapper, so a plain
// mouseleave never fires while the cursor browses the options: without the
// aria-expanded check the card sat on top of the listbox (Vandaag, Processen,
// Mij/Hele team). Focus that follows a mousedown is a click, not keyboard
// focus, and does not reopen the card; a Tab onto the control still does.
export function HoverCard({ children, card, align = 'left', width = 240, style, disabled = false, side = 'bottom', pinned = false }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const [pos, setPos] = useState(null);
  const insideRef = useRef(false); // cursor is on the trigger (or in its portaled menu)
  const pointerRef = useRef(false); // mouse button is down: the focus that follows is a click
  // Shares placeTooltip with InfoHint: clamp to the viewport (shift left off the
  // right edge, flip above off the bottom) and re-place on scroll/resize.
  const place = useCallback(() => {
    const el = anchorRef.current; if (!el) return;
    const h = tipRef.current ? tipRef.current.offsetHeight : 0;
    setPos(placeTooltip(el.getBoundingClientRect(), width, h, align, side));
  }, [width, align, side]);
  const menuOpen = () => { const el = anchorRef.current; return !!(el && el.querySelector('[aria-expanded="true"]')); };
  const show = () => { if (menuOpen()) return; place(); setOpen(true); };
  const hide = () => setOpen(false);
  // Every move inside re-evaluates, so the card returns after a pick (cursor
  // still on the control, menu closed) and leaves as soon as the menu opens.
  const onMove = () => {
    if (!insideRef.current || pointerRef.current) return;
    if (menuOpen()) { if (open) hide(); } else if (!open) show();
  };
  const onKey = (e) => {
    pointerRef.current = false;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') hide(); // opens the control's menu
  };
  const shown = open || pinned;
  useEffect(() => {
    if (!shown) return undefined;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [shown, place]);
  return (
    <span
      ref={anchorRef}
      style={{ position: 'relative', display: 'inline-flex', ...style }}
      onMouseEnter={() => { insideRef.current = true; show(); }}
      onMouseLeave={() => { insideRef.current = false; hide(); }}
      onMouseMove={onMove}
      onFocusCapture={() => { if (!pointerRef.current) show(); }}
      onBlurCapture={hide}
      onMouseDownCapture={() => { pointerRef.current = true; hide(); }}
      onMouseUpCapture={() => { pointerRef.current = false; }}
      onKeyDownCapture={onKey}
    >
      {children}
      {shown && pos && !disabled && typeof document !== 'undefined' && createPortal(
        <span ref={tipRef} role={pinned ? 'dialog' : 'tooltip'} style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9700, pointerEvents: pinned ? 'auto' : 'none', width, ...SURFACE.tooltip, padding: '8px 10px', fontSize: HV2.fsText, fontWeight: 400, lineHeight: 1.45, color: HV2.ink2, whiteSpace: 'normal', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, cursor: 'default', fontFamily: 'inherit' }}>
          {card}
        </span>, document.body)}
    </span>
  );
}

// TruncHintLayer: EEN hover voor een hele tabel (eigenaarsverzoek 30 sep 2026).
// Een cel doet mee met data-pb-full="<volledige tekst>"; de hover verschijnt
// meteen (geen timer, geen native title met zijn OS-vertraging) en alleen als de
// cel of een afknippend kind in die cel echt is afgekapt. Een gedelegeerde
// luisteraar op hostRef in plaats van een HoverCard per cel: vijftig rijen maal
// twaalf kolommen zou zeshonderd instanties met eigen state en effecten zijn.
// Zelfde vlak (SURFACE.tooltip), plaatsing (placeTooltip) en laag (9700) als
// InfoHint en HoverCard.
const clipsX = (el) => {
  try { return window.getComputedStyle(el).overflowX !== 'visible'; } catch { return false; }
};
export function isCellTruncated(cell) {
  if (!cell) return false;
  const nodes = [cell, ...cell.querySelectorAll('*')];
  return nodes.some((n) => n.clientWidth > 0 && n.scrollWidth > n.clientWidth + 1 && clipsX(n));
}
export function TruncHintLayer({ hostRef }) {
  const [tip, setTip] = useState(null); // { text, rect }
  const [pos, setPos] = useState(null);
  const tipRef = useRef(null);
  const cellRef = useRef(null);
  const lastTypeRef = useRef('mouse');
  useEffect(() => {
    const host = hostRef && hostRef.current;
    if (!host) return undefined;
    const hide = () => { cellRef.current = null; setTip(null); setPos(null); };
    const onPtr = (e) => { lastTypeRef.current = e.pointerType || 'mouse'; };
    const onDown = (e) => { onPtr(e); hide(); };
    const onOver = (e) => {
      if (lastTypeRef.current === 'touch') return;
      const cell = e.target && e.target.closest ? e.target.closest('[data-pb-full]') : null;
      if (cell === cellRef.current) return;
      if (!cell || !host.contains(cell)) { if (cellRef.current) hide(); return; }
      cellRef.current = cell;
      const text = cell.getAttribute('data-pb-full') || '';
      if (!text || !isCellTruncated(cell)) { setTip(null); setPos(null); return; }
      setPos(null);
      setTip({ text, rect: cell.getBoundingClientRect() });
    };
    const onOut = (e) => {
      const cur = cellRef.current;
      if (!cur) return;
      const to = e.relatedTarget;
      if (to && cur.contains(to)) return;
      hide();
    };
    host.addEventListener('mouseover', onOver);
    host.addEventListener('mouseout', onOut);
    host.addEventListener('wheel', hide, { passive: true });
    host.addEventListener('pointerdown', onDown);
    host.addEventListener('pointermove', onPtr);
    host.addEventListener('click', hide);
    window.addEventListener('scroll', hide, true);
    // Een record dat via het toetsenbord of een route opent, laat geen hint staan.
    window.addEventListener('hashchange', hide);
    window.addEventListener('keydown', hide);
    return () => {
      host.removeEventListener('mouseover', onOver);
      host.removeEventListener('mouseout', onOut);
      host.removeEventListener('wheel', hide);
      host.removeEventListener('pointerdown', onDown);
      host.removeEventListener('pointermove', onPtr);
      host.removeEventListener('click', hide);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('hashchange', hide);
      window.removeEventListener('keydown', hide);
    };
  }, [hostRef]);
  // Meten voor de eerste paint: de doos staat onzichtbaar op zijn plek, de
  // layout-effect zet hem daarna nog in hetzelfde frame op zijn echte plaats.
  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const el = tipRef.current;
    setPos(placeTooltip(tip.rect, el.offsetWidth, el.offsetHeight, 'left'));
  }, [tip]);
  if (!tip || typeof document === 'undefined') return null;
  return createPortal(
    <span ref={tipRef} role="tooltip" style={{ position: 'fixed', top: pos ? pos.top : tip.rect.bottom + 6, left: pos ? pos.left : tip.rect.left, visibility: pos ? 'visible' : 'hidden', zIndex: 9700, pointerEvents: 'none', width: 'max-content', maxWidth: 420, ...SURFACE.tooltip, padding: '8px 10px', fontSize: HV2.fsText, fontWeight: 400, lineHeight: 1.45, color: HV2.ink2, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
      {tip.text}
    </span>, document.body);
}

// DirectorGlyph: de babyblauwe omgekeerde piramide zelf (eigenaarsregel 30 aug
// 2026): het vaste merkteken voor director-only UI, zodat directors ZONDER hover
// al zien welke elementen normale gebruikers niet krijgen. Babyblauwe vulling
// met kobalt rand (dezelfde kobalt als de Director-pill) zodat hij op elke
// ondergrond leesbaar blijft.
function DirectorGlyph({ style }) {
  return (
    <svg aria-hidden="true" className="pb-dir-mark" width="10" height="10" viewBox="0 0 10 10" style={style}>
      <path d="M0.9 1.3h8.2L5 9.1z" fill="#89CFF0" stroke="#0047ab" strokeWidth="0.9" strokeLinejoin="round" />
    </svg>
  );
}

// DirectorHoverHint: wraps an existing director-only CONTROL without changing its
// layout. The control carries the always-visible baby-blue pyramid (top-right
// corner); hover or keyboard focus reveals the kobalt [P& Director] pill + an
// explanation. Attach this ONLY to elements that non-directors never receive, so
// the badge is a precise signal of "this is director-exclusive". Renders through
// HoverCard, so it shares the exact portal/positioning skeleton. The corner
// glyph is pointerEvents none: it mag nooit een klik of hover van het control
// zelf afvangen. `badge={false}` is for DirectorInlineMark, which IS the glyph.
// Pass `align`/`width`/`style` straight through.
export function DirectorHoverHint({ children, align = 'left', width = 240, style, badge = true, side = 'bottom' }) {
  return (
    <HoverCard align={align} width={width} style={style} side={side} card={<><DirectorPill />{t('Deze functie is alleen zichtbaar en actief voor directors.')}</>}>
      {children}
      {badge && <DirectorGlyph style={{ position: 'absolute', top: -5, right: -5, zIndex: 1, pointerEvents: 'none' }} />}
    </HoverCard>
  );
}

// DirectorInlineMark: de piramide IN de tekststroom, voor plekken waar de
// hoek-overlay van DirectorHoverHint niet past: menuregels, voetnoten,
// tabelkoppen. Zelfde hoverkaart, zelfde glyph, geen absolute positionering,
// dus veilig in elke flex-rij of dropdown zonder de layout te raken.
export function DirectorInlineMark({ style } = {}) {
  return (
    <DirectorHoverHint badge={false} style={{ verticalAlign: 'middle', flexShrink: 0, cursor: 'help', ...style }}>
      <DirectorGlyph />
    </DirectorHoverHint>
  );
}

// ── useMenuDismiss ──────────────────────────────────────────────────────────
// HET gedeelde sluit-idioom voor klik-menus: een document-mousedown die sluit
// bij een klik buiten, plus Escape. Eén hook, zodat er niet per scherm een
// eigen variant ontstaat (klik-weg-vlakken zonder Escape, of menus die
// helemaal niet sluiten).
//
// `refs` is één ref of een lijst refs. Een lijst is nodig zodra trigger en
// paneel niet in dezelfde DOM-tak staan, bijvoorbeeld bij een fixed
// gepositioneerd paneel dat buiten een overflow:auto-voorouder moet vallen.
//
// De listeners hangen pas aan zodra `active` waar is, dus de klik die het menu
// opent kan het niet meteen weer sluiten. Escape luistert in de CAPTURE-fase en
// stopt daar de propagatie: anders sluit dezelfde toetsaanslag ook de modal of
// het recordvenster eronder.
export function useMenuDismiss(active, close, refs) {
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!active) return undefined;
    const inside = (target) => {
      const list = Array.isArray(refsRef.current) ? refsRef.current : [refsRef.current];
      return list.some((r) => r && r.current && r.current.contains(target));
    };
    const onDoc = (e) => { if (!inside(e.target)) closeRef.current(); };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      closeRef.current();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey, true); };
  }, [active]);
}

// ── OverflowMenu ("Meer") ───────────────────────────────────────────────────
// DE gedeelde KLIK-dropdown voor actiebalken. HoverCard hierboven is
// hover-getriggerd en zijn paneel staat op pointerEvents:'none', dus dat ding kan
// per definitie geen klikbare items dragen - dit is de klikbare tegenhanger.
// Gegeneraliseerd uit CvMenu (CrmDatabase.js): eigen open-state, een
// position:relative wrapper, een absoluut gepositioneerd paneel, en het
// gedeelde sluit-idioom useMenuDismiss (klik buiten of Escape).
//
// De trigger is een render-prop: lib.js importeert Button.js NIET (Button.js
// importeert lib.js, dus dat zou een cirkel zijn). De aanroeper levert dus de
// canonieke <Button>, wat meteen garandeert dat de trigger een echte <button> is
// en met toetsenbord bereikbaar blijft.
//
//   <OverflowMenu align="right" trigger={({ toggle }) => (
//     <Button variant="white" size="sm" onClick={toggle}>Meer ▼</Button>
//   )}>
//     <OverflowMenuItem label="Archiveer" onClick={fn} />
//     <OverflowMenuDivider />
//     <OverflowMenuItem label="Verwijderen" danger onClick={fn} />
//   </OverflowMenu>
const OverflowMenuCtx = React.createContext(null);

export function OverflowMenu({ trigger, children, align = 'left', width = 244, style, portal = false }) {
  const [open, setOpen] = useState(false);
  // Viewportklem: op een smal scherm steekt een rechts-uitgelijnd paneel van
  // 244-290px links buiten beeld (de knop staat halverwege de regel). Na openen
  // eenmalig meten en het paneel precies genoeg opschuiven; op desktop meet dit
  // 0 en verandert er niets.
  const [shift, setShift] = useState(0);
  const [maxH, setMaxH] = useState(null);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  useMenuDismiss(open, close, [ref, panelRef]);
  useEffect(() => {
    if (!open) { setShift(0); setMaxH(null); setPos(null); return; }
    const el = panelRef.current;
    if (!el) return;
    // A scrollable toolbar clips an inline menu regardless of its z-index.
    // Opt into a body portal there, keeping it anchored during scroll/resize.
    if (portal) {
      const place = () => {
        const anchor = ref.current?.getBoundingClientRect();
        if (!anchor) return;
        const menu = el.getBoundingClientRect();
        const left = align === 'right' ? anchor.right - menu.width : anchor.left;
        const below = anchor.bottom + 4;
        const top = below + menu.height > window.innerHeight - 12 && anchor.top > window.innerHeight - anchor.bottom
          ? anchor.top - menu.height - 4 : below;
        setPos({ left: Math.max(12, Math.min(left, window.innerWidth - menu.width - 12)), top: Math.max(12, Math.min(top, window.innerHeight - menu.height - 12)) });
      };
      place();
      window.addEventListener('scroll', place, true);
      window.addEventListener('resize', place);
      return () => {
        window.removeEventListener('scroll', place, true);
        window.removeEventListener('resize', place);
      };
    }
    const r = el.getBoundingClientRect();
    const b = clipBounds(el, 12);
    if (r.left < b.left) setShift(b.left - r.left);
    else if (r.right > b.right) setShift(Math.max(b.left - r.left, b.right - r.right));
    // Een lang menu (record Meer: ~750px) liep onder het recordvenster uit en
    // de onderste regels waren onbereikbaar: begrens en laat het zelf scrollen.
    if (r.bottom > b.bottom) setMaxH(Math.max(160, Math.floor(b.bottom - r.top)));
  }, [open, portal, align, width]);
  const panel = open ? (
    <div ref={panelRef} role="menu" style={{
      ...(portal
        ? { position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden', zIndex: 9750, maxHeight: 'calc(100vh - 24px)', overflowY: 'auto' }
        : { position: 'absolute', top: '120%', ...(align === 'right' ? { right: 0 } : { left: 0 }), transform: shift ? `translateX(${shift}px)` : undefined, zIndex: 30, ...(maxH ? { maxHeight: maxH, overflowY: 'auto' } : null) }),
      width, maxWidth: 'calc(100vw - 24px)', ...SURFACE.menu, padding: 6,
    }}>
      <OverflowMenuCtx.Provider value={close}>{children}</OverflowMenuCtx.Provider>
    </div>
  ) : null;
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0, ...style }}>
      {typeof trigger === 'function' ? trigger({ open, toggle, close }) : trigger}
      {portal && panel ? createPortal(panel, document.body) : panel}
    </div>
  );
}

const overflowItemStyle = { display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', textAlign: 'left', padding: '8px 9px', background: 'transparent', border: 'none', borderRadius: 5, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 560, lineHeight: 1.35, cursor: 'pointer' };

// Eén regel in een OverflowMenu. `danger` kleurt hem HV2.redText - destructieve
// acties horen onderaan, onder een OverflowMenuDivider, nooit tussen het dagelijks
// werk. Sluit het menu vóór de actie, zodat een modal of confirm die de actie
// opent niet achter een openstaand paneel verschijnt.
export function OverflowMenuItem({ icon, label, sub, onClick, danger = false, disabled = false, title }) {
  const close = useContext(OverflowMenuCtx);
  return (
    <button type="button" role="menuitem" title={title} disabled={disabled}
      onClick={(e) => { if (disabled) return; if (close) close(); if (onClick) onClick(e); }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = HV2.bgSub; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      style={{ ...overflowItemStyle, color: danger ? HV2.redText : HV2.ink, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'default' : 'pointer' }}>
      {icon ? <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}>{icon}</span> : null}
      <span style={{ minWidth: 0 }}>{label}{sub ? <><br /><span style={{ color: HV2.mute2, fontWeight: 400 }}>{sub}</span></> : null}</span>
    </button>
  );
}

// Voor bestaande zelfstandige controls (een knop met eigen status-fetch, een
// select) die als menu-regel meemoeten: het menu sluit zodra er in de regel
// geklikt is, de control zelf blijft ongewijzigd.
export function OverflowMenuRow({ children, label }) {
  const close = useContext(OverflowMenuCtx);
  return (
    <div onClick={() => { if (close) close(); }} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, rowGap: 6, padding: '5px 9px', minWidth: 0 }}>
      {label ? <span style={{ fontSize: HV2.fsText, fontWeight: 560, color: HV2.muted, flexShrink: 0 }}>{label}</span> : null}
      {children}
    </div>
  );
}

// Haarlijn boven het destructieve blok onderin een OverflowMenu.
export function OverflowMenuDivider() {
  return <div style={{ height: 1, background: HV2.divider, margin: '5px 3px' }} />;
}

// Groepskop in een OverflowMenu (Documenten, Versturen, Record, Status). Zelfde
// recept als de 'Recent bewerkt'-kop in het bellijstpaneel: 13/700 in HV2.muted.
// Alleen tonen wanneer er minstens TWEE gelabelde groepen de poortwachters
// overleven; boven een enkele lijst is een kop ruis. menuGroupItems regelt dat.
export function OverflowMenuGroupLabel({ children }) {
  return <div style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.muted, padding: '7px 9px 3px' }}>{children}</div>;
}

// Bouwt de kinderen van een gegroepeerd OverflowMenu.
//   groups: [{ label?, danger?, items: [node | false | null] }]
// Per groep vallen de uitgepoorte regels (false/null) weg; een groep zonder
// overgebleven regels verdwijnt volledig, inclusief haar kop. De danger-groep
// krijgt een haarlijn boven zich in plaats van een kop. De vaste volgorde
// (Documenten, Versturen, Record, Status, danger) bepaalt de aanroeper.
export function menuGroupItems(groups) {
  const vis = (groups || [])
    .map((g) => ({ ...g, items: (g.items || []).filter(Boolean) }))
    .filter((g) => g.items.length);
  const labelled = vis.filter((g) => g.label && !g.danger).length >= 2;
  const out = [];
  vis.forEach((g, gi) => {
    if (g.danger) { if (gi > 0) out.push(<OverflowMenuDivider key={`mg-div-${gi}`} />); }
    else if (g.label && labelled) out.push(<OverflowMenuGroupLabel key={`mg-lab-${gi}`}>{g.label}</OverflowMenuGroupLabel>);
    g.items.forEach((it, ii) => out.push(React.isValidElement(it) && it.key == null ? React.cloneElement(it, { key: `mg-${gi}-${ii}` }) : it));
  });
  return out;
}

// Menubreedte volgt de INHOUD die overblijft: draagt een zichtbare regel een
// sub-regel, dan 290, anders 264. Per menu-instantie berekenen, na de poorten:
// een menu waarvan de sub-regels wegvallen hoort weer smal te zijn.
export function menuWidthFor(items) {
  const carriesSub = (node) => {
    if (!node || typeof node !== 'object') return false;
    if (Array.isArray(node)) return node.some(carriesSub);
    if (!node.props) return false;
    if (node.props.sub) return true;
    return carriesSub(node.props.children);
  };
  return carriesSub(items) ? 290 : 264;
}

// Menu-iconen: EEN glyph per begrip, stroke-only, currentColor, viewBox 0 0 24 24,
// zodat een regel op elke maat hetzelfde leest. De paden komen letterlijk uit het
// prototype (recordvenster-acties.html, de P-map). Geen emoji, geen P&-monogram
// als opsommingsteken: het monogram houdt alleen zijn echte taken.
// Chat (29 sep 2026): een ronde spraakballon met het P&-merk erin. Het merk
// komt letterlijk uit public/brand/pmark-black.svg (twee evenodd-paden, viewBox
// 25 67.07 249.97 165.87), geschaald naar ongeveer 11 eenheden breed en
// gecentreerd in de ballon. Gevuld in currentColor, geen lijn, zodat het merk op
// 14px nog leest; de ballon zelf is lijnwerk zoals de rest van de chrome.
export const CHAT_BUBBLE_D = 'M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.6A8.4 8.4 0 1 1 21 11.5z';
const CHAT_PMARK_D = [
  'm249.26,200.08l.96-1.15c9.7-11.59,14.71-26.81,15.71-47.73h-18.75c-.46,14.17-3.1,23.61-8.94,32.12l-1.36,1.99-37.03-45.25,1.55-1.12c3.89-2.81,11.91-8.44,11.9-8.44,11.33-7.99,16.61-16.9,16.61-28.02,0-20.85-15.15-35.41-36.83-35.41s-37.97,14.14-37.97,35.18c0,6.07,1.54,12.02,4.71,18.13l16.75-11.12c-.87-2.32-1.3-4.55-1.3-6.78,0-10.88,7.16-18.19,17.81-18.19s18.04,7.75,18.04,18.43c0,8.82-6.4,14.46-12.65,18.79-.22.15-5.32,3.79-7.84,5.43l-19.41,12.62c-15.21,10.24-31.44,23.04-31.44,45.56,0,29.94,18.98,47.82,50.77,47.82,10.13,0,19.42-2.02,27.66-6.02l-12-14.78c-4.79,1.8-9.8,2.63-15.67,2.63-18.14,0-31.3-12.67-31.3-30.13s11.54-25.16,22.71-33.08l1.37-.97,39.7,48.35,13.96,16.87,12.72,15.72h25.27l-25.74-31.45Z',
  'm72.16,70.36l-47.16.37.37,159.61h20.21v-79.07l-.36-62.18h26.95c25.38,0,37.72,10.15,37.72,31.02s-12.22,30.69-37.71,31.15h-9.43v18.37h9.43c36.5,0,58.3-18.5,58.3-49.51s-21.79-49.76-58.3-49.76Z',
];
const CHAT_PMARK_T = 'translate(5.75 4.75) scale(0.045)';
const chatGlyph = (
  <>
    <path d={CHAT_BUBBLE_D} />
    <g transform={CHAT_PMARK_T} fill="currentColor" stroke="none" fillRule="evenodd">
      {CHAT_PMARK_D.map((d, i) => <path key={i} d={d} />)}
    </g>
  </>
);
export function ChatIcon({ size = 18, color = 'currentColor', strokeWidth = 2, style, title }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" role={title ? 'img' : undefined} aria-label={title || undefined}
      aria-hidden={title ? undefined : 'true'} style={{ flexShrink: 0, display: 'block', color, ...style }}>
      {title && <title>{title}</title>}
      {chatGlyph}
    </svg>
  );
}

const MENU_ICON_PATHS = {
  calCheck: <>{kpiShapes('interview')}</>,
  // De KPI-vormen onder hun eigen naam (segmenttabel van de Processen-container).
  interview: <>{kpiShapes('interview')}</>,
  plaatsing: <>{kpiShapes('plaatsing')}</>,
  calPlus: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M12 14v4M10 16h4" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  process: <>{BRANCH_PATHS.map((d, i) => <path key={i} d={d} />)}</>,
  bdProc: <>{BRANCH_PATHS.map((d, i) => <path key={i} d={d} />)}</>,
  cvSend: <>{kpiShapes('cvSend')}</>,
  fileLines: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h5M8 17h7" /></>,
  filePerson: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 16a3 3 0 0 1 6 0" /><circle cx="12" cy="12" r="1.6" /></>,
  fileDown: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M12 18v-6M9.5 14.5 12 12l2.5 2.5" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /></>,
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>,
  // Versturen is overal het document met pijl, ook buiten CV Send (eigenaarsbesluit 5 okt 2026).
  send: <>{kpiShapes('cvSend')}</>,
  // Niet meer werkzaam bij dit bedrijf: de koffer met een streep erdoor.
  briefcaseOff: <><path d="M4 7h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><path d="M3 3l18 18" /></>,
  briefMail: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M9 13h6" /></>,
  list: <><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  warning: <><path d="M12 2L1 21h22L12 2z" /><path d="M12 9v5M12 17h.01" /></>,
  tree: <><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" /><path d="M12 8v3M6 16v-2h12v2M12 14v-3" /></>,
  history: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 8v4l3 2" /></>,
  ping: <><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><path d="M12 8v4M12 14h.01" /></>,
  gate: <><path d="M4 4h16v5H4zM4 15h16v5H4z" /><path d="M12 9v6" /></>,
  buildCheck: <><path d="M3 21h18M6 21V8l6-4 6 4v13" /><path d="M9.5 14.5 11 16l3.5-3.5" /></>,
  buildDown: <><path d="M3 21h18M6 21V8l6-4 6 4v13" /><path d="M12 12v5M9.5 14.5 12 17l2.5-2.5" /></>,
  star: <path d="M12 3l2.6 5.6 6 .8-4.4 4.2 1.1 6-5.3-2.9L6.7 19.6l1.1-6L3.4 9.4l6-.8z" />,
  archive: <><rect x="2" y="4" width="20" height="5" rx="1" /><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M10 13h4" /></>,
  unarchive: <><rect x="2" y="4" width="20" height="5" rx="1" /><path d="M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M12 18v-6M9.5 14.5 12 12l2.5 2.5" /></>,
  mailOff: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /><path d="M4 20 20 4" /></>,
  userOff: <><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4" /><path d="m17 8 5 5M22 8l-5 5" /></>,
  // Een nieuwe persoon (stand Nieuw van een sollicitatie, lib/applicant-state.mjs): het silhouet van userOff met een plus.
  userPlus: <><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4" /><path d="M19 8v6M16 11h6" /></>,
  pin: <><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></>,
  // Punaise (vastzetten), niet te verwarren met `pin` hierboven: die is een
  // kaartspeld en staat voor een plaats.
  punaise: <><path d="M12 17v5" /><path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1z" /></>,
  owners: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" /><circle cx="17.5" cy="9" r="2.4" /><path d="M17.5 14c2.5 0 4.5 1.9 4.5 4.6" /></>,
  link: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>,
  upgrade: <><path d="M4 12h14" /><path d="M13 7l5 5-5 5" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5 21 21" /></>,
  image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.6" /><path d="M21 16l-5-5-6 6-2-2-5 5" /></>,
  crown: <><path d="M4 17h16l1-9-5 3.5L12 5 8 11.5 3 8z" /><path d="M5 20h14" /></>,
  check: <path d="M4.5 12.5 10 18 19.5 7" />,
  ban: <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>,
  trash: <><path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></>,
  // Knopglyphs (eigenaarsbesluit 15 september 2026): een knop met een label draagt
  // een icoon, zodat 'annuleren', 'bewerken' en 'openen' niet alle drie als
  // dezelfde witte knop lezen. Geen enkele van deze zeven is nieuw getekend: elk
  // pad komt LETTERLIJK van de plek waar het al stond, conform de regel dat er
  // per begrip één tekening bestaat. De bronregel staat per entry.
  close: <><path d="M18 6 6 18" /><path d="M6 6l12 12" /></>,          // MarktenView.js:64, gelijk aan de Cross in Button.js:81
  plus: <path d="M12 5v14M5 12h14" />,                                  // ProcessDetailV2/primitives.js:46, CrmDatabase.js:3499, dealAdapter.js:941
  refresh: <><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></>, // director/MonitorTab.js:271
  pencil: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></>, // detail/parts.js:270, de meerderheidspen van de vier in de boom
  back: <path d="M15 18l-6-6 6-6" />,                                   // WeekAgenda.js:55, QuickCallLog.js:304
  undo: <><path d="M3 7v6h6" /><path d="M3.5 13a9 9 0 1 0 2.6-8.5L3 7" /></>, // Kwalificaties.js:182
  settings: <><path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>, // identiek aan NAV_ICON_PATHS.settings
  // Knopglyphs, tweede ronde (knopicoon-audit 17 september 2026). Zelfde regel als
  // hierboven: overgenomen van de plek waar de tekening al stond, bronregel per entry.
  // Alleen play, stop en volume bestonden nergens in de boom en zijn nieuw, in
  // hetzelfde raster (24-viewBox, stroke 2, ronde uiteinden).
  copy: <><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>, // CrmDatabase.js:5236
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" /></>,             // CrmDatabase.js:5967, NieuweKandidaatModal.js:985
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,                                // CallLists.js:61 (ICO_EYE), MonitorTab.js:265
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,                         // DesignSystem.js:151
  moon: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,                                                                 // app/login/page.js:43
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></>,                                   // gelijk aan NAV_ICON_PATHS.shield, CallLists.js:78
  key: <><circle cx="7.5" cy="15.5" r="4.5" /><path d="M10.7 12.3 21 2" /><path d="M16 7l3 3" /></>,                                  // nieuw: sleutel voor wachtwoord en ontgrendelen
  merge: <><circle cx="6" cy="5" r="2" /><circle cx="18" cy="19" r="2" /><path d="M6 7v15" /><path d="M6 9a10 10 0 0 0 10 10" /></>, // twee lijnen worden er een: vacature samenvoegen
  linkOff: <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /><path d="M4 20 20 4" /></>, // link + de schuine streep van mailOff
  play: <path d="M7 4l13 8-13 8z" />,                                                                                                 // nieuw
  stop: <rect x="6" y="6" width="12" height="12" rx="1" />,                                                                          // nieuw
  volume: <><path d="M11 5 6 9H2v6h4l5 4z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /></>,                                                // nieuw
  chart: <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></>,                                                              // NAV_ICON_PATHS.chart, targets-menu
  chat: chatGlyph,                                                                                                                   // ChatIcon: ballon met P&-merk
  menu: <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>,                                                     // NieuwHotStrip.js:219 (ListIcon), de drie regels van de actieknop met menu
  // Lijst met vergrootglas: de drie regels van `menu` hierboven, ingekort, plus het
  // vergrootglas van `search`, kleiner rechtsonder. De glyphvorm van TitleLink:
  // 'alle CRM-items met deze functie'. Zelfde raster (24-viewBox, stroke 2).
  listSearch: <><path d="M4 6h16" /><path d="M4 12h7" /><path d="M4 18h5" /><circle cx="16" cy="15" r="4" /><path d="M19 18l2.5 2.5" /></>,
};

export function MenuIcon({ name, size = 14, strokeWidth = 2, color = 'currentColor', style }) {
  const paths = MENU_ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={style}>{paths}</svg>
  );
}

// ── Gevulde Material-iconen (sectiekoppen recordvenster) ────────────────────
// Google Material, gevuld, viewBox 0 0 24 24. Bewust naast MenuIcon: de
// sectiekoppen van een recordvenster dragen een gevuld glyph, de menu- en
// navigatiechrome blijft lijnwerk. De paden komen letterlijk uit
// google/material-design-icons. 'description' en 'campaign' bestaan ook als
// markt-glyph in lib/partnerbook-icons.mjs; hier herhaald zodat een sectiekop
// niet aan de marktenlijst vastzit en die twee los kunnen wijzigen.
export const MD_ICON_PATHS = {
  // assignment_turned_in
  assignmentTurnedIn: 'M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z',
  percent: 'M7.5 11C9.43 11 11 9.43 11 7.5S9.43 4 7.5 4 4 5.57 4 7.5 5.57 11 7.5 11zm0-5C8.33 6 9 6.67 9 7.5S8.33 9 7.5 9 6 8.33 6 7.5 6.67 6 7.5 6zM4 18.58L18.59 4 20 5.41 5.42 20 4 18.58zM16.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm0 5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z',
  description: 'M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
  campaign: 'M18 11v2h4v-2h-4zm-2 6.61c.96.71 2.21 1.65 3.2 2.39.4-.53.8-1.07 1.2-1.6-.99-.74-2.24-1.68-3.2-2.4-.4.54-.8 1.08-1.2 1.61zM20.4 5.6c-.4-.53-.8-1.07-1.2-1.6-.99.74-2.24 1.68-3.2 2.4.4.53.8 1.07 1.2 1.6.96-.72 2.21-1.65 3.2-2.4zM4 9c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1v4h2v-4h1l5 3V6L8 9H4zm11.5 3c0-1.33-.58-2.53-1.5-3.35v6.69c.92-.81 1.5-2.01 1.5-3.34z',
  // schedule: de klok naast 'Uren per week' op een vacature.
  schedule: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
  // euro_symbol
  euro: 'M15 18.5c-2.51 0-4.68-1.42-5.76-3.5H15v-2H8.58c-.05-.33-.08-.66-.08-1s.03-.67.08-1H15V9H9.24C10.32 6.92 12.5 5.5 15 5.5c1.61 0 3.09.59 4.23 1.57L21 5.3C19.41 3.87 17.3 3 15 3c-3.92 0-7.24 2.51-8.48 6H3v2h3.06c-.04.33-.06.66-.06 1 0 .34.02.67.06 1H3v2h3.52c1.24 3.49 4.56 6 8.48 6 2.31 0 4.41-.87 6-2.3l-1.78-1.77c-1.13.98-2.6 1.57-4.22 1.57z',
};

// Material Symbols (viewBox 0 -960 960 960, gevuld). Aparte set omdat de
// Symbols-export een ander canvas gebruikt dan de klassieke MD_ICON_PATHS.
export const MS_ICON_PATHS = {
  // library_add: het glyph van de Nieuw-knop.
  libraryAdd: 'M520-400h80v-120h120v-80H600v-120h-80v120H400v80h120v120ZM320-240q-33 0-56.5-23.5T240-320v-480q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H320Zm0-80h480v-480H320v480ZM160-80q-33 0-56.5-23.5T80-160v-560h80v560h560v80H160Zm160-720v480-480Z',
  // orbit (het oude MatchRanking-glyph) is verwijderd: Research draagt sinds
  // 21 aug 2026 het eigen beeldmerk (components/HomeV2/MatchIcon.js).
};

export function MsIcon({ name, size = 16, color = 'currentColor', style }) {
  const d = MS_ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 -960 960 960" fill={color} aria-hidden="true"
      style={{ flexShrink: 0, display: 'block', ...style }}><path d={d} /></svg>
  );
}

export function MdIcon({ name, size = 14, color = 'currentColor', style }) {
  const d = MD_ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true"
      style={{ flexShrink: 0, display: 'block', ...style }}><path d={d} /></svg>
  );
}

// ── Navigatie-iconen (LeftRail) ─────────────────────────────────────────────
// Woonden in LeftRail.js; hier gehoist zodat de Vandaag-koprijen exact hetzelfde
// glyph tonen als het menu-item waar de container naartoe wijst (RailIcon
// hieronder). Multi-path via de array; LeftRail's eigen Ico tekent ze.
export const NAV_ICON_PATHS = {
  inbox:     ['M22 12h-6l-2 3h-4l-2-3H2', 'M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z'],
  bell:      ['M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9', 'M10.3 21a1.94 1.94 0 0 0 3.4 0'],
  home:      ['M3 9.5L12 3l9 6.5', 'M5 9v11h14V9', 'M9 20v-6h6v6'],
  calendar:  ['M3 5h18v16H3z', 'M3 9h18', 'M8 3v4', 'M16 3v4'],
  columns:   ['M4 4h6v16H4z', 'M14 4h6v10h-6z'],
  stages:    ['M7.5 12a2.5 2.5 0 1 0-5 0 2.5 2.5 0 1 0 5 0', 'M14.5 12a2.5 2.5 0 1 0-5 0 2.5 2.5 0 1 0 5 0', 'M21.5 12a2.5 2.5 0 1 0-5 0 2.5 2.5 0 1 0 5 0', 'M7.5 12H9.5', 'M14.5 12H16.5'],
  users:     ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  userplus:  ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M19 8v6', 'M22 11h-6'],
  chart:     ['M3 3v18h18', 'M7 14l4-4 3 3 5-6'],
  book:      ['M4 19.5A2.5 2.5 0 0 1 6.5 17H20', 'M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'],
  pen:       ['M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z', 'M15 5l4 4'],
  // Handset: RAIL_ICON_PATHS.calls (belknop-glyph) + page.js railNieuwItems 'Call Log'.
  calls:     [CALLS_PATH],
  list:      ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'],
  briefcase: ['M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16', 'M2 11h20', 'M4 7h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z'],
  doc:       ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
  archive:   ['M21 8v13H3V8', 'M1 3h22v5H1z', 'M10 12h4'],
  grid:      ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'],
  markten:   ['M3 4h18v16H3z', 'M3 12h18', 'M12 4v16'], // segmented board = markten
  // Building: ENT_PATHS.company (CRM-brede bedrijfsglyph) + page.js railNieuwItems 'Klant'.
  company:   ['M3 21h18M6 21V8l6-4 6 4v13M10 21v-5h4v5'],
  kaart:     ['M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11z', 'M14.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0'], // zelfde speld als MENU_ICON_PATHS.pin; rail-item /map
  // Werving (promptwerkplaats): schijf met roos, "gericht zoeken".
  target:    ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9z', 'M12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
  layers:    ['M12 2 2 7l10 5 10-5-10-5z', 'M2 17l10 5 10-5', 'M2 12l10 5 10-5'],
  branch:    BRANCH_PATHS, // shared with the detail-window EntityChip
  search:    ['M11 3a8 8 0 1 0 0 16 8 8 0 1 0 0-16z', 'M21 21l-4.3-4.3'],
  puzzle:    ['M20.5 11H19V7a2 2 0 0 0-2-2h-4V3.5a2.5 2.5 0 0 0-5 0V5H4a2 2 0 0 0-2 2v3.8h1.5a2.6 2.6 0 0 1 0 5.2H2V20a2 2 0 0 0 2 2h3.8v-1.5a2.6 2.6 0 0 1 5.2 0V22H17a2 2 0 0 0 2-2v-4h1.5a2.5 2.5 0 0 0 0-5z'],
  browser:   ['M3 5h18v14H3z', 'M3 9h18', 'M7 7h.01', 'M10 7h.01'],
  chevron:   ['M6 9l6 6 6-6'],
  clock:     ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
  check:     ['M9 11l3 3 8.5-8.5', 'M20.5 12v7a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10'],
  settings:  ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z'],
  // Console (Assistent, eigenaarsverzoek 15 september 2026): het universele
  // terminalteken, een chevron met een onderstreping. Dit is de ENE nieuwe
  // tekening in deze tabel sinds de regel 'iconen komen uit de bestaande set':
  // er stond geen console-, code- of terminalglyph in NAV_ICON_PATHS noch in
  // MENU_ICON_PATHS, en de eigenaar vroeg er expliciet om. Zelfde raster en
  // zelfde streekdikte als de rest, zodat hij op elke maat gelijk leest.
  terminal:  ['M5 7l4.5 4.5L5 16', 'M12.5 16.5h6.5'],
  // Financiën (management surface): bar chart.
  finance:   ['M3 3v18h18', 'M8 17v-4', 'M13 17V9', 'M18 17V5'],
  // Beheer (management surface): shield with a check.
  shield:    ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M9 12l2 2 4-4'],
};

// ── RailIcon: het kopicoon van een Vandaag-container ─────────────────────────
// Eén glyph per containerkop, links van de titel en RECHTS van de sleepgreep.
// Regel: wijst een container naar een bestaande plek in de app, dan draagt hij
// exact HET glyph van die plek (menu-item, entiteitsicoon, menu-actie) - geen
// tweede tekening voor hetzelfde begrip. Alleen containers zonder bestaand
// equivalent (aandacht, verwachte omzet, analyse) krijgen een nieuw
// pad, hier onderaan. Kruisverwijzingen: 'lijsten' = de pen van Call lists,
// 'targets' = de grafiek van de Targets-tab, 'processen' = BRANCH_PATHS.
const RAIL_ICON_PATHS = {
  taken:      <>{NAV_ICON_PATHS.check.map((d, i) => <path key={i} d={d} />)}</>,
  agenda:     <>{NAV_ICON_PATHS.calendar.map((d, i) => <path key={i} d={d} />)}</>,
  processen:  <>{BRANCH_PATHS.map((d, i) => <path key={i} d={d} />)}</>,
  targets:    <>{NAV_ICON_PATHS.chart.map((d, i) => <path key={i} d={d} />)}</>,
  lijsten:    <>{NAV_ICON_PATHS.pen.map((d, i) => <path key={i} d={d} />)}</>,
  markten:    <>{NAV_ICON_PATHS.markten.map((d, i) => <path key={i} d={d} />)}</>,
  director:   MENU_ICON_PATHS.ping,
  teamtarget: MENU_ICON_PATHS.owners,
  // getter: ENT_PATHS staat verderop in dit bestand, dus pas op renderen lezen.
  get jobs() { return ENT_PATHS.job; },
  // Call stats: dezelfde handset als de belknop in de LeftRail-voet.
  calls:      <>{NAV_ICON_PATHS.calls.map((d, i) => <path key={i} d={d} />)}</>,
  // NTP: de handset met een kruis (KPI_ICON_SHAPES.ntp, teambalk).
  ntp:        <>{kpiShapes('ntp')}</>,
  // VM, Gemiste calls en Telefonie van de bedrijfsgrafiek (KPI_ICON_SHAPES).
  vm:         <>{kpiShapes('vm')}</>,
  gemist:     <>{kpiShapes('gemist')}</>,
  telefonie:  <>{kpiShapes('telefonie')}</>,
  // Nieuw (geen bestaand equivalent in de app):
  aandacht:   <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5v5.5" /><path d="M12 16.3v.2" /></>,
  // Google Material Symbols "toll" (coins), herschreven als stroke-only.
  omzet:      <>{kpiShapes('omzet')}</>,
  analyse:    <path d="M3 12h3.5l2.5-6 4 13 2.6-7H21" />,
};

export function RailIcon({ name, size = 15, strokeWidth = 1.9, color = HV2.ink3, style }) {
  const paths = RAIL_ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ flexShrink: 0, display: 'block', ...style }}>{paths}</svg>
  );
}

// ── StatPill: de tellingen van een lijst als EEN randloos geheel ────────────
// Drie losse Chips naast elkaar ('29 leden' '0 gebeld' '13 opnieuw bellen')
// lezen als drie knoppen die je zou kunnen indrukken, terwijl het EEN feit is:
// hoe ver je in de lijst bent. Dit is die ene pil: segmenten achter een 1px
// naad, elk met zijn eigen chrome-glyph, en optioneel een voortgangsbalkje.
// Randloos kan alleen met een echte vulling - HV2.bgSub (#fcfcfc) valt weg op
// wit - dus de grond ligt een trede onder HV2.divider. Staat in lib.js en niet
// inline in de koprij, zodat het chip-contract (DESIGN-SYSTEM.md Regel 31) geldt
// en een volgende lijstkop dezelfde pil kan dragen.
export const STAT_FILL = '#eeeeef';
const STAT_SEAM = '#d6d6d8';
const STAT_TRACK = '#dcdcde';
// Het glyph komt uit de BESTAANDE chrome-sets, nooit uit een nieuwe tekening:
// MENU_ICON_PATHS eerst (owners, history), dan NAV_ICON_PATHS (calls = exact de
// handset van de belknop in de LeftRail, zodat telling en actie hetzelfde merk
// dragen). Lijnwerk, want een telling in een kop is chrome en geen recordtype
// (DESIGN-SYSTEM.md regel 7).
function statGlyph(name, color) {
  if (!name) return null;
  if (MENU_ICON_PATHS[name]) return <MenuIcon name={name} size={13} strokeWidth={1.9} color={color} style={{ display: 'block', flexShrink: 0 }} />;
  const nav = NAV_ICON_PATHS[name];
  if (!nav) return null;
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.9}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {nav.map((d, i) => <path key={i} d={d} />)}
    </svg>
  );
}
// segments: [{ key, icon, text, title, color, bar }] - false/null wordt
// overgeslagen, zodat een aanroep de voorwaarden inline mag schrijven.
export function StatPill({ segments = [], title, style }) {
  const vis = segments.filter(Boolean);
  if (!vis.length) return null;
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', background: STAT_FILL, color: HV2.ink2, borderRadius: 4, padding: '2px 10px', fontSize: HV2.fsText, fontWeight: 560, lineHeight: '18px', whiteSpace: 'nowrap', flexShrink: 0, ...style }}>
      {vis.map((s, i) => (
        <React.Fragment key={s.key || i}>
          {i > 0 && <span aria-hidden="true" style={{ width: 1, height: 14, background: STAT_SEAM, margin: '0 8px', flexShrink: 0 }} />}
          <span title={s.title} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: s.color || undefined }}>
            {s.bar != null && (
              <span aria-hidden="true" style={{ width: 44, height: 5, background: STAT_TRACK, display: 'inline-block', overflow: 'hidden', flexShrink: 0 }}>
                <span style={{ display: 'block', height: '100%', width: `${Math.max(0, Math.min(100, s.bar))}%`, background: HV2.green }} />
              </span>
            )}
            {statGlyph(s.icon, s.color || HV2.mute2)}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.text}</span>
          </span>
        </React.Fragment>
      ))}
    </span>
  );
}

// ── Onderscheidingsbalk (RecordBadges) ──────────────────────────────────────
// Alle record-iconen als een rij van 20-badges RECHTS van de naam, als lintjes op
// een uniform. De volgorde ligt vast: identiteit eerst (logo, persoon/DMU, markt),
// dan de mijlpalen in levensloopvolgorde (gesourced, interview gevoerd, geplaatst),
// administratie (privacy geaccepteerd) achteraan. ALLEEN wat waar of behaald is
// wordt getoond, nooit een grijze lege plek: geen enkele badge = geen balk. De
// hover draagt de betekenis plus de datum, want de badge zelf draagt geen tekst.
//
// Rule-23: dit is bewust GEEN Chip/TonePill maar een eigen primitive - vierkante
// icoontegels zonder tekst. Daarom staat hij hier in lib.js, het enige bestand dat
// het chip-contract mag uitbreiden (lib/design-chips.test.mjs zondert lib.js uit).
//
// Het bedrijfslogo komt als NODE binnen (`logo`), niet als slug: CompanyAvatar
// woont in detail/parts.js en dat bestand importeert dit bestand - zelf importeren
// zou een cirkel geven. De adapter levert dus <CompanyAvatar size={14} bare/>.
const RB_BOX = {
  width: 20, height: 20, borderRadius: 5, boxSizing: 'border-box', flex: 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  position: 'relative', cursor: 'default',
};
// Twee randkleuren die ALLEEN in deze balk bestaan: de gesourced- en de
// interview-tint (HV2.srcTint/HV2.intTint) hebben geen eigen randtoken, en de
// gewone HV2.border is er te koud voor. Bewust badge-only, niet naar HV2 promoten
// voordat een tweede plek ze nodig heeft.
const RB_SRC_BD = '#d8c9c2';
const RB_INT_BD = '#c4cade';

// De hover op een badge is een EIGEN instant-popover (zelfde placeTooltip +
// body-portal als InfoHint), geen native title=: die verschijnt pas na ~1s
// browserdelay en in OS-stijl. Eén compacte witte regel, direct bij
// mouseenter/focus. Genest gebruik (de persoonsschijf IN de logobadge) loopt
// via context: zolang de binnenste open is zwijgt de buitenste, zoals een
// geneste title= dat ook doet.
//
// Rail-vorm (eigenaarsverzoek 3 okt 2026, DESIGN-SYSTEM regel 42): de
// pictogramrail draagt zijn labels hiermee in plaats van een native title=.
//   side 'right'   het vlak staat rechts van de trigger, verticaal gecentreerd
//                  op de rij (standaard 'bottom': eronder, zoals de badges).
//   focusable      false als het kind zelf de focus en het aria-label draagt
//                  (een link of knop): de wrapper krijgt dan geen tabstop en
//                  geen eigen naam, toont op focus van het kind, en verdwijnt
//                  bij een klik zonder op de klikfocus terug te komen.
//   badge          een telling naast het label (TonePill of Badge).
//   disabled       geeft de kinderen ongewijzigd terug (de brede rail).
const InstantTipNest = React.createContext(null);

export function InstantTip({ label, style, children, side = 'bottom', focusable = true, badge = null, disabled = false, ...rest }) {
  const parent = useContext(InstantTipNest);
  const [open, setOpen] = useState(false);
  const [childOpen, setChildOpen] = useState(false);
  const anchorRef = useRef(null);
  const tipRef = useRef(null);
  const pointerRef = useRef(false); // muisknop omlaag: de focus die volgt is een klik
  const [pos, setPos] = useState(null);
  const place = useCallback(() => {
    const el = anchorRef.current; if (!el) return;
    const tip = tipRef.current;
    const r = el.getBoundingClientRect();
    const w = tip ? tip.offsetWidth : 0;
    const h = tip ? tip.offsetHeight : 0;
    const p = placeTooltip(r, w, h, 'left', side);
    if (side === 'right' && h) {
      const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
      p.top = Math.max(8, Math.min(r.top + (r.height - h) / 2, vh - 8 - h));
    }
    setPos(p);
  }, [side]);
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);
  if (disabled) return children;
  // Rechts gecentreerd hangt af van de gemeten hoogte: het vlak blijft een frame
  // onzichtbaar tot het effect hierboven het gemeten heeft, zodat het niet springt.
  const show = () => { if (side === 'right' && !open) setPos(null); else place(); setOpen(true); if (parent) parent(true); };
  const hide = () => { setOpen(false); if (parent) parent(false); };
  const own = focusable
    ? { tabIndex: 0, 'aria-label': label || undefined, onFocus: show, onBlur: hide }
    : {
      onFocus: () => { if (!pointerRef.current) show(); },
      onBlur: hide,
      onMouseDownCapture: () => { pointerRef.current = true; hide(); },
      onMouseUpCapture: () => { pointerRef.current = false; },
      onKeyDownCapture: () => { pointerRef.current = false; },
    };
  return (
    <span ref={anchorRef} style={{ outline: 'none', ...style }}
      onMouseEnter={show} onMouseLeave={hide} {...own} {...rest}>
      <InstantTipNest.Provider value={setChildOpen}>{children}</InstantTipNest.Provider>
      {open && !childOpen && label && typeof document !== 'undefined' && createPortal(
        <span ref={tipRef} role="tooltip" style={{ position: 'fixed', top: pos ? pos.top : 0, left: pos ? pos.left : 0, visibility: pos ? 'visible' : 'hidden', zIndex: 9700, pointerEvents: 'none', ...SURFACE.tooltip, padding: '5px 9px', fontSize: HV2.fsText, fontWeight: 400, lineHeight: 1.35, color: HV2.ink2, whiteSpace: 'nowrap', maxWidth: 'calc(100vw - 16px)', ...(badge ? { display: 'inline-flex', alignItems: 'center', gap: 6 } : null) }}>
          {label}{badge}
        </span>, document.body)}
    </span>
  );
}

function RecordBadge({ title, background = HV2.bg, borderColor = HV2.border, onClick, children }) {
  // onClick is alleen gezet voor de markt-badges (klik naar de Markten-pagina);
  // de RB_BOX-token zelf blijft ongewijzigd voor alle andere badges.
  const interactive = onClick ? {
    role: 'link',
    onClick,
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } },
  } : null;
  return (
    <InstantTip label={title} style={{ ...RB_BOX, background, border: `1px solid ${borderColor}`, ...(onClick ? { cursor: 'pointer' } : null) }} {...interactive}>
      {children}
    </InstantTip>
  );
}

// PersonDisk: de persoon-/DMU-schijf rechtsonder op een bedrijfslogo als het
// record een PERSOON bij dat bedrijf is. Draagt zijn eigen hover.
export function PersonDisk({ person }) {
  if (!person) return null;
  return (
    <InstantTip label={person === 'dmu' ? t('DMU, beslisser bij dit bedrijf') : t('Contactpersoon bij dit bedrijf')}
      style={{ position: 'absolute', right: -4, bottom: -4, width: 13, height: 13, borderRadius: '50%', boxSizing: 'border-box', background: HV2.bg, border: `1px solid ${HV2.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {person === 'dmu'
        ? <MenuIcon name="crown" size={8} strokeWidth={2.6} color={HV2.ink3} />
        : <EntityIcon entity="candidate" size={8} strokeWidth={2.6} color={HV2.ink3} />}
    </InstantTip>
  );
}

// SignerDisk: dezelfde schijf als PersonDisk, maar inline naast de naam of de
// DMU-aanduiding, voor wie de raamovereenkomst heeft ondertekend (is_signer,
// lib/crm/contract-signer.mjs). Zonder ondertekenaar niets.
export function SignerDisk({ show, style }) {
  if (!show) return null;
  return (
    <InstantTip label={t('Deze persoon heeft de raamovereenkomst ondertekend.')}
      style={{ width: 13, height: 13, borderRadius: '50%', boxSizing: 'border-box', background: HV2.bg, border: `1px solid ${HV2.border}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', verticalAlign: 'middle', ...style }}>
      <MenuIcon name="fileLines" size={8} strokeWidth={2.6} color={HV2.ink3} />
    </InstantTip>
  );
}

// CompanyMark: bedrijfslogo LINKS VAN de bedrijfsnaam (eigenaarsbesluit 13 sep
// 2026), niet meer als badge in de rail naast markt en industrie. Kaal logo op
// tekstmaat, met de persoonsschijf erop voor een contact.
export function CompanyMark({ logo, person = null, size = 16, title }) {
  if (!logo) return null;
  return (
    <span title={title || undefined} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, flex: 'none', verticalAlign: 'middle' }}>
      {logo}
      <PersonDisk person={person} />
    </span>
  );
}

export function RecordBadges({ data }) {
  if (!data) return null;
  const { markets = [], marketsInherited = false, industries = [], milestones = null, privacyAcceptedAt = null } = data;
  const badges = [];

  // 3..n: de markt(en). De glyph komt kaal binnen (marketGlyph), want deze badge
  // tekent het witte vlak en de hairline al zelf. Klikbaar naar de Markten-pagina
  // (gotoMarkten), net als MarketIconLink elders in de app - de titel volgt
  // bewust dezelfde formulering. marketsInherited: de markten zijn die van het
  // bedrijf (een contactpersoon zonder eigen markten), en de hover zegt dat.
  for (const m of (markets || []).filter(Boolean)) {
    badges.push(
      <RecordBadge key={`markt-${m}`} title={marketsInherited ? t('{markt} op de Markten-pagina, geërfd van het bedrijf', { markt: m }) : `${m} op de Markten-pagina`} onClick={(e) => gotoMarkten(m, e)}>
        <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(marketGlyph(m, { size: 14 }))}`}
          alt={m} width={14} height={14} style={{ display: 'block' }} />
      </RecordBadge>,
    );
  }

  // Industrieën (bedrijf: eigen lijst, vacature: die van de klant), zelfde
  // badge als een markt maar zonder klikdoel: het industrieglyph draagt de naam.
  for (const name of (industries || []).filter(Boolean)) {
    badges.push(
      <RecordBadge key={`ind-${name}`} title={name}>
        <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(industryGlyph(name, { size: 14 }))}`}
          alt={name} width={14} height={14} style={{ display: 'block' }} />
      </RecordBadge>,
    );
  }

  const ms = milestones || {};
  if (ms.sourced) {
    const datum = formatDateVoluit(ms.sourcedDate);
    badges.push(
      <RecordBadge key="src" background={HV2.srcTint} borderColor={RB_SRC_BD}
        title={datum ? `${t('CV Send gedaan')}, ${datum}` : t('CV Send gedaan')}>
        <MenuIcon name="search" size={11} strokeWidth={2.2} color={HV2.accentSrc} />
      </RecordBadge>,
    );
  }
  if (ms.interviewCount > 0) {
    const datum = formatDateVoluit(ms.lastInterviewDate);
    const n = ms.interviewCount;
    // Bij één ronde geen telling tussen haakjes: 'Interview gevoerd, 29 juli 2026'.
    const base = n > 1 ? t('Interview gevoerd ({n})', { n }) : t('Interview gevoerd');
    const title = !datum ? base : (n > 1 ? `${base}, ${t('laatste {datum}', { datum })}` : `${base}, ${datum}`);
    badges.push(
      <RecordBadge key="iv" background={HV2.intTint} borderColor={RB_INT_BD} title={title}>
        <MenuIcon name="calCheck" size={11} strokeWidth={2.1} color={HV2.accentInt} />
      </RecordBadge>,
    );
  }
  if (ms.placed) {
    const maand = formatMaandJaar(ms.placedDate);
    const base = ms.placedClient ? t('Geplaatst bij {klant}', { klant: ms.placedClient }) : t('Geplaatst');
    badges.push(
      <RecordBadge key="placed" background={HV2.greenBg} borderColor={HV2.greenBd}
        title={maand ? `${base}, ${maand}` : base}>
        <MenuIcon name="star" size={11} strokeWidth={2} color={HV2.greenText} />
      </RecordBadge>,
    );
  }
  // Administratie: ALLEEN de geaccepteerde privacyverklaring krijgt een lintje.
  // Verstuurd, afgeleverd of afgewezen is geen mijlpaal - die staan in de metastrook.
  if (privacyAcceptedAt) {
    const datum = formatDateVoluit(privacyAcceptedAt);
    badges.push(
      <RecordBadge key="priv" background={HV2.greenBg} borderColor={HV2.greenBd}
        title={datum ? `${t('Privacyverklaring geaccepteerd')}, ${datum}` : t('Privacyverklaring geaccepteerd')}>
        <MenuIcon name="check" size={11} strokeWidth={2.6} color={HV2.greenText} />
      </RecordBadge>,
    );
  }

  if (!badges.length) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6, flex: 'none' }}>
      {badges}
    </span>
  );
}

// PartnerMark: the icon-only form of PartnerPill - the white P& mark on a small
// kobalt square. For tight spots (search results, dense lists) where the full
// "Partner" pill won't fit.
export function PartnerMark({ size = 18, style }) {
  return (
    <span title="Partner (klant met retainer)" role="img" aria-label="Partner" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 4, background: '#0047ab', flexShrink: 0, ...style }}>
      <span style={{ display: 'inline-block', width: pmarkWidth(Math.round(size * 0.4)), height: Math.round(size * 0.4), backgroundColor: '#fff', WebkitMask: 'url(/logo-pmark-white.png) center/contain no-repeat', mask: 'url(/logo-pmark-white.png) center/contain no-repeat' }} />
    </span>
  );
}

// AppMark: Pearson App als AFZENDER. Wit vlak, zwarte ronde rand, het zwarte
// P&-merk erin. Staat in de afzenderkolom van het postvak op de plek waar een
// collega zijn UserAvatar heeft, zodat 'de app schreef dit' even snel leest als
// een gezicht. Bewust NIET de kobalt tegel van PartnerMark: die betekent partner
// (retained klant) en een tweede betekenis op hetzelfde beeld zou allebei de
// vlaggen slopen (eigenaarsbesluit 28 aug 2026).
export function AppMark({ size = 22, style }) {
  const h = Math.round(size * 0.5);
  return (
    <span role="img" aria-label="Pearson App" title="Pearson App" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: Math.max(4, Math.round(size / 3.6)), background: HV2.bg, border: `1px solid ${HV2.ink}`, flexShrink: 0, ...style }}>
      <span style={{ display: 'inline-block', width: pmarkWidth(h), height: h, backgroundColor: HV2.ink, WebkitMask: 'url(/logo-pmark-white.png) center/contain no-repeat', mask: 'url(/logo-pmark-white.png) center/contain no-repeat' }} />
    </span>
  );
}

// AgentMark: de Research Agent als ACTOR. Zelfde tegel als AppMark (wit vlak,
// zwarte ronde rand) zodat een niet-menselijke deelnemer overal hetzelfde leest,
// met het research-beeldmerk erin in plaats van het P&-merk: de agent is niet de
// app die iets meldt, hij is een testpersona die zelf door de app loopt. Geen
// foto, geen initialencirkel, want dan zou hij zich als collega voordoen.
export function AgentMark({ size = 22, style }) {
  return (
    <span role="img" aria-label="Research Agent" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: Math.max(4, Math.round(size / 3.6)), background: HV2.bg, border: `1px solid ${HV2.ink}`, color: HV2.ink, flexShrink: 0, ...style }}>
      <MatchIcon size={Math.max(10, Math.round(size * 0.62))} />
    </span>
  );
}

// OwnerAvatar: the CRM owner column rendered as JUST the avatar (no name), to
// reclaim the width the avatar+name pair used to eat. Sinds 3 september 2026
// (eigenaarsbesluit) draagt hij DEZELFDE hoverkaart als elke andere avatar
// (AvatarHoverCard: foto, naam, functietitel, markttegels) in plaats van een
// eigen kaartje met alleen naam en markt, zodat een eigenaar in een raster en
// een collega in een lijst identiek lezen. `user` must be the resolved user
// record (via ownerUser(users, item)) so it carries name + avatar.
export function OwnerAvatar({ user, size = 20, interactive = true }) {
  if (!user) return null;
  return <UserAvatar user={user} size={size} interactive={interactive} />;
}

// PhaseBar: checklist progress - mini bar + N/M. Green when complete.
export function PhaseBar({ label, done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
      {label && <span style={{ fontSize: HV2.fsText, color: HV2.muted, whiteSpace: 'nowrap' }}>{label}</span>}
      <span style={{ width: 46, height: 4, borderRadius: 2, background: HV2.divider, overflow: 'hidden', display: 'inline-block' }}>
        <span style={{ display: 'block', height: '100%', width: pct + '%', borderRadius: 2, background: done === total ? HV2.green : HV2.ink2 }} />
      </span>
      <span style={{ fontSize: HV2.fsText, fontWeight: 560, color: HV2.ink2 }}>{done}/{total}</span>
    </span>
  );
}

// KansChip: auto-calculated %. Colour follows value. Pair with calcKans(deal).
export function KansChip({ kans }) {
  const c = kans >= 66 ? HV2.green : kans >= 33 ? HV2.muted : HV2.red;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 28, height: 4, borderRadius: 2, background: HV2.divider, overflow: 'hidden', display: 'inline-block' }}>
        <span style={{ display: 'block', height: '100%', width: kans + '%', background: c, borderRadius: 2 }} />
      </span>
      <span style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.ink }}>{kans}%</span>
    </span>
  );
}

// SitChip: situational date filter with count. Active = bolder ink.
export function SitChip({ active, label, n, alert, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 2, whiteSpace: 'nowrap' }}>
      {alert && n > 0 && <Dot color={HV2.red} size={6} />}
      <span style={{ fontSize: HV2.fsText, fontWeight: active ? 650 : 480, color: active ? HV2.ink : HV2.muted, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontSize: HV2.fsText, fontWeight: 560, color: active ? HV2.ink3 : HV2.mute2, whiteSpace: 'nowrap' }}>({n})</span>
    </button>
  );
}

// ── Unsaved-draft guard ──────────────────────────────────────────────────────
// Detail windows close on Escape / scrim click / outside-click. A component
// holding a typed-but-unsaved draft (activity log, process note, geplande
// opvolging) registers here so every close path can confirm first instead of
// silently destroying the text. Same contract as Modal.js confirmDirty, but
// usable across the adapter boundary (the shells are layout-only and can't
// see the drafts).
//
// reloadOnly: telt alleen mee voor het automatisch herladen bij een nieuwe versie
// (VersionWatcher), niet voor de sluitvragen hieronder. Een modal heeft zijn eigen
// sluitvraag (confirmDirty) en een mislukte kwalificatie-opslag houdt zijn toast;
// een tweede window.confirm daarbovenop zou dubbel vragen (6 okt 2026, S4).
let _dirtyDrafts = 0;
let _reloadOnlyDrafts = 0;
export function useDraftGuard(dirty, { reloadOnly = false } = {}) {
  React.useEffect(() => {
    if (!dirty) return undefined;
    if (reloadOnly) { _reloadOnlyDrafts += 1; return () => { _reloadOnlyDrafts -= 1; }; }
    _dirtyDrafts += 1;
    return () => { _dirtyDrafts -= 1; };
  }, [dirty, reloadOnly]);
}
export function anyDraftDirty({ includeReloadOnly = false } = {}) { return _dirtyDrafts > 0 || (includeReloadOnly && _reloadOnlyDrafts > 0); }
// Tab sluiten of herladen met een open concept of een nog lopende autosave: de
// browser vraagt eerst (zijn eigen tekst, die kiezen we niet). Leest hetzelfde
// register als VersionWatcher, inclusief reloadOnly: een modal met invoer, een
// mislukte of nog geplande kwalificatie-opslag. Een automatische reload van
// VersionWatcher vraagt nooit, die herlaadt alleen als dit register leeg is.
// Eenmaal geinstalleerd vanuit app/page.js; geeft de opruimfunctie terug (S25).
export function draftUnloadHandler(event) {
  if (!anyDraftDirty({ includeReloadOnly: true })) return undefined;
  event.preventDefault();
  event.returnValue = '';
  return '';
}
export function installDraftUnloadGuard() {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('beforeunload', draftUnloadHandler);
  return () => window.removeEventListener('beforeunload', draftUnloadHandler);
}
// Een antwoord geldt even voor de hele klik: terug, kruimel en sluiten vragen
// zelf en zetten daarna proces of record om, en een klik uit de bellog sluit
// eerst het proces en opent dan het record. Dat vraagt EEN keer, en na 'Annuleren'
// weigert de rest van dezelfde klik stil.
let _draftDecision = { at: 0, ok: true };
function askDrafts(msg) {
  if (!anyDraftDirty()) return true;
  if (Date.now() - _draftDecision.at < 1000) return _draftDecision.ok;
  const ok = window.confirm(t(msg));
  _draftDecision = { at: Date.now(), ok };
  return ok;
}
export function confirmCloseWithDrafts() {
  return askDrafts('Er staat niet-opgeslagen tekst in dit venster. Toch sluiten?');
}
// Een ander record of proces openen terwijl er een concept staat (bellog,
// zoekbalk, Vandaag): eerst vragen, anders verhuist of verdwijnt de tekst.
export function confirmSwitchWithDrafts() {
  return askDrafts('Er staat niet-opgeslagen tekst in dit dossier. Toch een ander dossier openen?');
}

// ── Midnight-safe "vandaag" ──────────────────────────────────────────────────
// Een `new Date()` losjes in de render-body gebruikt in een useMemo-dependency
// werkt niet: die deps-array wijzigt pas als React opnieuw rendert om een ANDERE
// reden, dus een tab die 's nachts openstaat blijft rekenen met gisteren.
// useToday() geeft een Date terug die pas een NIEUWE identity krijgt zodra de
// kalenderdag omslaat (toDateString-vergelijking) - een minuut-interval plus
// een visibilitychange-check merken middernacht, maar de state-update zelf
// wordt overgeslagen zolang de dag nog hetzelfde is, anders thrasht dit 1440x
// per dag i.p.v. eenmaal. Gebruik `useToday()` in plaats van `new Date()` overal
// waar dat resultaat in een useMemo-deps-array moet staan (7 aug 2026).
export function useToday() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const check = () => {
      setNow((prev) => {
        const fresh = new Date();
        return fresh.toDateString() === prev.toDateString() ? prev : fresh;
      });
    };
    const iv = setInterval(check, 60000);
    document.addEventListener('visibilitychange', check);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', check); };
  }, []);
  return now;
}

// ── Entity icons (one source of truth) ───────────────────────────────────────
// Used by the CRM tabs, the left rail CRM submenu, record drawers, the command
// palette and anywhere an entity is named, so a candidate/client/prospect/job/
// contact/bellijst/proces always shows the same glyph.
const _entStroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
// Gevulde silhouetten (eigenaarsbesluit 28 aug 2026). De lijnversies vielen bij
// 13px uit elkaar en drie procesglyphs waren identiek; nu draagt elk type een
// eigen dichte vorm en zit het detail als uitsparing IN de vorm (fill-rule
// evenodd), niet als tweede lijn ernaast. Eén pad per glyph, zodat kleur alleen
// via `fill` loopt en een aanroeper nooit per ongeluk een lijnversie krijgt.
// Uitzondering: elk proces gebruikt het lijn-takglyph (BRANCH_PATHS), zowel
// de navigatie als alle procestracks (eigenaarsbesluit 4 oktober 2026).
const ENT_FILL = (d) => <path fillRule="evenodd" d={d} />;
const ENT_PATHS = {
  candidate: ENT_FILL('M12 3.2a4.2 4.2 0 1 0 0 8.4a4.2 4.2 0 1 0 0-8.4Z M3.6 21c0-4.5 3.7-7.2 8.4-7.2s8.4 2.7 8.4 7.2Z'),
  // Spreekballon, GEEN persoon: een contactpersoon is degene met wie je spreekt,
  // en een tweede mensfiguur was op 13px niet van de kandidaat te onderscheiden.
  contact:   ENT_FILL('M3.4 2.8h17.2a2 2 0 0 1 2 2v10.6a2 2 0 0 1-2 2h-9.4l-5.6 4.4v-4.4H3.4a2 2 0 0 1-2-2V4.8a2 2 0 0 1 2-2Z M6.6 8.8h2.6v2.6H6.6Z M10.7 8.8h2.6v2.6h-2.6Z M14.8 8.8h2.6v2.6h-2.6Z'),
  company:   ENT_FILL('M12 2.2 21.4 8.2V21.6H2.6V8.2Z M9.9 21.6v-5.6h4.2v5.6Z M6.3 10.4h3.2v3.2H6.3Z M14.5 10.4h3.2v3.2h-3.2Z'),
  prospect:  ENT_FILL('M12 2.4a9.6 9.6 0 1 0 0 19.2a9.6 9.6 0 1 0 0-19.2Z M12 6.4a5.6 5.6 0 1 0 0 11.2a5.6 5.6 0 1 0 0-11.2Z M12 9.6a2.4 2.4 0 1 0 0 4.8a2.4 2.4 0 1 0 0-4.8Z'),
  job:       ENT_FILL('M9.4 2.4h5.2A2.7 2.7 0 0 1 17.3 5.1v2.3h-2.6V5.4a.6.6 0 0 0-.6-.6H9.9a.6.6 0 0 0-.6.6v2H6.7V5.1A2.7 2.7 0 0 1 9.4 2.4Z M2.4 9.4h19.2v10a2.5 2.5 0 0 1-2.5 2.5H4.9a2.5 2.5 0 0 1-2.5-2.5Z M10.3 12.8h3.4v2.8h-3.4Z'),
  // Bellijst: een lijst met een afspeelpijl - je werkt hem van boven naar beneden
  // af. Was een pen, en die zei bewerken in plaats van bellen.
  callList:  ENT_FILL('M2.4 3.6h12.4v3H2.4Z M2.4 9.4h12.4v3H2.4Z M2.4 15.2h8.4v3H2.4Z M16.8 12.6 23 16.8l-6.2 4.2Z'),
  // Call log: gevulde handset, zelfde gewicht als de andere entiteitsglyphs.
  callLog:   ENT_FILL('M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z'),
  // Interviewverzoek (CRM-tab 'Interviewverzoeken'): agenda met vinkje - een
  // klant deelde een moment via de publieke CV-link.
  interviewRequest: ENT_FILL('M6.6 2h2.8v3.4H6.6Z M14.6 2h2.8v3.4h-2.8Z M4 4.4h16a1.9 1.9 0 0 1 1.9 1.9v13.8A1.9 1.9 0 0 1 20 22H4a1.9 1.9 0 0 1-1.9-1.9V6.3A1.9 1.9 0 0 1 4 4.4Z M10.9 18 6.9 14l1.9-1.9 2.1 2.1 4.3-4.3 1.9 1.9Z'),
  // CV Send (eigenaarsbesluit 5 okt 2026): de gevulde versie van het document
  // met pijl uit lib/kpi-icons.mjs (CV_SEND_PATHS). Document met omgeslagen hoek
  // als silhouet, rechtsonder uitgespaard voor de pijl, zodat vorm en pijl op 13px
  // los van elkaar blijven. Als proces is het process.
  cvSend:    ENT_FILL('M4.4 1.8H12.2V6.4a.8.8 0 0 0 .8.8h4.4V11H11.4v11.2H4.4a1.8 1.8 0 0 1-1.8-1.8V3.6a1.8 1.8 0 0 1 1.8-1.8Z M13.8 1.8l3.6 3.8h-3.6Z M12.8 15.2h4.4v-2.8l4.8 4-4.8 4v-2.8h-4.4Z'),
  applicant: ENT_FILL('M8.5 1.6h7a1.2 1.2 0 0 1 1.2 1.2v1.6H7.3V2.8a1.2 1.2 0 0 1 1.2-1.2Z M4.2 4h15.6a1.6 1.6 0 0 1 1.6 1.6v15.8a1.6 1.6 0 0 1-1.6 1.6H4.2a1.6 1.6 0 0 1-1.6-1.6V5.6a1.6 1.6 0 0 1 1.6-1.6Z M12 7.4a2.6 2.6 0 1 0 0 5.2a2.6 2.6 0 1 0 0-5.2Z M7 18.8c0-2.2 2.2-3.6 5-3.6s5 1.4 5 3.6Z'),
  process:   <>{BRANCH_PATHS.map((d, i) => <path key={i} d={d} />)}</>,
  // Kwalificatie (eigenaarskeuze 15 sep 2026): een dossiermap met tab. De tab
  // breekt de bovenrand, en dat doet geen enkel ander glyph in deze tabel, dus de
  // silhouet blijft op 13px herkenbaar in een dichte lijstrij. Het type staat als
  // uitsparing in de map: de kandidaatfiguur of de koffer, precies de vormen die
  // 'candidate' en 'job' hierboven al dragen, zodat de familie klopt. Tot deze
  // datum waren dit twee lijnglyphs in Kwalificaties.js (een persoon met vinkje,
  // een koffer met plusje): beide lazen als 'goedgekeurd' en 'nieuw', geen van
  // beide als kwalificatie, en lijn hoort niet bij een recordtype (designregel 7).
  qualCandidate: ENT_FILL('M3.4 3.6h5.6l2 2.4h9.6a1.8 1.8 0 0 1 1.8 1.8v11.8a1.8 1.8 0 0 1-1.8 1.8H3.4a1.8 1.8 0 0 1-1.8-1.8V5.4a1.8 1.8 0 0 1 1.8-1.8Z M12 9.6a2.1 2.1 0 1 0 0 4.2a2.1 2.1 0 1 0 0-4.2Z M8.4 18c0-2 1.6-3.1 3.6-3.1s3.6 1.1 3.6 3.1Z'),
  qualJob:       ENT_FILL('M3.4 3.6h5.6l2 2.4h9.6a1.8 1.8 0 0 1 1.8 1.8v11.8a1.8 1.8 0 0 1-1.8 1.8H3.4a1.8 1.8 0 0 1-1.8-1.8V5.4a1.8 1.8 0 0 1 1.8-1.8Z M10.4 8.8h3.2a1.1 1.1 0 0 1 1.1 1.1v.9h-1.7v-.5h-2v.5h-1.7v-.9a1.1 1.1 0 0 1 1.1-1.1Z M7.8 11.4h8.4v5.8H7.8Z'),
  // Sjabloon (U3, 12 okt 2026): de drie delen van MenuIcon fileLines gevuld, het
  // blad als silhouet, de omgeslagen hoek en de twee tekstregels als uitsparing.
  template:  ENT_FILL('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z M14 2v6h6Z M8 12.2h5v1.6H8Z M8 16.2h7v1.6H8Z'),
  // De stand Mijn en Team van de teambalk (13 okt 2026): een hele gestalte, hoofd,
  // romp en twee benen, en drie gestalten naast elkaar (de middelste groter). De
  // figuren raken elkaar niet, anders snijdt evenodd een gat waar ze overlappen.
  personSolo: ENT_FILL('M12 1.6a2.6 2.6 0 1 0 0 5.2a2.6 2.6 0 1 0 0-5.2Z M8.6 8h6.8a2 2 0 0 1 2 2v5.4h-2.2V22h-2.4v-6.2h-1.6V22H8.8v-6.6H6.6V10a2 2 0 0 1 2-2Z'),
  personTeam: ENT_FILL('M12 1.2a2.3 2.3 0 1 0 0 4.6a2.3 2.3 0 1 0 0-4.6Z M10.4 6.6h3.2a1.6 1.6 0 0 1 1.6 1.6V14h-1.1v8h-1.7v-6.6h-.8V22H9.9v-8H8.8V8.2a1.6 1.6 0 0 1 1.6-1.6Z M4.3 4.4a2 2 0 1 0 0 4a2 2 0 1 0 0-4Z M2.8 9.2h3a1.4 1.4 0 0 1 1.4 1.4v4.8h-1V22H4.7v-6h-.8v6H2.4v-6.6h-1v-4.8a1.4 1.4 0 0 1 1.4-1.4Z M19.7 4.4a2 2 0 1 0 0 4a2 2 0 1 0 0-4Z M18.2 9.2h3a1.4 1.4 0 0 1 1.4 1.4v4.8h-1V22h-1.5v-6h-.8v6h-1.5v-6.6h-1v-4.8a1.4 1.4 0 0 1 1.4-1.4Z'),
};
const ENT_STROKE_KEYS = new Set(['process']);
const ENT_ALIAS = { client: 'company', prospect: 'prospect', kandidaat: 'candidate', bedrijf: 'company', bellijst: 'callList', hotlist: 'callList', proces: 'process', interviewProces: 'process', bdProces: 'process', placement: 'process', bd: 'process', sourcing: 'process', sollicitant: 'applicant', kwalificatie: 'qualCandidate', kandidaatKwalificatie: 'qualCandidate', jobKwalificatie: 'qualJob' };

// ── Unified person identity icon ────────────────────────────────────────────
// Every person in the app (a candidate, or a contact at a client/prospect)
// renders the SAME base person glyph plus a small secondary badge to its right
// that says which kind they are. Color carries the type so it reads at a glance
// and stays identical everywhere (CRM, Call Logs, inbox, director mode):
//   candidate → cobalt blue client → green contracting → pink prospect → yellow-orange
//   unknown (no CRM match) → purple, as on the Call Logs page.
export const PERSON_COLORS = { candidate: '#0047AB', client: '#15803d', prospect: '#d9890a', contracting: '#d63384', unknown: '#6d28d9' };
// Gevuld, net als ENT_PATHS: de persoonsschijf en de typebadge staan altijd naast
// een entiteitsglyph, dus ze moeten hetzelfde gewicht hebben.
const PERSON_BASE = <path fillRule="evenodd" d="M12 3.4a3.6 3.6 0 1 0 0 7.2a3.6 3.6 0 1 0 0-7.2Z M5 20.6c0-3.9 3.1-6.2 7-6.2s7 2.3 7 6.2Z" />;
// Secondary type badges. Candidate is a NEW glyph (an ID/CV card) deliberately
// distinct from the plain person; client/prospect reuse the CRM-header icons.
const PERSON_SECONDARY = {
  candidate: <path fillRule="evenodd" d="M4.6 3.2h14.8a2 2 0 0 1 2 2v13.6a2 2 0 0 1-2 2H4.6a2 2 0 0 1-2-2V5.2a2 2 0 0 1 2-2Z M12 7.4a2.4 2.4 0 1 0 0 4.8a2.4 2.4 0 1 0 0-4.8Z M7.8 17.4c0-2.3 1.9-3.7 4.2-3.7s4.2 1.4 4.2 3.7Z" />,
  client:    <path fillRule="evenodd" d="M12 2.2 21.4 8.2V21.6H2.6V8.2Z M9.9 21.6v-5.6h4.2v5.6Z M6.3 10.4h3.2v3.2H6.3Z M14.5 10.4h3.2v3.2h-3.2Z" />,
  prospect:  <path fillRule="evenodd" d="M12 2.4a9.6 9.6 0 1 0 0 19.2a9.6 9.6 0 1 0 0-19.2Z M12 6.4a5.6 5.6 0 1 0 0 11.2a5.6 5.6 0 1 0 0-11.2Z M12 9.6a2.4 2.4 0 1 0 0 4.8a2.4 2.4 0 1 0 0-4.8Z" />,
  // Contracting deelt het bedrijfsglyph van client: het is dezelfde soort
  // relatie, de petrolkleur zegt welk soort.
  contracting: <path fillRule="evenodd" d="M12 2.2 21.4 8.2V21.6H2.6V8.2Z M9.9 21.6v-5.6h4.2v5.6Z M6.3 10.4h3.2v3.2H6.3Z M14.5 10.4h3.2v3.2h-3.2Z" />,
  unknown:   null,
};
// Resolve a CRM match {entity, client_type} → person type key.
export function personType(match) {
  if (!match) return 'unknown';
  if (match.entity === 'candidate') return 'candidate';
  if (match.client_type === 'contracting') return 'contracting';
  if (match.client_type === 'client') return 'client';
  // Prospect contracting valt hier bewust op 'prospect': niets getekend, dus de
  // prospectkleur en niet het roze van een getekende contracting klant
  // (eigenaarsregel 11 sep 2026).
  return 'prospect'; // contacts default to prospect unless their company is a client
}
export function PersonIcon({ type = 'unknown', size = 16, title }) {
  const t = PERSON_COLORS[type] ? type : 'unknown';
  const color = PERSON_COLORS[t];
  const sec = PERSON_SECONDARY[t];
  const ss = { fill: color, stroke: 'none' };
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 24 24" {...ss}>{PERSON_BASE}</svg>
      {sec && <svg width={Math.round(size * 0.72)} height={Math.round(size * 0.72)} viewBox="0 0 24 24" {...ss}>{sec}</svg>}
    </span>
  );
}
// strokeWidth telt alleen nog voor het takglyph (`process`); de gevulde glyphs
// negeren hem. De prop blijft in de signatuur staan omdat ruim honderd aanroepen
// hem meegeven - hem weghalen zou een sweep door dertig bestanden zijn zonder
// dat er iets aan het beeld verandert.
export function EntityIcon({ entity, size = 13, color = 'currentColor', strokeWidth = 2, style }) {
  const key = ENT_ALIAS[entity] || entity;
  const paths = ENT_PATHS[key] || ENT_PATHS.candidate;
  const box = { width: size, height: size, viewBox: '0 0 24 24', style: { flexShrink: 0, display: 'block', ...style } };
  if (ENT_STROKE_KEYS.has(key)) return <svg {...box} {..._entStroke} stroke={color} strokeWidth={strokeWidth}>{paths}</svg>;
  return <svg {...box} fill={color} stroke="none">{paths}</svg>;
}

// ── CV-soorten (13 okt 2026) ────────────────────────────────────────────────
// Drie CV's per kandidaat, elk met een eigen kleur en een eigen gevuld glyph:
// het P& CV met naam (voor klanten), het anonieme P& CV (voor prospects) en het
// originele bestand uit het CRM. De kleur draagt het glyph, de streep onder de
// actieve schakelaarknop en de bovenrand van de CV-pagina (CvPage.js); het label
// blijft inkt. `tint` is de vulling van de actieve schakelaarknop en van
// 'Bewerken' zodra die aan staat.
//
// Kleuren (eigenaarsbesluit 13 okt 2026, palet kobalt, oranje, inkt): de kleur
// draagt het glyph, de schakelaarmarkering en het accent van de CV-pagina; hover
// is de donkere variant voor de schakelaar. Inhoud, geen status.
// label en sub zijn de bron voor de drie knoppen in de recordkop en het CV-veld
// en de schakelaar op de CV-pagina (door t() heen).
export const CV_KIND = {
  // Getters: HV2 wisselt mee met het thema, een kopie bij het laden niet.
  named: { get color() { return HV2.blue; }, hover: '#24417f', get tint() { return HV2.blueBg; }, label: 'P& CV', sub: 'met naam, voor klanten' },
  anon: { get color() { return HV2.orange; }, hover: '#8c4618', get tint() { return HV2.orangeBg; }, label: 'P& CV anon', sub: 'zonder naam, voor prospects' },
  original: { get color() { return HV2.muted; }, hover: '#000000', get tint() { return HV2.subtle; }, label: 'CV original', sub: 'bestand uit het CRM' },
};
export const CV_KINDS = ['named', 'anon', 'original'];
// Zelfde bouw als ENT_PATHS: een blad met omgeslagen hoek als silhouet (de hoek
// als uitsparing via evenodd) en het type als uitsparing in het blad.
const CV_FOLD = 'M14 2v6h6Z';
const CV_DOC = `M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z ${CV_FOLD}`;
const CV_KIND_PATHS = {
  // Persoon: hoofd en schouders, dezelfde vorm als de kandidaatglyph.
  named: ENT_FILL(`${CV_DOC} M12 8.6a2.4 2.4 0 1 0 0 4.8a2.4 2.4 0 1 0 0-4.8Z M7.6 18.8c0-2.4 2-3.8 4.4-3.8s4.4 1.4 4.4 3.8Z`),
  // Masker: de band is uitgespaard, de ogen binnen de band zijn weer gevuld.
  anon: ENT_FILL(`${CV_DOC} M6.4 12.2c0-.9.7-1.4 1.6-1.4h8c.9 0 1.6.5 1.6 1.4v1.4c0 1.6-1.2 2.6-2.6 2.6c-1.3 0-2-.8-2.6-1.8c-.2-.3-.5-.3-.8 0c-.6 1-1.3 1.8-2.6 1.8c-1.4 0-2.6-1-2.6-2.6Z M7.9 13a1.3 .8 0 1 0 2.6 0a1.3 .8 0 1 0-2.6 0Z M13.5 13a1.3 .8 0 1 0 2.6 0a1.3 .8 0 1 0-2.6 0Z M8 18.2h8v1.4H8Z`),
  // Origineel: drie tekstregels, het bestand zoals het binnenkwam.
  original: ENT_FILL(`${CV_DOC} M8 11.2h8v1.6H8Z M8 14.6h8v1.6H8Z M8 18h5v1.6H8Z`),
};
// crop: viewBox om het blad zelf (17 bij 21), zodat het blad de hoogte vult; de
// breedte volgt. fold: de omgeslagen hoek gevuld in inkt (hover op de knoppen).
export function CvKindIcon({ kind, size = 24, color, crop = false, fold = false, style }) {
  const k = CV_KIND[kind] ? kind : 'original';
  const w = crop ? Math.round((size * 17) / 21) : size;
  return (
    <svg width={w} height={size} viewBox={crop ? '3.5 1.5 17 21' : '0 0 24 24'} fill={color || CV_KIND[k].color} stroke="none" aria-hidden="true"
      style={{ flexShrink: 0, display: 'block', ...style }}>
      {CV_KIND_PATHS[k]}
      {fold && <path d={CV_FOLD} fill={HV2.ink2} />}
    </svg>
  );
}
// De drie CV-knoppen (recordkop en CV-veld in Kerngegevens, eigenaarsbesluit 13 okt
// 2026): het glyph zelf is de knop, geen tegel, rand of vulling. Hover vult de
// omgeslagen hoek in inkt; de InstantTip geeft label en subregel. Zonder bestand
// staat 'CV original' uit (35%), met 'geen bestand' als reden. offReasons zet
// een soort uit met een eigen reden (sollicitant zonder kandidaat: geen P& CV).
export function CvKindButtons({ hasOriginal, onOpen, offReasons = null }) {
  const [hover, setHover] = useState(null);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none' }}>
      {CV_KINDS.map((k) => {
        const reason = offReasons && offReasons[k];
        const off = !!reason || (k === 'original' && !hasOriginal);
        const label = t(CV_KIND[k].label);
        const sub = reason ? t(reason) : off ? t('geen bestand') : t(CV_KIND[k].sub);
        return (
          <InstantTip key={k} focusable={false} style={{ display: 'inline-flex' }}
            label={<span><b style={{ fontWeight: 650, color: HV2.ink }}>{label}</b><span style={{ display: 'block', marginTop: 2 }}>{sub}</span></span>}>
            <button type="button" aria-disabled={off || undefined} aria-label={label}
              onClick={off ? undefined : () => onOpen && onOpen(k)}
              onMouseEnter={() => setHover(k)} onMouseLeave={() => setHover((h) => (h === k ? null : h))}
              onFocus={() => setHover(k)} onBlur={() => setHover((h) => (h === k ? null : h))}
              style={{ display: 'inline-flex', padding: 0, margin: 0, border: 'none', background: 'none', borderRadius: 3, lineHeight: 0, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.35 : 1 }}>
              <CvKindIcon kind={k} size={28} crop fold={!off && hover === k} />
            </button>
          </InstantTip>
        );
      })}
    </span>
  );
}

// TeamIcon: twee personen, de tegenhanger van een enkele OwnerAvatar. Bestaat
// omdat een scope-schakelaar tussen 'Mijn' en 'Mijn Team' aan beide kanten een
// beeldmerk nodig heeft: links de avatar van de gebruiker zelf, rechts dit
// glyph. GEEN entiteitsglyph, dus lijn en niet gevuld (designregel 7: chrome en
// bediening zijn stroke-glyphs in currentColor) en dus ook niet in ENT_PATHS,
// waar alleen recordtypes staan. De persoonsvorm is bewust die van
// PERSON_BASE/candidate, zodat de twee figuren als mensen lezen naast de
// gevulde kandidaat-glyph elders in dezelfde rij.
export function TeamIcon({ size = 14, color = 'currentColor', title, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {..._entStroke} stroke={color}
      style={{ flexShrink: 0, display: 'block', ...style }} aria-hidden={title ? undefined : true}>
      {title ? <title>{title}</title> : null}
      <path d="M15.5 19.5v-1.6a3.6 3.6 0 0 0-3.6-3.6H6.1a3.6 3.6 0 0 0-3.6 3.6v1.6" />
      <path d="M9 4.5a3.3 3.3 0 1 0 0 6.6a3.3 3.3 0 1 0 0-6.6Z" />
      <path d="M21.5 19.5v-1.6a3.6 3.6 0 0 0-2.7-3.5" />
      <path d="M15.6 4.7a3.3 3.3 0 0 1 0 6.4" />
    </svg>
  );
}

// ── Nieuw & Hot markeringen ─────────────────────────────────────────────────
// Iconen in plaats van pills (eigenaarsbesluit 3 september 2026): de afwezigheid
// van een icoon is het signaal, de title draagt het woord. Drie soorten:
//   new         -> Material Symbols add_circle (NH_NEW_PATH in
//                  lib/partnerbook-icons.mjs, gedeeld met de kantoormuur), gevuld
//                  in HV2.blue (eigenaarsbesluit 12 september 2026: kleur op elk
//                  merkteken; het zegel van 3 september is vervangen);
//   hot         -> trending uit GLYPHS (gevuld, amber), woord 'Prioriteit';
//   reactivated -> historie uit MENU_ICON_PATHS (lijn).
// Nooit een eigen glyph.
const NH_TITLES = { new: 'Nieuw', hot: 'Prioriteit', reactivated: 'Gereactiveerd' };
export function NieuwHotIcon({ kind = 'new', size = 13, title, style }) {
  const label = title === undefined ? NH_TITLES[kind] : title;
  const box = { width: size, height: size, viewBox: '0 0 24 24', style: { flexShrink: 0, display: 'block', ...style }, 'aria-hidden': label ? undefined : true };
  const cap = label ? <title>{label}</title> : null;
  if (kind === 'hot') return <svg {...box} fill={TONE_COLORS.amber} stroke="none">{cap}<path d={GLYPHS.trending} /></svg>;
  if (kind === 'reactivated') return <svg {...box} {..._entStroke}>{cap}{MENU_ICON_PATHS.history}</svg>;
  return <svg {...box} viewBox={NH_NEW_VIEWBOX} fill={HV2.blue} stroke="none">{cap}<path d={NH_NEW_PATH} /></svg>;
}
// De toepasselijke markeringen naast elkaar; niets als er geen zijn. Een nieuwe
// klant draagt dezelfde ster als een nieuwe vacature, alleen het woord verschilt.
export function NieuwHotMarks({ isNew, isHot, clientFlag, size = 13, style }) {
  const marks = [];
  if (isNew) marks.push(<NieuwHotIcon key="new" kind="new" size={size} />);
  if (isHot) marks.push(<NieuwHotIcon key="hot" kind="hot" size={size} />);
  if (clientFlag === 'new') marks.push(<NieuwHotIcon key="client" kind="new" size={size} title="Nieuwe klant" />);
  if (clientFlag === 'reactivated') marks.push(<NieuwHotIcon key="react" kind="reactivated" size={size} />);
  if (!marks.length) return null;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0, ...style }}>{marks}</span>;
}

// ── Procesmarker in een lijstrij ────────────────────────────────────────────
// Minitypevak links van een kandidaatnaam in de bellijst en de CRM kandidaten-
// grid. De vulling is EXACT de bandvulling van het venster waar de marker naartoe
// wijst, dus die komt uit RECORD_TYPE_META en niet uit een eigen hex: hertint een
// track daar, dan loopt de marker automatisch mee. Grijs = afgewezen of
// gearchiveerd proces.
//
// De sleuf van 20px staat er ALTIJD, ook zonder proces, anders schuiven de namen
// per rij en golft de kolom. De payload komt uit lib/crm/process-marker.mjs.
const MARKER_FILL = { int: () => RECORD_TYPE_META.placement.color, bd: () => RECORD_TYPE_META.bd.color, src: () => RECORD_TYPE_META.sourcing.color, dead: () => HV2.divider };

// De zweeftekst hoort bij de marker, niet bij de tabel: zo staat hij een keer en
// lezen de bellijst en de grid hetzelfde. `status` en `by` komen ruw van de server
// (statuscode, rejectedReason), hier pas vertaald, net als de Fase-kolom.
export function processMarkerTitle(mk) {
  if (!mk || !mk.state) return '';
  if (mk.state === 'int') {
    const fase = mk.status ? t(statusShort(mk.status)) : '';
    if (mk.client && fase) return t('Actief interviewproces bij {klant}, {fase}', { klant: mk.client, fase });
    if (mk.client) return t('Actief interviewproces bij {klant}', { klant: mk.client });
    return fase ? t('Actief interviewproces, {fase}', { fase }) : t('Actief interviewproces');
  }
  if (mk.state === 'bd') {
    const fase = mk.status ? t(statusShort(mk.status)) : '';
    if (mk.client && fase) return t('Actief BD proces bij {klant}, {fase}', { klant: mk.client, fase });
    if (mk.client) return t('Actief BD proces bij {klant}', { klant: mk.client });
    return fase ? t('Actief BD proces, {fase}', { fase }) : t('Actief BD proces');
  }
  const datum = formatDateVoluit(mk.date);
  if (mk.state === 'src') {
    return datum ? t('CV Send gedaan {datum}', { datum }) : t('CV Send gedaan');
  }
  if (mk.dealId === null) return t('Historisch proces (import)');
  if (mk.archived) return datum ? t('Gearchiveerd proces, {datum}', { datum }) : t('Gearchiveerd proces');
  const wie = mk.by === 'client' ? t('klant') : mk.by === 'candidate' ? t('kandidaat') : '';
  if (wie && datum) return t('Interviewproces afgewezen door {wie}, {datum}', { wie, datum });
  if (wie) return t('Interviewproces afgewezen door {wie}', { wie });
  return datum ? t('Interviewproces afgewezen, {datum}', { datum }) : t('Interviewproces afgewezen');
}

// `onOpen(dealId)` opent het proces waar de marker naar wijst. Een echte <button>,
// niet een span met tabindex: de bellijstrij laat elke klik op een button/a met
// rust (closest-poort), en toetsenbordbediening komt gratis. preventDefault hoort
// er OOK bij, want in de grid zit de naamcel binnen een <a href> naar het
// kandidaatrecord; alleen stopPropagation laat die link alsnog navigeren.
export function ProcessMarker({ marker, onOpen, offLimit = false }) {
  const state = marker?.state || null;
  const box = { width: 20, height: 20, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', verticalAlign: 'middle' };

  // Off-limits: red stop marker, highest priority.
  if (offLimit) {
    const title = t('Off-Limits (GDPR) - niet benaderen');
    return (
      <span title={title} aria-label={title} style={{ ...box, background: HV2.redBg, border: `1px solid ${HV2.red}`, cursor: 'help' }}>
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={HV2.red} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'block' }}>
          <circle cx="12" cy="12" r="10" /><path d="M4.93 4.93l14.14 14.14" />
        </svg>
      </span>
    );
  }

  // No process at all: single diagonal line (top-right to bottom-left).
  if (!state) {
    return (
      <span aria-hidden title={t('Geen proces')} style={{ ...box, background: 'transparent', border: `1px solid ${HV2.border}`, cursor: 'default' }}>
        <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={HV2.border} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'block' }}>
          <line x1="20" y1="4" x2="4" y2="20" />
        </svg>
      </span>
    );
  }

  // Active or dead process: colored marker that opens the deal on click.
  const title = processMarkerTitle(marker);
  const isDead = state === 'dead';
  const bg = isDead ? 'transparent' : (MARKER_FILL[state] || MARKER_FILL.dead)();
  const iconColor = isDead ? HV2.mute2 : HV2.ink;
  const border = isDead ? `1px solid ${HV2.mute2}` : 'none';
  return (
    <button type="button" title={title} aria-label={title}
      onAuxClick={(e) => { newTabClick(recordHref('process', marker.dealId), e); }}
      onClick={(e) => { if (newTabClick(recordHref('process', marker.dealId), e)) return; e.stopPropagation(); e.preventDefault(); if (onOpen && marker.dealId) onOpen(marker.dealId); }}
      style={{ ...box, background: bg, border, padding: 0, font: 'inherit', cursor: 'pointer', opacity: isDead ? 0.55 : 1 }}>
      <EntityIcon entity="process" size={11} strokeWidth={2.2} color={iconColor} />
    </button>
  );
}

// Runtime phases - replaced once the beta page fetches /api/phases. De
// fase-ondergrenzen (`fromStatus`, lib/deal-phase.mjs) worden hier EEN keer
// gestempeld, bij binnenkomst; de statische terugval draagt ze al letterlijk.
let _runtimePhases = null;
export function setRuntimePhases(p) { _runtimePhases = ensurePhaseFloors(p); }
// Merge static defaults UNDER the runtime blob so a saved phases config that
// predates a track (e.g. 'sourcing') still resolves that track's checklist.
// Director customizations to bd/placement still win; new tracks fall back to PHASE_CHECKLIST.
export function getRuntimePhases() { return _runtimePhases ? { ...PHASE_CHECKLIST, ..._runtimePhases } : PHASE_CHECKLIST; }

// Multi-owner: a deal is "owned by" a user if they're the primary owner (userId)
// OR an additional owner (deal.coOwners). Used by every Mij/Team scope filter so
// co-owned processes show up in a co-owner's lists. KPI credit stays on userId.
export function dealOwnedBy(deal, uid) {
  if (!deal || uid == null) return false;
  const u = String(uid);
  if (String(deal.userId) === u) return true;
  return Array.isArray(deal.coOwners) && deal.coOwners.some((c) => c && String(c.userId) === u);
}

// Candidate lifecycle stage - DERIVED from a candidate's deals (never stored).
// Priority high→low: signed placement > active placement > any sourcing deal > none.
// The same priority is mirrored server-side in /api/crm (badge column + filter).
export const CAND_STAGES = ['Unsourced', 'Sourced', 'Interview Process', 'Placement/Signed', 'Left Placement'];
export function deriveStage(deals, candidateSlug) {
  const mine = (deals || []).filter((d) => d && String(d.candidateSlug) === String(candidateSlug));
  if (mine.some((d) => d.track === 'placement' && d.signed)) return 'Placement/Signed';
  if (mine.some((d) => d.track === 'placement' && !d.archived)) return 'Interview Process';
  if (mine.some((d) => d.track === 'sourcing')) return 'Sourced';
  return 'Unsourced';
}
// 'Left Placement' (vertrokken bij plaatsing, 10 sep 2026) is een afgeronde
// plaatsing zonder lopend proces: gedempt neutraal, geen tweede groen.
const STAGE_TONE = { 'Unsourced': 'neutral', 'Sourced': 'amber', 'Interview Process': 'blue', 'Placement/Signed': 'green', 'Left Placement': 'neutral' };
// Weergavelabel per stage: de OPGESLAGEN waarden blijven Engelse data-keys
// ('Interview Process' etc., nooit vertalen/muteren); alleen de UI toont NL.
export const STAGE_LABEL = { 'Interview Process': 'Interviewproces', 'Sourced': 'CV Send', 'Unsourced': 'Geen CV Send', 'Left Placement': 'Vertrokken bij plaatsing' };
export const stageLabel = (stage) => STAGE_LABEL[stage] || stage;
export function StageBadge({ stage }) { return <TonePill tone={STAGE_TONE[stage] || 'neutral'}>{stageLabel(stage) || 'Unsourced'}</TonePill>; }
// U34 - kandidaat-stage in de universele track-taal (eigenaarsbesluit): 'Sourced'
// en 'Interview Process' tonen de canonieke SRC/INT TrackTag + label (net als de
// track-knoppen) i.p.v. een gekleurde pill - de oude oranje 'Sourced'-pill is weg
// (rule 17). 'Placement/Signed' houdt zijn bestaande groene signed-conventie en
// 'Unsourced' blijft gedempt neutraal (beide via StageBadge, ongewijzigd).
export function StageTrackTag({ stage }) {
  const row = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: HV2.fsText, fontWeight: 560, color: HV2.ink, whiteSpace: 'nowrap' };
  if (stage === 'Sourced') return <span style={row}><TrackTag track="sourcing" />{t('CV Send')}</span>;
  if (stage === 'Interview Process') return <span style={row}><TrackTag track="placement" />{t('Interviewproces')}</span>;
  return <StageBadge stage={stage} />;
}

export { isActionChecked, phaseKeyOf } from '@/lib/process-chance.mjs';
export function getPhaseProgress(deal) { return processPhaseProgress(deal, getRuntimePhases()); }

// Bewust de OPGESLAGEN fase-index: dit is "welke stappen heeft dit proces
// achtergelaten", en een fase die de status oversloeg is niet achtergelaten
// (eigenaarsbesluit). Uitzondering met telling in lib/one-fact-one-source.test.mjs.
export function hasPriorIncomplete(deal) {
  const phaseData = getRuntimePhases();
  const phases = phaseData[deal.track];
  if (!phases) return false;
  const pc = deal.phaseChecklist || { phaseIndex: 0, actions: {} };
  const phaseIndex = Math.min(pc.phaseIndex || 0, phases.length - 1);
  if (phaseIndex === 0) return false;
  const actions = pc.actions || {};
  for (let i = 0; i < phaseIndex; i++) {
    const phaseActions = phases[i].actions || [];
    for (const a of phaseActions) {
      if (!isActionChecked(actions, i, a)) return true;
    }
  }
  return false;
}

export function calcKans(deal) { return processChance(deal, getRuntimePhases()); }

export function explainKans(deal) {
  if (!deal) return [];
  const prog = getPhaseProgress(deal);
  if (!prog) return [];
  const inPhase = prog.total > 0 ? prog.done / prog.total : 0;
  const phaseBase = Math.round(((prog.phaseIndex + inPhase) / Math.max(1, prog.totalPhases)) * 60);
  const parts = [];
  parts.push(`Fasevoortgang: +${phaseBase} (fase ${prog.phaseIndex + 1}/${prog.totalPhases}, ${prog.done}/${prog.total} stappen)`);
  let statusBoost = 0;
  if (deal.track === 'placement') statusBoost = ({ 'IV1': 8, 'IV2': 20, 'IV3': 32, 'IV4': 36, 'IV5': 39, 'IV6': 41, 'Aanbieding arbeidscontract': 42 })[deal.status] || 0;
  else if (deal.track === 'bd') statusBoost = ({ 'Lead': 0, 'Voorstel NCNP': 12, 'Voorstel Retained': 16, 'Voorstel Partnership': 20, 'Vragen over Raamovereenkomst': 28 })[deal.status] || 0;
  if (statusBoost) parts.push(`Status ${deal.status}: +${statusBoost}`);
  if (!deal.signed && !deal.archived && deal.createdAt) {
    const ageDays = Math.max(0, Math.floor((Date.now() - new Date(deal.createdAt).getTime()) / 86400000));
    let agePen = 0;
    if      (ageDays > 90) agePen = 22;
    else if (ageDays > 60) agePen = 14;
    else if (ageDays > 30) agePen = 7;
    else if (ageDays > 14) agePen = 3;
    if (agePen) parts.push(`Ouderdom (${ageDays} dgn): −${agePen}`);
  }
  const r = (deal.rescheduleLog || []).length;
  if (r > 0) parts.push(`Verschuivingen (${r}×): −${r * 5}`);
  if (deal.signed) parts.unshift('Signed → 100%');
  return parts;
}

// Dezelfde uitsplitsing als explainKans, maar als label/waarde-paren voor het
// raster van een HintBody (eigenaarsbesluit 3 sep 2026: een berekening is een
// grid, geen zin). Het teken is een gewoon minteken, geen unicode-min.
export function explainKansRows(deal) {
  if (!deal) return [];
  const prog = getPhaseProgress(deal);
  if (!prog) return [];
  const inPhase = prog.total > 0 ? prog.done / prog.total : 0;
  const phaseBase = Math.round(((prog.phaseIndex + inPhase) / Math.max(1, prog.totalPhases)) * 60);
  const rows = [];
  rows.push([t('Fasevoortgang (fase {f}/{tf}, {d}/{td} stappen)', { f: prog.phaseIndex + 1, tf: prog.totalPhases, d: prog.done, td: prog.total }), `+${phaseBase}`]);
  let statusBoost = 0;
  if (deal.track === 'placement') statusBoost = ({ 'IV1': 8, 'IV2': 20, 'IV3': 32, 'IV4': 36, 'IV5': 39, 'IV6': 41, 'Aanbieding arbeidscontract': 42 })[deal.status] || 0;
  else if (deal.track === 'bd') statusBoost = ({ 'Lead': 0, 'Voorstel NCNP': 12, 'Voorstel Retained': 16, 'Voorstel Partnership': 20, 'Vragen over Raamovereenkomst': 28 })[deal.status] || 0;
  if (statusBoost) rows.push([t('Status {s}', { s: deal.status }), `+${statusBoost}`]);
  if (!deal.signed && !deal.archived && deal.createdAt) {
    const ageDays = Math.max(0, Math.floor((Date.now() - new Date(deal.createdAt).getTime()) / 86400000));
    let agePen = 0;
    if      (ageDays > 90) agePen = 22;
    else if (ageDays > 60) agePen = 14;
    else if (ageDays > 30) agePen = 7;
    else if (ageDays > 14) agePen = 3;
    if (agePen) rows.push([t('Ouderdom {n} dagen', { n: ageDays }), `-${agePen}`]);
  }
  const r = (deal.rescheduleLog || []).length;
  if (r > 0) rows.push([t('Verschuivingen {n}', { n: r }), `-${r * 5}`]);
  if (deal.signed) rows.unshift([t('Getekend'), '100']);
  return rows;
}

// ── Date helpers ───────────────────────────────────────────────────────────
export function pad2(n) { return String(n).padStart(2, '0'); }
export function localDateStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
export function todayStr() { return localDateStr(new Date()); }
export function weekFromToday() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return localDateStr(d);
}
// Today's interview rounds that have a clock time, as concrete start moments.
// Shared by the Timer view and the ping engine so they agree on what's "today".
// Only placement interviews carry an HH:MM start, so only those can be timed -
// untimed follow-ups ("Opvolgen") have no start moment to ping against.
export function todaysInterviews(deals, dayStr = todayStr()) {
  const out = [];
  (deals || []).forEach((deal) => {
    if (!deal || deal.archived || deal.track !== 'placement') return;
    for (let n = 1; n <= 6; n++) {
      const date = deal[`iv${n}Date`];
      const time = deal[`iv${n}Time`];
      if (date !== dayStr || !time) continue;
      const when = new Date(`${date}T${time}`);
      if (Number.isNaN(when.getTime())) continue;
      out.push({ id: deal.id, deal, round: n, label: `IV${n}`, time, date, when });
    }
  });
  return out.sort((a, b) => a.when - b.when);
}
// Match a canonicalised phone number to the deals/processes it belongs to, using
// the per-person canonical fields the deal already carries. Client-side twin of
// getDealPhoneMap() in /api/voip - the client holds `deals`, so no DB round trip.
// Newest-updated first; one number can hit several processes.
export function matchDealsByPhone(deals, canonical) {
  if (!canonical) return [];
  const out = [];
  (deals || []).forEach((d) => {
    if (!d || d.archived) return;
    if (d.candidatePhoneCanonical === canonical) out.push({ deal: d, contactRole: 'candidate', contactName: d.candidateName || '' });
    else if (d.clientDmuPhoneCanonical === canonical) out.push({ deal: d, contactRole: 'client', contactName: d.clientDmuName || '' });
    else if (d.phoneCanonical === canonical) out.push({ deal: d, contactRole: 'legacy', contactName: '' });
  });
  return out.sort((a, b) => String(b.deal.updatedAt || '').localeCompare(String(a.deal.updatedAt || '')));
}
const MAANDEN_VOLUIT = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
// Datum voluit ('9 juli 2026') voor gedempte "gewijzigd/geïmporteerd"-regels.
// Lege/ongeldige invoer -> '' (de UI toont dan niets).
// CV-link voor een kandidaat: het externe systeem-gehoste bestanden lopen sinds de
// R2-migratie (2026-07-17) via onze eigen gateway /api/candidate-cv; een
// in-app geupload CV (Vercel Blob) linkt rechtstreeks. null = niets serveerbaars
// (bewust niet gemigreerde LinkedIn-exports + de 45 in het externe systeem al dode
// bestanden) - de UI toont dan geen link (eigenaarsbesluit 2026-07-17).
// Volgorde omgedraaid op 7 augustus 2026: r2Key gaat nu voor op link. Een in de app
// aangemaakte kandidaat met een geuploade CV krijgt NOOIT een resume_link (alleen
// resume_r2_key, zie app/api/cv-intake/index/route.js), dus de oude link-eerst-regel
// liet die CV altijd onzichtbaar (5 kandidaten in productie, o.a. pb-msinvbj1j156).
// Zelfde volgorde als app/api/cv-profile/fill/route.js al gebruikte.
export function cvHref(slug, link, r2Key) {
  if (r2Key) return `/api/candidate-cv?slug=${encodeURIComponent(slug)}`;
  if (link && !/api\.recruitcrm\.io/i.test(link)) return link;
  return null;
}
export function formatDateVoluit(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MAANDEN_VOLUIT[d.getMonth()]} ${d.getFullYear()}`;
}
// Zelfde als formatDateVoluit, maar valt bij een onparseerbare datum terug op de
// eerste 10 tekens van de ruwe waarde in plaats van een lege string (CV Send-rijen).
export function formatDateVoluitSafe(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return formatDateVoluit(d);
}
// Maand + jaar ('maart 2025'): voor een mijlpaal waar de exacte dag niets toevoegt,
// zoals 'Geplaatst bij {klant}' in de onderscheidingsbalk.
export function formatMaandJaar(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${MAANDEN_VOLUIT[d.getMonth()]} ${d.getFullYear()}`;
}
// Gedempte herkomst-regel voor een salarisveld: 'geïmporteerd 9 juli 2026' als de
// bron een rcrm-import is, anders 'gewijzigd 9 juli 2026'. '' als er geen datum is.
export function salaryChangedLabel(at, src) {
  const d = formatDateVoluit(at);
  if (!d) return '';
  return `${src === 'rcrm-import' ? 'geïmporteerd' : 'gewijzigd'} ${d}`;
}
// Parse a free-text monthly salary ("€5.400", "5400") to a number, or null when
// it is not a plausible gross monthly figure (rejects ranges and garbage).
// Delegates to the single source of truth in lib/salary.mjs (parseSalaryNL).
export function parseSalaryPm(v) {
  return parseSalaryNL(v);
}
// (salarySoftFlag verwijderd - het amber soft-flag-oppervlak is vervangen door de
// neutrale SalaryFit-strip (components/HomeV2/SalaryFit.js), die de canonieke
// resolver-output tegen de job-range legt. Geen andere consumers meer.)
// statusShort woont sinds 20 september 2026 naast de andere statuswoorden in
// lib/cv-send-labels.mjs; deze regel houdt het bestaande importpad in stand.
export { statusShort };

// V4 dot-prefix urgency classification for opvolg dates.
// Returns { dot: 'red'|'ink'|'hollow'|null, label: string }
export function urgencyDot(nextDate) {
  if (!nextDate) return null;
  const t = todayStr();
  const w = weekFromToday();
  if (nextDate < t) return { dot: 'red',    label: formatDateVoluit(nextDate) };
  if (nextDate === t) return { dot: 'ink',    label: 'Vandaag' };
  if (nextDate <= w) return { dot: 'hollow', label: formatDateVoluit(nextDate) };
  return { dot: null, label: formatDateVoluit(nextDate) };
}

// ── KPI / Whiteboard formatting ─────────────────────────────────────────────
export function fmtKpiVal(kpi, val) {
  if (kpi === 'Revenue' && val > 0) return fmtEuro(val);
  return val;
}
// External-link affordance: every URL that leaves the app renders with this ↗
// box icon so users know it opens a new browser tab. Use ExtA for text links;
// use ExtIcon inline when the anchor markup must stay custom.
export const ExtIcon = ({ size = 11, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);
export function ExtA({ href, children, style, title, onClick }) {
  if (!href) return null;
  // App-interne paden (bijv. de CV-gateway /api/candidate-cv) blijven relatief;
  // alleen kale externe domeinen krijgen https:// ervoor.
  const url = /^https?:\/\//i.test(href) || href.startsWith('/') ? href : `https://${href}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={onClick} title={title || t('Opent in een nieuw tabblad')}
      style={{ color: HV2.blue, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%', ...style }}>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
      <ExtIcon />
    </a>
  );
}

// linkifyText: splits plain text on URLs and record mentions, wraps each
// match in a clickable element. URLs get <ExtA> (external), mentions
// ([[type:slug|Name]]) get an in-app hash link. Returns the original string
// when nothing matches (cheap path for the majority of log entries).
const MENTION_RE = /\[\[(candidate|company|contact|job):([^\]|]+)\|([^\]]+)\]\]/;
const USER_MENTION_RE = /@\[([^\]|]+)\|([^\]]+)\]/;
const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"')\]]+/i;
// Dezelfde vermeldingen als platte tekst, voor plekken waar geen nodes kunnen
// staan: een title-tip, of een tekst die zelf al in een link zit (de detailkolom
// van Beheer > Actuals/Results). [[candidate:jan-jansen|Jan Jansen]] leest als
// 'Jan Jansen', @[id|Naam] als 'Naam'. Leest MENTION_RE en USER_MENTION_RE, dus
// een nieuw vermeldingstype komt hier vanzelf mee.
export function mentionPlainText(text) {
  return String(text || '')
    .replace(new RegExp(MENTION_RE.source, 'g'), '$3')
    .replace(new RegExp(USER_MENTION_RE.source, 'g'), '$2');
}
export function linkifyText(text) {
  if (!text || typeof text !== 'string') return text || '';
  const parts = [];
  let rest = text;
  let ki = 0;
  while (rest) {
    const mm = MENTION_RE.exec(rest);
    const mu = URL_RE.exec(rest);
    const um = USER_MENTION_RE.exec(rest);
    if (!mm && !mu && !um) { parts.push(rest); break; }
    const candidates = [mm, mu, um].filter(Boolean);
    const first = candidates.reduce((a, b) => a.index <= b.index ? a : b);
    if (first === mm) {
      if (mm.index > 0) parts.push(rest.slice(0, mm.index));
      const [, type, slug, name] = mm;
      const href = `#crm-${type}-${encodeURIComponent(slug)}`;
      parts.push(<a key={ki++} {...hashNav(href, () => { location.hash = href; })} style={{ color: HV2.blue, textDecoration: 'none' }}>{name}</a>);
      rest = rest.slice(mm.index + mm[0].length);
    } else if (first === um) {
      if (um.index > 0) parts.push(rest.slice(0, um.index));
      parts.push(<span key={ki++} style={{ color: HV2.blue, fontWeight: 560 }}>@{um[2]}</span>);
      rest = rest.slice(um.index + um[0].length);
    } else {
      if (mu.index > 0) parts.push(rest.slice(0, mu.index));
      const url = mu[0];
      const href = url.startsWith('www.') ? 'https://' + url : url;
      const clean = href.replace(/[.,;:!?)]+$/, '');
      const displayUrl = url.replace(/[.,;:!?)]+$/, '');
      const trailing = url.slice(displayUrl.length);
      parts.push(<ExtA key={ki++} href={clean}>{displayUrl}</ExtA>);
      if (trailing) parts.push(trailing);
      rest = rest.slice(mu.index + mu[0].length);
    }
  }
  if (parts.length <= 1 && typeof parts[0] === 'string') return text;
  return <>{parts}</>;
}

// ── Internal record navigation: open-in-new-tab support ─────────────────────
// The app is hash-routed (#crm-<entity>-<slug>, #proces-<id>) and every record
// hash deep-links on a fresh load, so a real <a href="#…"> lets the browser open
// a record in a new tab natively. hashNav turns a hash href into anchor props:
// middle-click needs no JS at all (the native <a href> handles auxclick), and the
// onClick lets the browser take ANY modified click (Cmd/Ctrl/Shift/Alt, or the
// middle button) while a PLAIN click stays in-app via onActivate (no full reload).
// Omit onActivate to only claim modified clicks and let a plain click fall through
// to a surrounding handler (e.g. a table row whose own onClick expands/opens it).
export function hashNav(href, onActivate) {
  return {
    href,
    onClick: (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      e.preventDefault();
      if (onActivate) onActivate(e);
    },
  };
}

// Naar een hash in de app. Staan we al op de doelhash, dan vuurt de browser geen
// hashchange en zou de klik niets doen; app/page.js leest de hash zelf, dus een
// synthetische hashchange volstaat. Accepteert 'x', '#x' en '/#x'.
export function goHash(hash) {
  const h = normalizeHash(hash).replace(/^#/, '');
  if (!h || typeof window === 'undefined') return;
  if (normalizeHash(window.location.hash) === `#${h}`) window.dispatchEvent(new Event('hashchange'));
  else window.location.hash = h;
}

// ── RecordLink: de blauwe verwijzing naar een record ────────────────────────
// Een echte <a href> (Tab, Enter, rechtsklik kopieert een werkende deep link,
// cmd/ctrl/shift/middenklik opent een nieuw tabblad via hashNav). stop houdt de
// klik weg bij een klikbare rij eronder, anders opent er een tweede record.
// href is optioneel: een aanroeper die al een eigen hash heeft (een tab, een
// bellijst) geeft die mee, anders bouwt recordHref hem uit entity en slug.
export function RecordLink({ entity, slug, label, onActivate, style, stop = true, href, titleAttr }) {
  const h = href || recordHref(entity, slug);
  if (!h) return <>{label}</>;
  const nav = hashNav(h, onActivate || (() => goHash(h)));
  return (
    <a
      {...nav}
      title={titleAttr}
      onClick={(e) => { if (stop) e.stopPropagation(); nav.onClick(e); }}
      style={{ fontWeight: 560, color: HV2.blue, textDecoration: 'none', ...style }}
    >
      {label}
    </a>
  );
}

// ── GlyphLink: het kleine lijst-met-vergrootglas-icoon als link ─────────────
// EEN glyph-anchor voor de hele app (unit L, 4 oktober 2026, uit TitleLink gehaald):
// de functiepagina naast een jobnaam (TitleLink variant 'glyph') en een processtadium
// naast de segmenten van de Processen-container. Een echte <a href> via hashNav
// (toetsenbord, cmd-klik, Terug, herladen), 24 bij 24 raakvlak, aria-label en title
// uit de aanroeper, muted naar blauw bij hover en focus. `className` alleen voor een
// pb-klasse uit globals.css.
export function GlyphLink({ href, onActivate, label, stop = true, style, className }) {
  const [lit, setLit] = useState(false);
  if (!href) return null;
  const nav = hashNav(href, onActivate || (() => goHash(href)));
  const onClick = (e) => { if (stop) e.stopPropagation(); nav.onClick(e); };
  return (
    <a
      href={nav.href}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={className}
      onMouseEnter={() => setLit(true)}
      onMouseLeave={() => setLit(false)}
      onFocus={() => setLit(true)}
      onBlur={() => setLit(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, marginLeft: 4, marginTop: -4, marginBottom: -4,
        verticalAlign: 'middle', flexShrink: 0, textDecoration: 'none',
        color: lit ? HV2.blue : HV2.muted, ...style,
      }}
    >
      <MenuIcon name="listSearch" size={14} />
    </a>
  );
}

// ── TitleLink: elke getoonde functietitel opent de functiepagina ────────────
// Besluit 4 oktober 2026 (plan unified functietitel): een functietitel (positie, functie van een
// contactpersoon, jobnaam, roleTitle van een proces) is overal dezelfde link
// naar de CRM-selectie met een exact functiefilter (sinds unit J
// #zoeken-<tab>?functie=<titel>; Kandidaten, Contacten en Jobs houden het
// filter bij een tabwissel). EEN component, EEN href (functieHref uit
// lib/nav-routes.mjs), twee vormen:
//   variant 'text'  (standaard) de titel zelf is de anchor. Leeg -> '-'.
//   variant 'glyph' een klein lijst-met-vergrootglas-icoon direct NA een naam
//                   die zelf al naar zijn record linkt (de jobnaam in een
//                   grid): het record blijft de naam, de functiepagina is een
//                   klik verder. Leeg -> niets.
// Nooit een chip in de anchor; de wiring-units bouwen geen eigen glyph-anchor.
// Uitzondering: in een keuzelijst (QuickCallLog-zoekresultaten, DMU-kiezer) is de rij zelf de actie en staat de titel als platte tekst.
export function TitleLink({ title, onActivate, style, stop = true, titleAttr, children, variant = 'text' }) {
  const href = functieHref(title);
  const glyph = variant === 'glyph';
  if (!href) return glyph ? null : '-';
  const shown = functieWhitespace(title);
  const label = titleAttr || t('Alle CRM-items met de functie {t}', { t: shown });
  const nav = hashNav(href, onActivate || (() => goHash(functieHash(title))));
  const onClick = (e) => { if (stop) e.stopPropagation(); nav.onClick(e); };
  if (glyph) return <GlyphLink href={nav.href} onActivate={onActivate || (() => goHash(functieHash(title)))} label={label} stop={stop} style={style} />;
  return (
    <a
      href={nav.href}
      onClick={onClick}
      title={label}
      style={{ fontWeight: 560, color: HV2.blue, textDecoration: 'none', ...style }}
    >
      {children ?? title}
    </a>
  );
}

// Rijen, kaarten en tegels die GEEN <a href> kunnen dragen (een <tr>, of een
// kaart met eigen knoppen erin) krijgen hiermee toch het browsergedrag dat
// gebruikers verwachten: cmd/ctrl-klik, shift-klik en middenklik openen het
// record in een nieuw tabblad, een gewone klik blijft in de app via de eigen
// onClick van de rij. Klikken binnen een echte <a>/<button>/veld gaan
// onaangeroerd door, die regelen hun eigen doel. Kan een element wel een
// anchor zijn, gebruik dan hashNav, dat is de eerste keuze.
export function newTabNav(href) {
  if (!href) return {};
  const nested = (e) => !!(e.target && e.target.closest && e.target.closest('a[href], button, input, select, textarea'));
  const open = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== 'undefined') window.open(href, '_blank', 'noopener,noreferrer');
  };
  return {
    onClickCapture: (e) => { if (nested(e)) return; if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) open(e); },
    onAuxClick: (e) => { if (e.button !== 1 || nested(e)) return; open(e); },
  };
}

// Zelfde verhaal voor een knop die zelf navigeert en niet in een <a> kan zitten
// (bijvoorbeeld de procesmarkering in een rij die al een kandidaat-anchor is):
// geef de modified klik en de middenklik een nieuw tabblad, en meld met de
// return of de klik al is afgehandeld.
export function newTabClick(href, e) {
  if (!href) return false;
  const wants = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1;
  if (!wants) return false;
  e.preventDefault();
  e.stopPropagation();
  if (typeof window !== 'undefined') window.open(href, '_blank', 'noopener,noreferrer');
  return true;
}

// fmtEuro woont in lib/fee.js, dit is alleen de doorgeefluik-export zodat de
// bestaande HomeV2-imports blijven werken. Voeg hier geen tweede implementatie toe.
export { fmtEuro };
// Afgekorte variant voor smalle KPI-rails: duizendtallen als K, dus bewust zonder
// decimalen. Onder de duizend valt hij terug op de canonieke opmaak.
export function fmtEuroShort(v) {
  const n = Math.round(v || 0);
  if (n >= 1000) {
    const k = n / 1000;
    return '€ ' + (k % 1 === 0 ? k : k.toLocaleString('nl-NL', { maximumFractionDigits: 1 })) + 'K';
  }
  return fmtEuro(n, { cents: false });
}

// Sum a single user's actuals for a KPI between two ISO dates (inclusive).
export function sumKpi(entries, kpi, fromDate, toDate) {
  return entries
    .filter(e => e.kpi === kpi && e.type !== 'target' && e.date >= fromDate && e.date <= toDate)
    .reduce((s, e) => s + (Number(e.value) || 0), 0);
}

// ── UI primitives ───────────────────────────────────────────────────────────
// Avatar is a photo (users.avatar_image via /api/users/avatar, max 200x200) when
// set, else a single-color circle with white initials. De kleur is avatarFill uit
// lib/user-colors.mjs (eigen keuze, dan users.color tenzij grijs, dan vast per id).
// Een vorm zonder avatar_colors (de gedenormaliseerde eigenaar { userName,
// userColor }, een notitie-auteur, of het grijs dat een route als terugval invulde)
// kent de eigen keuze niet en zoekt daarom eerst de volledige gebruikersrij op in
// het register hieronder, zodat elke avatar van dezelfde persoon dezelfde kleur toont.
export function avatarColor(user) {
  if (!user) return avatarFill(null);
  const own = user.avatar_colors || user.avatarColors || user.userAvatarColors;
  if (!own) {
    const id = user.id ?? user.userId ?? user.authorId;
    const nm = String(user.name ?? user.userName ?? user.authorName ?? '').trim().toLowerCase();
    const known = (id != null && id !== '' && AVATAR_FILL_BY_ID.get(String(id))) || (nm && AVATAR_FILL_BY_NAME.get(nm));
    if (known) return known;
  }
  return avatarFill(user);
}
// id/naam -> avatarFill van de volledige gebruikersrij, gevuld door registerAvatars.
const AVATAR_FILL_BY_ID = new Map();
const AVATAR_FILL_BY_NAME = new Map();
// Avatar photo registry: id/name -> avatar_url, filled from every canonical
// users load (bootstrap, /api/users). Lets UserAvatar find the photo even for
// the denormalized owner shapes ({ userName, userColor }) that flow through
// list endpoints without an avatar_url of their own. Same resolve order as
// ownerUser: id first, then case-insensitive name.
const AVATAR_URL_BY_ID = new Map();
const AVATAR_URL_BY_NAME = new Map();
// Naast de foto ook de identiteit die de avatar-hover toont: volledige naam en
// functietitel. Zelfde reden als het foto-register hierboven, want de meeste
// avatars krijgen een gedenormaliseerde eigenaar-vorm ({ userName, userColor })
// zonder rol mee. De titel wordt server-side afgeleid (/api/bootstrap, roleTitle
// in lib/roles.mjs), zodat er maar EEN regel is voor wat er in staat.
const USER_META_BY_ID = new Map();
const USER_META_BY_NAME = new Map();
export function registerAvatars(users) {
  for (const u of users || []) {
    if (!u) continue;
    const id = u.id != null ? String(u.id) : '';
    const nm = (u.name || '').trim().toLowerCase();
    // MERGEN, niet overschrijven. Alleen /api/bootstrap draagt roleTitle mee; de
    // vier andere aanroepers (CoachingTab, DashboardTab, PeopleTab, de eigen rij na
    // Instellingen) leveren lijsten zonder dat veld. Botweg zetten zou de titel daar
    // stilletjes wissen, en dan valt de hover terug op alleen een naam.
    const prev = (id && USER_META_BY_ID.get(id)) || (nm && USER_META_BY_NAME.get(nm)) || null;
    const meta = {
      id: id || (prev && prev.id) || '',
      name: u.name || (prev && prev.name) || '',
      roleTitle: u.roleTitle || (prev && prev.roleTitle) || '',
      // Alle markten van de collega (market_owners, primair eerst), alleen
      // /api/bootstrap draagt ze mee; andere lijsten laten de vorige waarde staan.
      markten: Array.isArray(u.markten) ? u.markten : (prev && prev.markten) || [],
      // Het eigen toestel (users.voip_extension, 29 sep 2026). /api/users draagt de
      // kolom mee (SELECT *), bootstrap niet: mergen, zoals roleTitle.
      voipExtension: String(u.voip_extension || u.voipExtension || '').trim() || (prev && prev.voipExtension) || '',
    };
    // De kleur alleen uit een volledige rij: met id (anders zou een naam-only lijst
    // de vaste per-id-kleur overschrijven met een naamsleutel) en met avatar_colors
    // (een lijst zonder die kolom kent de eigen keuze niet).
    if (id && 'avatar_colors' in u) {
      const fill = avatarFill(u);
      AVATAR_FILL_BY_ID.set(id, fill);
      if (nm) AVATAR_FILL_BY_NAME.set(nm, fill);
    }
    if (id) { AVATAR_URL_BY_ID.set(id, u.avatar_url || null); USER_META_BY_ID.set(id, meta); }
    if (nm) { AVATAR_URL_BY_NAME.set(nm, u.avatar_url || null); USER_META_BY_NAME.set(nm, meta); }
  }
}
export function userMeta(user) {
  if (!user) return null;
  if (user.id != null && USER_META_BY_ID.has(String(user.id))) return USER_META_BY_ID.get(String(user.id));
  const nm = (user.name || '').trim().toLowerCase();
  return nm ? (USER_META_BY_NAME.get(nm) || null) : null;
}

// De identiteitskaart van de Research Agent (versie, tools, afgeronde runs, open
// meldingen). EEN fetch per sessie, gestart bij de EERSTE hover, daarna uit de
// module-cache. Mislukt de call, dan blijft de hover werken met alleen de naam.
let AGENT_META = null;
let agentMetaPromise = null;
const agentMetaSubs = new Set();
function loadAgentMeta() {
  if (AGENT_META) return;
  if (!agentMetaPromise) {
    agentMetaPromise = fetch('/api/agent-reports?meta=1')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        AGENT_META = (d && !d.error) ? d : {};
        for (const fn of agentMetaSubs) fn();
      })
      .catch(() => { AGENT_META = {}; for (const fn of agentMetaSubs) fn(); });
  }
}
function useAgentMeta() {
  const [, bump] = useState(0);
  useEffect(() => {
    if (AGENT_META) return undefined;
    loadAgentMeta();
    const fn = () => bump((n) => n + 1);
    agentMetaSubs.add(fn);
    return () => { agentMetaSubs.delete(fn); };
  }, []);
  return AGENT_META;
}

// All-time score op de avatarkaart (eigenaarsbesluit 29 sep 2026): rang en omzet
// uit /api/leaderboard-alltime, dezelfde lezer als het All-Time Leaderboard
// (readAllTimeRows in lib/team-revenue.mjs), dus hetzelfde getal. EEN fetch per
// sessie bij de eerste hover, daarna uit de module-cache; zelfde patroon als
// AGENT_META hierboven. Mislukt de call, dan blijft de regel weg.
let ALLTIME_BY_ID = null;
let allTimePromise = null;
const allTimeSubs = new Set();
function loadAllTime() {
  if (ALLTIME_BY_ID || allTimePromise) return;
  allTimePromise = fetch('/api/leaderboard-alltime')
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      const map = new Map();
      for (const row of (d && Array.isArray(d.rows) ? d.rows : [])) {
        if (row && row.userId != null && Number(row.revenue) > 0) map.set(String(row.userId), { rank: row.rank, revenue: Number(row.revenue) });
      }
      ALLTIME_BY_ID = map;
    })
    .catch(() => { ALLTIME_BY_ID = new Map(); })
    .finally(() => { for (const fn of allTimeSubs) fn(); });
}
function useAllTimeScores() {
  const [, bump] = useState(0);
  useEffect(() => {
    if (ALLTIME_BY_ID) return undefined;
    loadAllTime();
    const fn = () => bump((n) => n + 1);
    allTimeSubs.add(fn);
    return () => { allTimeSubs.delete(fn); };
  }, []);
  return ALLTIME_BY_ID;
}
// Records (29 sep 2026): per soort de hoogste maand ooit (/api/awards, lib/awards.mjs).
// Zelfde module-cache als de all-time scores: een fetch per sessie, bij de eerste
// lezer (een hoverkaart of de Records-strook in Chat).
let AWARDS_LIST = null;
let awardsPromise = null;
const awardsSubs = new Set();
function loadAwards() {
  if (AWARDS_LIST || awardsPromise) return;
  awardsPromise = fetch('/api/awards')
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { AWARDS_LIST = d && Array.isArray(d.awards) ? d.awards : []; })
    .catch(() => { AWARDS_LIST = []; })
    .finally(() => { for (const fn of awardsSubs) fn(); });
}
export function useAwards() {
  const [, bump] = useState(0);
  useEffect(() => {
    if (AWARDS_LIST) return undefined;
    loadAwards();
    const fn = () => bump((n) => n + 1);
    awardsSubs.add(fn);
    return () => { awardsSubs.delete(fn); };
  }, []);
  return AWARDS_LIST;
}
// Beker als lijnwerk (chrome, geen emoji, geen goudverloop).
export function TrophyIcon({ size = 13, color = 'currentColor', strokeWidth = 2, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0, display: 'block', ...style }}>
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z" /><path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
    </svg>
  );
}
const MAAND_KORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export function awardValueLabel(a) {
  if (!a) return '';
  return a.money ? fmtEuroCompact(a.value) : String(a.value);
}
export function awardMonthShort(month) {
  const [y, m] = String(month || '').split('-');
  const n = MAAND_KORT[Number(m) - 1];
  return n && y ? `${n} ${y}` : String(month || '');
}

// '€ 1,2 mln' vanaf een miljoen, anders het volle bedrag zonder centen.
function fmtEuroCompact(v) {
  const n = Number(v) || 0;
  if (n >= 1e6) return `€ ${(n / 1e6).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} mln`;
  return fmtEuro(n, { cents: false });
}

export function avatarSrc(user) {
  if (!user) return null;
  if (user.avatar_url) return user.avatar_url;
  if (user.id != null && AVATAR_URL_BY_ID.has(String(user.id))) return AVATAR_URL_BY_ID.get(String(user.id));
  const nm = (user.name || '').trim().toLowerCase();
  return nm ? (AVATAR_URL_BY_NAME.get(nm) || null) : null;
}
export function userInitials(user) {
  const name = (user?.name || '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}
// De hoverkaart achter ELKE avatar: volledige naam, functietitel, de markten
// van de collega als marktiegels, de all-time score (rang en omzet) en het eigen
// toestel. De all-time omzet ging er op 3 september 2026 af en staat er sinds 29
// september 2026 weer op (eigenaarsbesluit, draait dat van 3 sep terug), nu met de
// rang uit dezelfde bron als het All-Time Leaderboard. Zelfde overlay-skelet als DirectorHoverHint (portal,
// vaste positie, pointerEvents:none); OwnerAvatar gebruikt precies deze kaart,
// dus het gedrag is overal identiek. De markten komen uit registerAvatars
// (/api/bootstrap, market_owners); zonder lijst valt de kaart terug op users.markt.
function AvatarHoverCard({ user }) {
  const meta = userMeta(user);
  const scores = useAllTimeScores();
  const uid = (meta && meta.id) || (user.id != null ? String(user.id) : '');
  const score = scores && uid ? scores.get(uid) : null;
  const ext = (meta && meta.voipExtension) || String(user.voip_extension || user.voipExtension || '').trim();
  const awards = useAwards();
  const mine = awards && uid ? awards.filter((a) => a.userId === uid) : [];
  const name = (meta && meta.name) || user.name || '?';
  const title = (meta && meta.roleTitle) || '';
  const src = avatarSrc(user);
  const markten = (meta && Array.isArray(meta.markten) && meta.markten.length)
    ? meta.markten
    : (Array.isArray(user.markten) && user.markten.length ? user.markten : (user.markt ? [String(user.markt).trim()] : [])).filter(Boolean);
  return (
    <>
      {/* Profielfoto op 75×75 bovenaan de hoverkaart (eigenaarsbesluit 13 aug 2026).
          Zonder foto staat er alleen de naam - geen lege cirkel. */}
      {src && (
        <img src={src} alt="" style={{ width: 75, height: 75, borderRadius: '50%', objectFit: 'cover', alignSelf: 'center', flexShrink: 0 }} />
      )}
      <span style={{ fontSize: HV2.fsText, fontWeight: 560, color: HV2.ink, textAlign: src ? 'center' : undefined }}>{name}</span>
      {title && <span style={{ fontSize: HV2.fsText, color: HV2.muted, textAlign: src ? 'center' : undefined }}>{title}</span>}
      {/* Markttegels, primair eerst. De SVG gaat als data-URI in een <img> en
          nooit als DOM-HTML: marktnamen komen uit de database. */}
      {markten.length > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, justifyContent: src ? 'center' : undefined }}>
          {markten.map((m) => (
            <img key={m} src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(marketIcon(m, { size: 18 }))}`}
              alt={m} title={m} width={18} height={18} style={{ display: 'block', flexShrink: 0 }} />
          ))}
        </span>
      )}
      {/* Zolang de scores laden een gedempte plekhouder, daarna de regel of niets. */}
      {!scores && uid && <span style={{ fontSize: HV2.fsText, color: HV2.divider, textAlign: src ? 'center' : undefined }}>{t('All-time')}</span>}
      {score && (
        <span style={{ fontSize: HV2.fsText, color: HV2.ink, fontVariantNumeric: 'tabular-nums', textAlign: src ? 'center' : undefined }}>
          {t('All-time #{r}, {v}', { r: score.rank, v: fmtEuroCompact(score.revenue) })}
        </span>
      )}
      {ext && <span style={{ fontSize: HV2.fsText, color: HV2.muted, fontVariantNumeric: 'tabular-nums', textAlign: src ? 'center' : undefined }}>{t('Toestel {n}', { n: ext })}</span>}
      {/* Records die deze collega houdt (lib/awards.mjs): beker plus kort label. */}
      {mine.length > 0 && (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, justifyContent: src ? 'center' : undefined }}>
          {mine.map((a) => (
            <Chip key={a.key} title={`${t(a.label)}: ${awardValueLabel(a)}, ${awardMonthShort(a.month)}`}>
              <TrophyIcon size={13} />{t(a.badge || a.label)}
            </Chip>
          ))}
        </span>
      )}
    </>
  );
}
// De hoverkaart van de Research Agent. Bewust NIET via HoverCard: die kaart draagt
// pointerEvents:'none' (zodat een tooltip nooit een klik afvangt) en sluit op de
// mouseleave van de trigger, dus een knop erin zou onbereikbaar zijn. Deze kaart is
// wel interactief en blijft daarom open zolang de muis op de trigger OF op de kaart
// staat. De plaatsingsmath blijft gedeeld: placeTooltip, net als InfoHint en HoverCard.
function AgentHover({ children, width = 250, style }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const cardRef = useRef(null);
  const closeTimer = useRef(null);
  const [pos, setPos] = useState(null);
  const place = useCallback(() => {
    const el = anchorRef.current; if (!el) return;
    const h = cardRef.current ? cardRef.current.offsetHeight : 0;
    setPos(placeTooltip(el.getBoundingClientRect(), width, h, 'right'));
  }, [width]);
  // Korte marge zodat de muis van de tegel naar de kaart kan reizen zonder dat de
  // kaart onder de cursor vandaan valt.
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const show = () => { cancelClose(); place(); setOpen(true); };
  const hide = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 140); };
  // Meteen weg bij een klik, net als HoverCard: het meldvenster hangt op een lagere
  // laag dan deze kaart, dus een blijvende kaart zou eroverheen staan.
  const closeNow = () => { cancelClose(); setOpen(false); };
  useEffect(() => () => cancelClose(), []);
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, place]);
  return (
    <span ref={anchorRef} style={{ position: 'relative', display: 'inline-flex', ...style }}
      onMouseEnter={show} onMouseLeave={hide} onFocusCapture={show} onBlurCapture={hide}
      onMouseDownCapture={(e) => { if (!cardRef.current || !cardRef.current.contains(e.target)) closeNow(); }}>
      {children}
      {open && pos && typeof document !== 'undefined' && createPortal(
        <span ref={cardRef} onMouseEnter={cancelClose} onMouseLeave={hide} onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9700, width, ...SURFACE.tooltip, padding: '10px 12px', fontSize: HV2.fsText, fontWeight: 400, lineHeight: 1.45, color: HV2.ink2, whiteSpace: 'normal', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 6, cursor: 'default', fontFamily: 'inherit' }}>
          {/* De kaart sluit zichzelf pas op de KLIK van de knop, niet op mousedown:
              een kaart die bij mousedown verdwijnt neemt de knop mee en dan komt de
              klik nooit aan. React stuurt events uit de portal ook door de
              componentboom, dus de mousedown-capture hierboven slaat de kaart over
              en een klik stopt hier, anders opent hij ook de knop eromheen. */}
          <AgentHoverCard onAct={closeNow} />
        </span>, document.body)}
    </span>
  );
}

// Een regel van de agentkaart: label links, waarde rechts. Zelfde typografie als
// de omzetregel in AvatarHoverCard.
function AgentRow({ label, value }) {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
      <span style={{ fontSize: HV2.fsText, color: HV2.muted }}>{label}</span>
      <span style={{ fontSize: HV2.fsText, fontWeight: 560, color: HV2.ink, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </span>
  );
}

function AgentHoverCard({ onAct }) {
  const meta = useAgentMeta();
  const tools = (meta && Array.isArray(meta.tools)) ? meta.tools : [];
  return (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <AgentMark size={26} />
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <span style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.ink }}>{t('Research Agent')}</span>
          <span style={{ fontSize: HV2.fsText, color: HV2.muted }}>{t('Geautomatiseerde testpersona')}</span>
        </span>
      </span>
      {/* Niets tonen zolang de kaart nog onderweg is: een '0 testruns' die een tel
          later verspringt leest als een fout. */}
      {meta && (
        <>
          {meta.version && <AgentRow label={t('Versie')} value={meta.version} />}
          {tools.length > 0 && <AgentRow label={t('Tools')} value={tools.join(', ')} />}
          <span style={{ fontSize: HV2.fsText, color: HV2.ink2 }}>{t('{n} testruns afgerond', { n: Number(meta.runs) || 0 })}</span>
          <span style={{ fontSize: HV2.fsText, color: HV2.ink2 }}>{t('{n} open meldingen', { n: Number(meta.openReports) || 0 })}</span>
        </>
      )}
      <button type="button" onClick={() => { if (onAct) onAct(); openAgentReport(); }}
        style={{ ...btnBase, height: 26, padding: '0 10px', marginTop: 2, background: HV2.bg, color: HV2.ink, fontSize: HV2.fsText }}>
        {t('Bug melden')}
      </button>
    </>
  );
}

// AgentAvatar: de tegel van de testpersona met zijn hoverkaart eromheen, als een
// echte knop. EEN bron voor beide plekken waar hij staat: overal waar UserAvatar
// de Research Agent tegenkomt (CRM-eigenaarskolom, agenda, logregels) en het vaste
// icoon in de bovenbalk, zodat melden op elke pagina bereikbaar is.
// interactive={false}: de tegel zit al in een knop (accountknop, scopemenu van de
// Jobs-strook), dus geen geneste <button>: invalide HTML en een hydration error.
// De hoverkaart met Bug melden blijft staan.
export function AgentAvatar({ size = 22, round = true, interactive = true }) {
  const mark = <AgentMark size={size} style={round ? { borderRadius: '50%' } : undefined} />;
  return (
    <AgentHover style={{ flexShrink: 0 }}>
      {interactive ? (
        <button type="button" onClick={openAgentReport} title={t('Bug melden')} aria-label={t('Bug melden')}
          style={{ display: 'inline-flex', padding: 0, margin: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 0 }}>
          {mark}
        </button>
      ) : mark}
    </AgentHover>
  );
}

// `hover` staat standaard AAN: de eigenaar wil de kaart overal waar een avatar staat.
// `interactive={false}` voor een avatar binnen een knop; alleen de Research Agent
// merkt het verschil (zie AgentAvatar).
export function UserAvatar({ user, size = 22, hover = true, interactive = true }) {
  const [failedSrc, setFailedSrc] = useState(null);
  if (!user) return null;
  // De testpersona krijgt zijn eigen tegel en zijn eigen kaart: geen foto, geen
  // initialencirkel, want die zouden hem als collega laten lezen. Het pad hieronder
  // voor echte gebruikers blijft ongewijzigd; hover={false} valt hier netjes op de
  // kale tegel terug.
  if (isResearchAgentUser(user)) {
    if (!hover) return <AgentMark size={size} style={{ borderRadius: '50%' }} />;
    return <AgentAvatar size={size} interactive={interactive} />;
  }
  const src = avatarSrc(user);
  const inner = (src && src !== failedSrc)
    ? (
      // Geen native title= meer als de hoverkaart aanstaat: twee tooltips over
      // elkaar (zie de opmerking bij placeTooltip hierboven).
      <img src={src} alt={user.name || ''} title={hover ? undefined : user.name} onError={() => setFailedSrc(src)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, display: 'inline-block', userSelect: 'none' }} />
    )
    : (
      // White initials with a soft shadow stay legible on both light and dark avatar colors.
      <div title={hover ? undefined : user.name} style={{
        width: size, height: size, borderRadius: '50%',
        background: avatarColor(user), flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 650, fontSize: Math.max(10, Math.round(size * 0.42)),
        lineHeight: 1, letterSpacing: '0.01em', textShadow: '0 1px 2px rgba(0,0,0,0.45)',
        userSelect: 'none',
      }}>{userInitials(user)}</div>
    );
  if (!hover) return inner;
  return (
    <HoverCard width={240} style={{ flexShrink: 0 }} card={<AvatarHoverCard user={user} />}>
      {inner}
    </HoverCard>
  );
}

// Resolve an item's owner to a full user record (carrying avatar_url + color) from
// the canonical users list - by id first, then by name - so a custom avatar still
// renders when the item stored a stale/imported userId or only a denormalized
// name (the "Ilan shows a grey ?" bug). Falls back to the item's own owner fields.
// Accepts the deal/lead shape: { userId, userName, userColor, userAvatarColors }.
export function ownerUser(users, item) {
  if (!item) return null;
  const list = users || [];
  if (item.userId != null && item.userId !== '') {
    const byId = list.find((u) => String(u.id) === String(item.userId));
    if (byId) return byId;
  }
  const nm = (item.userName || '').trim().toLowerCase();
  if (nm) {
    const byName = list.find((u) => (u.name || '').trim().toLowerCase() === nm);
    if (byName) return byName;
  }
  return { name: item.userName || '?', color: item.userColor, avatar_colors: item.userAvatarColors };
}

// useReducedMotion - true zodra het OS 'beperk beweging' meldt. Bewust een HOOK
// en geen kale check: de eerste render (server EN client-hydratie) geeft altijd
// false, dus de SSR-markup blijft byte-identiek aan de geanimeerde variant en er
// is geen hydration-mismatch. Pas na mount schakelt een laadstaat naar zijn
// statische frame. Zelfde patroon als RequestChip hierboven, hier herbruikbaar.
export function useReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const onChange = (e) => setReduce(e.matches);
    if (mq.addEventListener) { mq.addEventListener('change', onChange); return () => mq.removeEventListener('change', onChange); }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  return reduce;
}

// Vulkleur van een skeleton-balkje: net donkerder dan HV2.divider zodat een balk
// leesbaar blijft op zowel HV2.bg als HV2.bgSub. Staat hier omdat lib.js de
// tokenbron is; nooit los in een scherm herhalen.
const SKEL_BAR = '#d6d6d9';

// `color` overschrijft de kobaltinkt van de pillen. Alleen voor een vlak dat zijn
// eigen palet draagt (het zwarte laadvenster van Research, MatchLoading): overal
// elders blijft de standaard staan, anders krijgt elk scherm zijn eigen laadkleur.
//
// TERUGGEZET op 30 aug 2026. De prop verdween in 7970841e (een parallelle ronde
// aan de Nieuw-modals) terwijl MatchLoading hem bleef meegeven; de amberen pillen
// van de draaiende laan vielen daardoor stil terug op kobalt. Geen fout, geen rode
// test, alleen een verkeerde kleur. lib/design-loading.test.mjs bewaakt dit nu.
export function LoadingPills({ label = 'Laden', size = 34, inline = false, color = HV2.blue }) {
  const reduce = useReducedMotion();
  const wrap = inline
    ? { display: 'inline-flex', alignItems: 'center', gap: 8 }
    : { padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, flex: 1, minHeight: 160 };
  return (
    <div style={wrap}>
      {reduce ? (
        <svg viewBox="0 0 29 20" width={size} height={Math.round(size * 20 / 29)} role="img" aria-label={label} style={{ color, display: 'inline-block' }}>
          <rect x="3" y="7" width="7.5" height="6" rx="3" ry="3" fill="currentColor" opacity="1" />
          <rect x="11" y="7" width="7.5" height="6" rx="3" ry="3" fill="currentColor" opacity="1" />
          <rect x="19" y="7" width="7.5" height="6" rx="3" ry="3" fill="currentColor" opacity="1" />
        </svg>
      ) : (
        <svg viewBox="0 0 29 20" width={size} height={Math.round(size * 20 / 29)} role="img" aria-label={label} style={{ color, display: 'inline-block' }}>
          <rect x="3" y="7" width="7.5" height="6" rx="3" ry="3" fill="currentColor" opacity="0.22">
            <animate attributeName="opacity" values="0.22;1;0.22;0.22" keyTimes="0;0.18;0.36;1" dur="1.5s" begin="0s" repeatCount="indefinite" />
          </rect>
          <rect x="11" y="7" width="7.5" height="6" rx="3" ry="3" fill="currentColor" opacity="0.22">
            <animate attributeName="opacity" values="0.22;1;0.22;0.22" keyTimes="0;0.18;0.36;1" dur="1.5s" begin="0.5s" repeatCount="indefinite" />
          </rect>
          <rect x="19" y="7" width="7.5" height="6" rx="3" ry="3" fill="currentColor" opacity="0.22">
            <animate attributeName="opacity" values="0.22;1;0.22;0.22" keyTimes="0;0.18;0.36;1" dur="1.5s" begin="1s" repeatCount="indefinite" />
          </rect>
        </svg>
      )}
      {label && <span style={{ fontSize: HV2.fsText, color: HV2.muted, fontWeight: 480 }}>{label}</span>}
    </div>
  );
}

// Dense laad-skeleton: een dun grijs blok in sectiehoogte (HV2, geen tekst/spinner).
// Voor progressieve rendering - vervangt 'niets tonen' of een misleidende lege-staat
// terwijl een code-split chunk of een sectie-fetch nog loopt, zodat 'laden' niet als
// 'leeg'/'niet gekoppeld' oogt. Zachte SMIL-puls (zelfde conventie als LoadingPills,
// geen CSS-keyframes, geen glow).
export function SkeletonBlock({ height = 96, style }) {
  const reduce = useReducedMotion();
  return (
    <div aria-hidden="true" style={{ height, borderRadius: 8, border: `1px solid ${HV2.border}`, background: HV2.bgSub, overflow: 'hidden', ...style }}>
      <svg width="100%" height="100%" preserveAspectRatio="none" role="presentation" style={{ display: 'block' }}>
        <rect x="0" y="0" width="100%" height="100%" fill={HV2.subtle}>
          {!reduce && <animate attributeName="opacity" values="0.45;0.9;0.45" dur="1.4s" repeatCount="indefinite" />}
        </rect>
      </svg>
    </div>
  );
}

// SkeletonRows - het skelet van een TABELBODY, voor de CRM-grids. Toont de vorm
// van de data (aantal rijen, kolombreedtes, haarlijn-rijscheiding) in plaats van
// een lege tabel of de tekst 'Laden'. Gebruik hem bij de EERSTE vulling en bij een
// tab-/filterwissel; bij een refetch blijven de bestaande rijen staan (stale) en
// volstaat een LoadingPills inline naast de teller.
// Deterministisch: breedte en basisdekking komen uit vaste tabellen op (rij, kolom),
// nooit uit Math.random - anders danst het skelet bij elke render.
// as='tr' rendert <tr>/<td> zodat hij direct in een bestaande <tbody> past.
const SKEL_W = [1, 0.75, 0.92, 0.6, 0.84];
const SKEL_O = [0.55, 0.7, 0.85];
export function SkeletonRows({ rows = 8, cols = 5, widths, rowHeight = 34, as, style }) {
  const reduce = useReducedMotion();
  const asRow = as === 'tr';
  const baseW = (c) => (Array.isArray(widths) && widths[c] != null ? widths[c] : 120);
  const bar = (r, c) => (
    <svg width={Math.round(baseW(c) * SKEL_W[(r * 3 + c) % SKEL_W.length])} height="9" role="presentation" style={{ display: 'block' }}>
      <rect x="0" y="0" width="100%" height="9" rx="3" ry="3" fill={SKEL_BAR} fillOpacity={SKEL_O[(r + c) % SKEL_O.length]}>
        {!reduce && <animate attributeName="opacity" values="0.35;0.9;0.35" dur="1.4s" begin={`${(r * 0.08).toFixed(2)}s`} repeatCount="indefinite" />}
      </rect>
    </svg>
  );
  const idx = (n) => Array.from({ length: Math.max(0, n) }, (_, i) => i);
  if (asRow) {
    return (
      <>
        {idx(rows).map((r) => (
          <tr key={r} aria-hidden="true" style={{ height: rowHeight, ...style }}>
            {idx(cols).map((c) => (
              <td key={c} style={{ borderBottom: `1px solid ${HV2.divider}`, padding: '0 10px' }}>{bar(r, c)}</td>
            ))}
          </tr>
        ))}
      </>
    );
  }
  return (
    <div aria-hidden="true" style={style}>
      {idx(rows).map((r) => (
        <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 14, height: rowHeight, padding: '0 10px', borderBottom: `1px solid ${HV2.divider}` }}>
          {idx(cols).map((c) => <div key={c} style={{ width: baseW(c), flexShrink: 0 }}>{bar(r, c)}</div>)}
        </div>
      ))}
    </div>
  );
}

// SectionLoading - compacte gecentreerde laadstaat voor een subpaneel of een lazy
// chunk binnen een sectie: transparante achtergrond, vaste minimumhoogte zodat de
// sectie niet inklapt en daarna terugspringt. label=null houdt het puur visueel,
// de wrapper draagt dan de toegankelijke naam.
export function SectionLoading({ height = 120, size = 24, label = null, style }) {
  return (
    <div role="status" aria-label={label || t('Laden')} style={{ minHeight: height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', ...style }}>
      <LoadingPills inline size={size} label={label} />
    </div>
  );
}

// ChunkLoading - de gedeelde fallback voor een VIEW-brede dynamic() chunk
// (loading: ChunkLoading). Modals en popovers die nog niet zichtbaar horen te zijn
// houden loading: () => null; alles wat de gebruiker al aankijkt krijgt deze.
export function ChunkLoading() {
  return <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}><LoadingPills /></div>;
}

// Fetch-fout ≠ leeg ≠ laden. Kalme foutmelding + handmatige retry (useApi
// heeft shouldRetryOnError:false, dus zonder retry blijft een fout hangen).
export function ErrorState({ onRetry, label, status }) {
  const isNotFound = status === 404;
  return (
    <div style={{ padding: 16, textAlign: 'center' }}>
      <div style={{ fontSize: HV2.fsText, color: HV2.redText }}>{label || (isNotFound ? t('Dit dossier bestaat niet meer.') : t('Kon gegevens niet laden.'))}</div>
      {onRetry && !isNotFound && (
        <button type="button" onClick={onRetry}
          style={{ marginTop: 8, padding: '5px 12px', background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: 6, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 560, cursor: 'pointer', color: HV2.ink2 }}>
          {t('Opnieuw proberen')}
        </button>
      )}
    </div>
  );
}

// Stale data ≠ geen fout: een revalidatie die mislukt terwijl er al data op het
// scherm staat (SWR houdt die data vast en zet alleen `error`, zie lib/swr.js)
// mag niet stilzwijgend voor vers doorgaan. Band volgens Regel 34 punt 4
// (statusstrook van een regel): padding '9px 14px', radius 8, haarlijnrand,
// HV2.bgSub, geen marge (de aanroeper regelt de afstand tot de rest van de
// stapel). Geen linkerrand-accent (Regel 36). Knop is bewust geen <Button> (dit
// bestand kan components/HomeV2/Button.js niet importeren, dat leest HV2 terug
// uit lib.js en dat is circulair); dezelfde knoprecept als ErrorState hierboven.
export function StaleNotice({ onRetry, busy }) {
  return (
    <div style={{ padding: '9px 14px', background: HV2.bgSub, border: `1px solid ${HV2.border}`, borderBottom: recordContainerBorder(HV2.border), borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ fontSize: HV2.fsText, color: HV2.redText }}>{t('Niet ververst: je ziet mogelijk oude gegevens.')}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} disabled={busy}
          style={{ flexShrink: 0, padding: '5px 12px', background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: 6, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 560, cursor: busy ? 'default' : 'pointer', color: HV2.ink2, opacity: busy ? 0.6 : 1 }}>
          {busy ? t('Bezig…') : t('Opnieuw laden')}
        </button>
      )}
    </div>
  );
}

// Fire-and-forget activity log (same endpoint as the main app).
export function logActivity(action, detail, metadata = {}) {
  fetch('/api/activity', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, detail, metadata }),
  }).catch(() => {});
}

// ── Sorting / grouping ──────────────────────────────────────────────────────
export function getIvSortDate(deal) {
  if (deal.track !== 'placement') return null;
  if (['IV2', 'IV3', 'Aanbieding arbeidscontract'].includes(deal.status) && deal.iv2Date) return deal.iv2Date;
  if (deal.iv1Date) return deal.iv1Date;
  return null;
}

export function sortDeals(arr) {
  return [...arr].sort((a, b) => {
    const aIsBd = a.track === 'bd' ? 1 : 0;
    const bIsBd = b.track === 'bd' ? 1 : 0;
    if (aIsBd !== bIsBd) return aIsBd - bIsBd;
    const progA = getPhaseProgress(a);
    const progB = getPhaseProgress(b);
    const phaseA = progA ? progA.phaseIndex : 0;
    const phaseB = progB ? progB.phaseIndex : 0;
    if (phaseA !== phaseB) return phaseA - phaseB;
    const statList = a.track === 'bd' ? BD_STATUSES : a.track === 'sourcing' ? SOURCING_STATUSES : PLACEMENT_STATUSES;
    const sA = statList.indexOf(a.status);
    const sB = statList.indexOf(b.status);
    if (sA !== sB) return (sA === -1 ? 999 : sA) - (sB === -1 ? 999 : sB);
    const da = getIvSortDate(a);
    const db = getIvSortDate(b);
    if (da && db) return da.localeCompare(db);
    if (da) return -1;
    if (db) return 1;
    return 0;
  });
}

export function groupDealsByUser(subset, users) {
  const groups = users
    .map(u => ({ user: u, deals: sortDeals(subset.filter(d => String(d.userId) === String(u.id))) }))
    .filter(g => g.deals.length > 0);
  const unmatched = subset.filter(d => !users.find(u => String(u.id) === String(d.userId)));
  if (unmatched.length) {
    groups.push({
      user: { name: unmatched[0].userName || '?', color: unmatched[0].userColor },
      deals: sortDeals(unmatched),
    });
  }
  groups.sort((a, b) => b.deals.length - a.deals.length);
  return groups;
}

// ── Meer laden als volle rij onder een tabel (het Client Jobs-idioom op
// Vandaag): drie punten met diepte, het label en het aantal dat nog onder ligt.
// De opacity-trap leest als 'er ligt meer onder', dus geen extra uitleg.
// Plaatsen als <tfoot> in dezelfde <table>, dan loopt de rij tot beide randen.
export function MoreDots({ size = 14, color = HV2.ink3 }) {
  return (
    <svg width={size * 2} height={size} viewBox="0 0 48 24" fill={color} aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
      <circle cx="8" cy="12" r="4.5" opacity="1" />
      <circle cx="24" cy="12" r="4.5" opacity="0.55" />
      <circle cx="40" cy="12" r="4.5" opacity="0.25" />
    </svg>
  );
}

// remaining: aantal rijen dat nog niet geladen is. loading: de klik loopt nog
// (de punten worden even de laadpillen). capped: het plafond is bereikt en de
// rij vraagt om een scherper filter in plaats van een knop.
export function LoadMoreRow({ colSpan, onClick, remaining = 0, loading = false, capped = false }) {
  const row = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '6px 10px', background: HV2.bgSub, border: 'none', color: HV2.ink3, fontFamily: 'inherit', fontSize: HV2.fsText, fontWeight: 560 };
  return (
    <tfoot>
      <tr>
        <td colSpan={colSpan} style={{ padding: 0, borderTop: `1px solid ${HV2.border}` }}>
          {capped ? (
            <div style={{ ...row, color: HV2.muted, fontWeight: 400 }}>{t('Verfijn je filter voor meer resultaten')}</div>
          ) : (
            <button type="button" onClick={onClick} disabled={loading} title={t('Meer laden')} aria-label={t('Meer laden')}
              style={{ ...row, cursor: loading ? 'default' : 'pointer' }}>
              {loading ? <LoadingPills inline size={14} label="" color={HV2.ink3} /> : <MoreDots />}
              <span>{t('Meer laden')}</span>
              {remaining > 0 && <span style={{ color: HV2.muted, fontWeight: 400 }}>{remaining.toLocaleString('nl-NL')}</span>}
            </button>
          )}
        </td>
      </tr>
    </tfoot>
  );
}

// ── CrumbGlyph: het glyph van een kruimel ────────────────────────────────────
// Overgenomen uit components/HomeV2/detail/UnifiedDetailWindow.js, waar het als
// lokaal component leeft; die kopie verhuist in unit C1 naar deze import. De
// padbalk tekent hetzelfde pad boven de kaart, dus het glyph hoort in de
// gedeelde laag: twee kopieen worden twee vocabulaires zodra iemand er een
// soort bij zet.
//
// Twee families in EEN component. Is de naam een recordsoort, dan tekent
// EntityIcon hem, zodat een zijwaartse kruimel (proces, klant, contact, job)
// dezelfde gevulde glyph draagt als zijn chip en zijn typeband. Anders is het
// een herkomstglyph van de wortelkruimel: viewBox 24, stroke 2, ronde uiteinden,
// currentColor, gerenderd op 13.
const CRUMB_GLYPH_PATHS = {
  grid: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M3 14h18M9 9v11" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5 21 21" /></>,
  filter: <path d="M3 5h18l-7 8v6l-4-2v-4z" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
};

// Kent EntityIcon deze naam echt, of valt hij daar stilletjes terug op de
// kandidaatglyph? Alleen bij een echte treffer tekent de kruimel een
// entiteitsglyph; anders zou elke onbekende naam als kandidaat lezen.
function isEntityGlyph(name) {
  const key = ENT_ALIAS[name] || name;
  return Object.prototype.hasOwnProperty.call(ENT_PATHS, key);
}

export function CrumbGlyph({ name, size = 13 }) {
  if (!name) return null;
  if (isEntityGlyph(name)) return <EntityIcon entity={name} size={size} strokeWidth={2} style={{ flex: 'none' }} />;
  const paths = CRUMB_GLYPH_PATHS[name];
  if (!paths) return null;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>{paths}</svg>
  );
}

// ── NavPair: HET gekoppelde vorige/volgende-paar ─────────────────────────────
// Ook dit stond inline in UnifiedDetailWindow.js (navBtn + wikkel). De padbalk
// draagt nu zowel de lijstpijlen als het terug/vooruit-paar, dus de geometrie
// staat hier: EEN omlijnde groep, radius 6, een haarlijn tussen de twee knoppen,
// vlakke doorzichtige vulling, hover tint naar HV2.bgSub (DESIGN-SYSTEM.md,
// gekoppelde clusters). Nadrukkelijk GEEN <Button> en geen .pb-btn: dit idioom
// heeft geen reliefschaduw.
//
// `titles` is de enige ontsnapping: de padbalk gebruikt dezelfde vorm ook voor
// browser-terug en -vooruit, en die twee heten anders dan de lijstpijlen. Laat
// hem leeg en je krijgt de lijstpijlen.
//
// `trailing` hangt een derde knop BINNEN dezelfde omlijning (de caret van de
// padbalk). Binnen en niet ernaast, want een aangeschoven knop tegen een hoek
// van radius 6 laat een hap in de rand achter. De knop zelf draagt alleen zijn
// linkerhaarlijn; rand, radius en overflow blijven van de groep.
export function NavPair({ prevLabel, nextLabel, canPrev, canNext, onPrev, onNext, height = 26, titles = null, trailing = null }) {
  const base = {
    prev: (titles && titles.prev) || t('Vorige (pijl links)'),
    next: (titles && titles.next) || t('Volgende (pijl rechts)'),
  };
  const btn = (prev) => {
    const enabled = prev ? !!canPrev : !!canNext;
    const label = prev ? prevLabel : nextLabel;
    const title = enabled && label ? `${base[prev ? 'prev' : 'next']}: ${label}` : base[prev ? 'prev' : 'next'];
    return (
      <button type="button" disabled={!enabled} onClick={prev ? onPrev : onNext} title={title} aria-label={title}
        onMouseEnter={(e) => { if (enabled) e.currentTarget.style.background = HV2.bgSub; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        style={{
          width: 30,
          height,
          border: 'none',
          borderLeft: prev ? 'none' : `1px solid ${HV2.border}`,
          background: 'transparent',
          color: HV2.ink2,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: enabled ? 'pointer' : 'not-allowed',
          opacity: enabled ? 1 : 0.4,
          padding: 0,
          flex: 'none',
          fontFamily: 'inherit',
        }}>
        <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={prev ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} /></svg>
      </button>
    );
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'stretch', height, border: `1px solid ${HV2.border}`, borderRadius: 6, overflow: 'hidden', flex: 'none' }}>
      {btn(true)}
      {btn(false)}
      {trailing}
    </span>
  );
}
